"use client";

import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useRouter, usePathname } from "next/navigation";
import { supabase } from "@/core/supabase";
import {
  usePlayer,
  usePlayerActions,
  usePlayerStable,
  usePlayerRealtime,
  SubtitleCue,
  FREE_PREVIEW_LOCK_REASON,
} from "@/contexts/player-context";
import { BottomPlayerControls } from "@/components/episode/BottomPlayerControls";
import { VideoPlayerVidstack } from "@/components/VideoPlayerVidstack";
import { cn } from "@/lib/utils";
import {
  Drawer,
  DrawerContent,
  DrawerClose,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from "@/components/ui/drawer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { UpgradePromptDialog } from "@/components/ui/upgrade-prompt-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ChevronDown,
  RotateCcw,
  RotateCw,
  Play,
  Pause,
  Languages,
  ArrowUpRight,
  GraduationCap,
  Loader2,
  MessageCircle,
  Quote,
  ClipboardCopy,
  Check,
  Sparkles,
  Lightbulb,
  EyeOff,
  ChevronsDownUp,
  PlayCircle,
  CircleGauge,
  Mic,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useUser } from "@/lib/context/user-context";
import { useUserSettings } from "@/contexts/user-settings-context";
import { useWordExplanation } from "@/hooks/use-word-explanation";
import { useYoudaoTranslation } from "@/hooks/use-youdao-translation";
import { SpeakerIcon } from "@/components/SpeakerIcon";
import { SpinnerIcon } from "@/components/ui/SpinnerIcon";
import { getSpeakerLang } from "@/utils/device-detection";
import type { ActiveVocabularyTermData } from "@/components/episode/ImmersiveVocabularyCards";
import { normalizeCefrLevel } from "@/components/episode/ImmersiveVocabularyCards";
import { getTermColorInfo } from "@/components/vocabulary/term-card-backgrounds";
import { createVocabularyTermKey } from "@/lib/vocabulary-term-key";
import { normalizeChosenExplanationText, normalizeChosenExplanationAudioUrl } from "@/lib/vocabulary/chosen-explanation";
import { resolveDictionaryDisplayName } from "@/lib/vocabulary/dictionary-display";
// import { mergeTranscriptSegments } from "@/lib/transcript/segment-merge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { useI18n } from "@/lib/i18n";
import { vocabularyLevelChecker, vocabularyLevelManager } from "@/lib/vocabularyLevel";
import { useTheme } from "next-themes";
import { useEpisodeStore } from "@/stores/episodeStore";
import { TimeDrivenTranscript } from "@/components/episode/TimeDrivenTranscript";
import { ChaptersCarousel, type Chapter } from "@/components/episode/ChaptersCarousel";
import { AnimatePresence, motion } from "framer-motion";
import { DynamicIsland } from "../dynamic-island";
import { Skiper21, Skiper21Drawer } from "../pop-island";
import { VocabularyNarrationPlayer } from "@/components/episode/NarrationModeIndicatorCard";
import { VocabularyDisplay } from "@/components/content/vocabulary-display";
import { PhraseSection } from "@/components/episode/PhraseSection";
import { useIsMobile } from "@/hooks/use-mobile";
import { mediaCueRegistry } from "@/lib/media-cue-registry";
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { useSaveShadowingRecord } from "@/hooks/useShadowingStats";
import { TextDiffHighlight } from "@/lib/text-diff-highlight-improved";
import { calculateOptimalSimilarity } from "@/lib/text-similarity";
import { ShadowingPracticeCard } from "@/components/episode/ShadowingPracticeCard";
import { useUsage } from "@/contexts/usage-context";
import {
  GLOBAL_PLAYER_TIME_SLIDER_CONTAINER_ID,
  GLOBAL_PLAYER_TIME_SLIDER_ELEMENT_ID,
  GLOBAL_PLAYER_TIME_SLIDER_CONTAINER_READY_EVENT,
  GLOBAL_PLAYER_TIME_SLIDER_MOUNTED_EVENT,
  GLOBAL_PLAYER_TIME_SLIDER_UNMOUNTED_EVENT,
} from "@/components/player/time-slider-constants";
import { toast } from "sonner";
import { useYouTubeAccessibility } from "@/hooks/useYouTubeAccessibility";
import { useGeoCountry } from "@/hooks/useGeoCountry";

export {
  GLOBAL_PLAYER_TIME_SLIDER_CONTAINER_ID,
  GLOBAL_PLAYER_TIME_SLIDER_ELEMENT_ID,
  GLOBAL_PLAYER_TIME_SLIDER_CONTAINER_READY_EVENT,
  GLOBAL_PLAYER_TIME_SLIDER_MOUNTED_EVENT,
  GLOBAL_PLAYER_TIME_SLIDER_UNMOUNTED_EVENT,
} from "@/components/player/time-slider-constants";

interface VocabularyIslandItem {
  id: string;
  term: string;
  scenarioName?: string;
  timestamp: number | null;
  endTimestamp: number | null;
  level?: string;
  data: any;
}

interface VocabularyIslandContentProps {
  items: VocabularyIslandItem[];
  currentTime?: number;
  onTimestampClick?: (timestamp: number, endTimestamp?: number) => void;
  formatTime: (seconds: number) => string;
  showTranslation: boolean;
  onTermActivate?: (item: VocabularyIslandItem) => void;
}

interface YouTubeMetadataSnapshot {
  videoId: string;
  title?: string;
  thumbnail?: string;
  ownerAvatarUrl?: string;
  author?: string;
  ownerProfileUrl?: string;
  publishDate?: string;
}

const VOCABULARY_ISLAND_CUE_SOURCE_ID = "global-player-vocabulary-island";
type StreamedTranscriptSection = {
  text: string;
  timestamp: [number, number];
  translation?: string | Record<string, string>;
  chunks: Array<{
    text: string;
    timestamp: [number, number];
    translation?: string | Record<string, string>;
  }>;
};

const cueDebugLog = (...args: unknown[]) => {
  if (process.env.NODE_ENV === "production") {
    return;
  }
  // console.log("[DynamicIslandCue]", ...args);
};

const parseVocabularyTimestamp = (
  value: number | string | null | undefined
): number | null => {
  if (value == null) {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) return null;

  const hmsFractionPattern = /^\d{1,2}:\d{2}:\d{2}(?:\.\d+)?$/;
  const msFractionPattern = /^\d{1,2}:\d{2}(?:\.\d+)?$/;

  if (hmsFractionPattern.test(trimmed)) {
    const [hoursPart, minutesPart, secondsPart] = trimmed.split(":");
    const hours = Number(hoursPart);
    const minutes = Number(minutesPart);
    const seconds = Number(secondsPart);
    if (
      Number.isFinite(hours) &&
      Number.isFinite(minutes) &&
      Number.isFinite(seconds)
    ) {
      return hours * 3600 + minutes * 60 + seconds;
    }
    return null;
  }

  if (msFractionPattern.test(trimmed)) {
    const [minutesPart, secondsPart] = trimmed.split(":");
    const minutes = Number(minutesPart);
    const seconds = Number(secondsPart);
    if (Number.isFinite(minutes) && Number.isFinite(seconds)) {
      return minutes * 60 + seconds;
    }
    return null;
  }

  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : null;
};

const HEX_COLOR_REGEX = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const YOUTUBE_URL_REGEX =
  /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;

const extractYoutubeVideoId = (value?: string | null) => {
  if (!value) {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  if (/^[A-Za-z0-9_-]{11}$/.test(trimmed)) {
    return trimmed;
  }
  const match = trimmed.match(YOUTUBE_URL_REGEX);
  return match?.[1] ?? null;
};

const normalizeHexColor = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (!HEX_COLOR_REGEX.test(trimmed)) {
    return null;
  }

  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;

  if (withHash.length === 4) {
    const r = withHash[1];
    const g = withHash[2];
    const b = withHash[3];
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }

  return withHash.toUpperCase();
};

const clampColorChannel = (value: number) =>
  Math.max(0, Math.min(255, Math.round(value)));

const shadeColor = (color: string, percent: number): string => {
  const normalized = normalizeHexColor(color);
  if (!normalized) {
    return "#000000";
  }

  const clampedPercent = Math.max(-100, Math.min(100, percent));
  const factor = (100 + clampedPercent) / 100;

  const r = parseInt(normalized.substring(1, 3), 16);
  const g = parseInt(normalized.substring(3, 5), 16);
  const b = parseInt(normalized.substring(5, 7), 16);

  const shadedR = clampColorChannel(r * factor);
  const shadedG = clampColorChannel(g * factor);
  const shadedB = clampColorChannel(b * factor);

  const rr = shadedR.toString(16).padStart(2, "0");
  const gg = shadedG.toString(16).padStart(2, "0");
  const bb = shadedB.toString(16).padStart(2, "0");

  return `#${rr}${gg}${bb}`.toUpperCase();
};

const hexToRgba = (color: string, alpha: number): string => {
  const normalized = normalizeHexColor(color);
  const clampedAlpha = Math.max(0, Math.min(1, alpha));
  if (!normalized) {
    return `rgba(0, 0, 0, ${clampedAlpha})`;
  }

  const r = parseInt(normalized.substring(1, 3), 16);
  const g = parseInt(normalized.substring(3, 5), 16);
  const b = parseInt(normalized.substring(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${clampedAlpha})`;
};

const hexToRgb = (color: string): { r: number; g: number; b: number } | null => {
  const normalized = normalizeHexColor(color);
  if (!normalized) {
    return null;
  }

  return {
    r: parseInt(normalized.substring(1, 3), 16),
    g: parseInt(normalized.substring(3, 5), 16),
    b: parseInt(normalized.substring(5, 7), 16),
  };
};

const compositeHexColor = (
  topColor: string,
  topAlpha: number,
  bottomColor: string
): string => {
  const top = hexToRgb(topColor);
  const bottom = hexToRgb(bottomColor);
  if (!top || !bottom) {
    return topColor;
  }

  const clampedAlpha = Math.max(0, Math.min(1, topAlpha));
  const inverseAlpha = 1 - clampedAlpha;

  const r = clampColorChannel(top.r * clampedAlpha + bottom.r * inverseAlpha);
  const g = clampColorChannel(top.g * clampedAlpha + bottom.g * inverseAlpha);
  const b = clampColorChannel(top.b * clampedAlpha + bottom.b * inverseAlpha);

  return `#${r.toString(16).padStart(2, "0")}${g
    .toString(16)
    .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`.toUpperCase();
};

const hslToHex = (h: number, s: number, l: number): string => {
  const normalizedH = ((h % 360) + 360) % 360;
  const normalizedS = Math.max(0, Math.min(100, s)) / 100;
  const normalizedL = Math.max(0, Math.min(100, l)) / 100;

  const a = normalizedS * Math.min(normalizedL, 1 - normalizedL);
  const channel = (n: number) => {
    const k = (n + normalizedH / 30) % 12;
    const color = normalizedL - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };

  return `#${channel(0)}${channel(8)}${channel(4)}`.toUpperCase();
};

const generateColorFromString = (value: string): string => {
  if (!value) {
    return "#4B5563";
  }

  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = value.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = Math.abs(hash) % 360;
  const saturation = 60 + (Math.abs((hash >> 7) % 26));
  const lightness = 48 + (Math.abs((hash >> 13) % 16)) - 8;

  return hslToHex(hue, saturation, lightness);
};

const linearizeChannel = (value: number) => {
  const ratio = value / 255;
  return ratio <= 0.04045
    ? ratio / 12.92
    : Math.pow((ratio + 0.055) / 1.055, 2.4);
};

const getRelativeLuminance = (color: string): number | null => {
  const normalized = normalizeHexColor(color);
  if (!normalized) {
    return null;
  }

  const r = parseInt(normalized.substring(1, 3), 16);
  const g = parseInt(normalized.substring(3, 5), 16);
  const b = parseInt(normalized.substring(5, 7), 16);

  const linearR = linearizeChannel(r);
  const linearG = linearizeChannel(g);
  const linearB = linearizeChannel(b);

  return 0.2126 * linearR + 0.7152 * linearG + 0.0722 * linearB;
};

const extractTranslationSnippet = (term: any, languageCode: string = 'zh'): string | undefined => {
  const candidates: Array<string | undefined | null> = [
    normalizeChosenExplanationText(term?.chosen_explanation, languageCode),
    normalizeChosenExplanationText(term?.explanation, languageCode),
  ];

  if (Array.isArray(term?.examples)) {
    for (const example of term.examples) {
      if (example?.translation && typeof example.translation === "string") {
        candidates.push(example.translation);
        break;
      }
      if (example?.context_note && typeof example.context_note === "string") {
        candidates.push(example.context_note);
        break;
      }
    }
  }

  const chosen = candidates.find(
    (value) => typeof value === "string" && value.trim().length > 0
  );

  return chosen ? chosen.trim() : undefined;
};

const buildVocabularyIslandItems = (
  vocabulary: any
): VocabularyIslandItem[] => {
  const scenarios = vocabulary?.scenarios;
  if (!Array.isArray(scenarios)) {
    return [];
  }

  const items: VocabularyIslandItem[] = [];

  scenarios.forEach((scenario: any, scenarioIndex: number) => {
    if (!Array.isArray(scenario?.terms)) {
      return;
    }

    scenario.terms.forEach((term: any, termIndex: number) => {
      if (!term?.term) {
        return;
      }

      const key =
        createVocabularyTermKey(term.term, scenario?.name) ??
        `scenario-${scenarioIndex}-term-${termIndex}`;
      const timestamp =
        parseVocabularyTimestamp(term.timestamp) ??
        parseVocabularyTimestamp(term.examples?.[0]?.timestamp);
      const endTimestamp =
        parseVocabularyTimestamp(term.endTimestamp) ??
        parseVocabularyTimestamp(term.examples?.[0]?.endTimestamp);

      items.push({
        id: key,
        term: term.term,
        scenarioName: scenario?.name,
        timestamp,
        endTimestamp,
        level: normalizeCefrLevel(term?.level),
        data: term,
      });
    });
  });

  items.sort((a, b) => {
    const aTime = a.timestamp ?? Number.POSITIVE_INFINITY;
    const bTime = b.timestamp ?? Number.POSITIVE_INFINITY;
    return aTime - bTime;
  });

  return items;
};

const WINDOW_EPSILON = 0.15;
const MIN_FALLBACK_ITEM_DURATION = 4;
const FALLBACK_LOOKBACK_SECONDS = 5;

const findFirstTimestampIndex = (
  items: VocabularyIslandItem[],
  target: number
) => {
  let low = 0;
  let high = items.length - 1;
  let result = items.length;

  while (low <= high) {
    const mid = (low + high) >> 1;
    const timestamp =
      items[mid].timestamp ?? Number.POSITIVE_INFINITY;
    if (timestamp >= target) {
      result = mid;
      high = mid - 1;
    } else {
      low = mid + 1;
    }
  }

  return Math.max(0, result - 2);
};

const selectActiveVocabularyItems = (
  items: VocabularyIslandItem[],
  currentTime: number
): VocabularyIslandItem[] => {
  if (!items.length) {
    return [];
  }

  const windowStart = currentTime - FALLBACK_LOOKBACK_SECONDS;
  const startIndex = findFirstTimestampIndex(items, windowStart);
  const active: VocabularyIslandItem[] = [];

  for (let index = startIndex; index < items.length; index += 1) {
    const item = items[index];
    const timestamp = item.timestamp;
    if (timestamp == null) {
      continue;
    }
    if (timestamp > currentTime + MIN_FALLBACK_ITEM_DURATION) {
      break;
    }
    const start = timestamp;
    const end =
      item.endTimestamp != null
        ? item.endTimestamp
        : item.timestamp != null
        ? item.timestamp + MIN_FALLBACK_ITEM_DURATION
        : timestamp + MIN_FALLBACK_ITEM_DURATION;

    if (
      currentTime >= start - WINDOW_EPSILON &&
      currentTime <= end + WINDOW_EPSILON
    ) {
      active.push(item);
    }
  }

  return active;
};

const VocabularyIslandContent: React.FC<VocabularyIslandContentProps & { languageCode?: string; setCurrentEpisode?: (episode: any) => void; tempExplanationOverrides?: Record<string, { chosen: string; explanation: string }> }> = ({
  items,
  currentTime,
  onTimestampClick,
  formatTime,
  showTranslation,
  onTermActivate,
  languageCode = 'zh',
  setCurrentEpisode,
  tempExplanationOverrides = {},
}) => {
  if (!items.length) {
    return null;
  }

  return (
    <div className="flex w-[340px] max-w-[calc(100vw-32px)] flex-col gap-3 px-1.5 py-1.5">
      <div className="flex flex-col gap-1">
        {items.map((item) => {
          // Check for temporary overrides first
          const override = item.term ? tempExplanationOverrides[item.term] : undefined;
          
          // Resolve chosen explanation with language code
          // If override exists, use it. Otherwise fall back to item data.
          const chosenExp = override?.chosen || normalizeChosenExplanationText(item.data?.chosen_explanation, languageCode);
          
          // if (process.env.NODE_ENV !== 'production' && item.term === 'glucose levels') {
          //   console.log('[VocabularyIsland] Rendering glucose levels', {
          //     languageCode,
          //     hasOverride: !!override,
          //     overrideChosen: override?.chosen?.substring(0, 30),
          //     overrideExplanation: override?.explanation?.substring(0, 30),
          //     dataChosenExp: item.data?.chosen_explanation,
          //     dataExplanation: item.data?.explanation,
          //     resolvedChosenExp: chosenExp?.substring(0, 30),
          //   });
          // }
          
          // For translation snippet (if used), we also prefer the chosen explanation
          const translation = showTranslation
            ? (chosenExp || extractTranslationSnippet(item.data, languageCode)) // Prefer our resolved chosen exp
            : undefined;
            
          // The text to display below the term
          const explanationText = override?.explanation || normalizeChosenExplanationText(item.data?.explanation, languageCode) || chosenExp;

          const timestamp = item.timestamp;
          const endTimestamp = item.endTimestamp;
          const fallbackEnd =
            timestamp != null ? timestamp + 4 : Number.POSITIVE_INFINITY;
          const activeWindowEnd =
            endTimestamp != null ? endTimestamp : fallbackEnd;
          const isActive =
            timestamp != null &&
            (typeof currentTime !== "number"
              ? true
              : currentTime >= timestamp && currentTime <= activeWindowEnd);

          const timeLabel =
            timestamp != null ? formatTime(Math.max(timestamp, 0)) : "—";
          const levelLabel = item.level;
          const levelColorClass = getTermColorInfo(
            item.term,
            item.scenarioName
          ).textClass;

          const isInteractive =
            (onTimestampClick && timestamp != null) || Boolean(onTermActivate);

          const handleClick = () => {
            // 如果有点击单词的意图（onTermActivate），优先处理单词激活
            // 不触发时间戳跳转，避免在打开 NarrationModeIndicatorCard 时意外 seek
            if (onTermActivate) {
              onTermActivate(item);
              return;
            }
            // 只有在没有 onTermActivate 时才触发时间戳跳转
            if (onTimestampClick && timestamp != null) {
              onTimestampClick(timestamp, endTimestamp ?? undefined);
            }
          };

          return (
            <button
              key={item.id}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClick();
              }}
              disabled={!isInteractive}
              className={cn(
                "w-full bg-white/20 dark:bg-white/70  rounded-[18px] px-3 py-1.5 text-left transition",
                isInteractive
                  ? "hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 active:bg-white/20"
                  : "cursor-default opacity-80",
                isActive && "border-white/60 bg-white/20"
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex flex-col gap-0">
                  <div className="text-base font-semibold text-white dark:text-black/80">
                    {item.term
                      ? item.term.charAt(0).toUpperCase() + item.term.slice(1)
                      : ""}
                  </div>
                  {explanationText ? (
                    <p className="mt-0 text-[12px] leading-snug text-white/70 dark:text-black/50 line-clamp-1">
                      {explanationText}
                    </p>
                  ) 
                  // : item.scenarioName ? (
                  //   <span className="w-fit py-0.5 text-[11px] font-medium uppercase tracking-wide text-white/70">
                  //     {item.scenarioName}
                  //   </span>
                  // ) 
                  : null}
                </div>
                <div className="flex flex-col items-end text-right pr-3">
                  {isActive ? (
                    <span
                      className={cn(
                        "rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-medium uppercase",
                        levelColorClass
                      )}
                    >
                      {levelLabel ?? "—"}
                    </span>
                  ) : (
                    <span className="text-[12px] font-medium text-white/70">
                      {timeLabel}
                    </span>
                  )}
                </div>
              </div>
              {/* {translation && (
                <p className="mt-2 text-sm text-white/80 line-clamp-2">
                  {translation}
                </p>
              )} */}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const flattenTranscriptSegments = (segments: any[]): SubtitleCue[] => {
  if (!Array.isArray(segments)) {
    return [];
  }

  const cues: SubtitleCue[] = [];

  segments.forEach((segment, segmentIndex) => {
    if (!segment) return;

    const candidateChunks =
      Array.isArray(segment.chunks) && segment.chunks.length > 0
        ? segment.chunks
        : Array.isArray(segment.sentences) && segment.sentences.length > 0
        ? segment.sentences
        : [segment];

    candidateChunks.forEach((chunk: any, chunkIndex: number) => {
      if (!chunk) return;

      const rawStart = Array.isArray(chunk.timestamp)
        ? chunk.timestamp[0]
        : Array.isArray(segment.timestamp)
        ? segment.timestamp[0]
        : chunk.startTime ?? segment.startTime ?? 0;

      const rawEnd = Array.isArray(chunk.timestamp)
        ? chunk.timestamp[1]
        : Array.isArray(segment.timestamp)
        ? segment.timestamp[1]
        : chunk.endTime ?? segment.endTime ?? rawStart;

      const start = Number(rawStart);
      const end = Number(rawEnd);
      const text =
        typeof chunk.text === "string" && chunk.text.trim().length > 0
          ? chunk.text.trim()
          : typeof segment.text === "string"
          ? segment.text.trim()
          : "";

      const translationCandidate = chunk.translation ?? segment.translation;
      const translation =
        (typeof translationCandidate === "string" &&
          translationCandidate.trim().length > 0) ||
        (typeof translationCandidate === "object" && translationCandidate !== null)
          ? translationCandidate
          : undefined;

      if (!text) {
        return;
      }

      const cueStart = Number.isFinite(start) ? start : 0;
      const cueEnd = Number.isFinite(end) ? end : NaN;

      cues.push({
        id:
          chunk.id ||
          segment.id ||
          `subtitle-${segmentIndex}-${chunkIndex}-${Math.round(
            cueStart * 1000
          )}`,
        start: cueStart,
        end: cueEnd,
        text,
        translation,
      });
    });
  });

  cues.sort((a, b) => a.start - b.start);
  return cues;
};

const buildSubtitlesFromStreamChunk = (payload: any): SubtitleCue[] => {
  if (!payload) {
    return [];
  }

  const chunkIndex = typeof payload.chunkIndex === "number" ? payload.chunkIndex : 0;
  const chunkStart = typeof payload.chunkStart === "number" ? payload.chunkStart : 0;
  const fallbackEnd =
    typeof payload.chunkEnd === "number" && Number.isFinite(payload.chunkEnd)
      ? payload.chunkEnd
      : chunkStart + 0.6;

  const segments = Array.isArray(payload.segments) ? payload.segments : [];
  const baseSegments =
    segments.length > 0
      ? segments
      : typeof payload.text === "string" && payload.text.trim().length > 0
        ? [{ text: payload.text, start: chunkStart, end: fallbackEnd }]
        : [];

  return baseSegments
    .map((segment: any, segmentIndex: number): SubtitleCue | null => {
      if (!segment) {
        return null;
      }

      const text =
        typeof segment.text === "string" && segment.text.trim().length > 0
          ? segment.text.trim()
          : "";
      if (!text) {
        return null;
      }

      const start =
        typeof segment.start === "number" && Number.isFinite(segment.start)
          ? segment.start
          : chunkStart;
      const rawEnd =
        typeof segment.end === "number" && Number.isFinite(segment.end)
          ? segment.end
          : fallbackEnd;
      const end = rawEnd > start ? rawEnd : start + 0.5;
      const translation =
        (typeof segment.translation === "string" &&
          segment.translation.trim().length > 0) ||
        (typeof segment.translation === "object" && segment.translation !== null)
          ? segment.translation
          : undefined;

      return {
        id:
          segment.id ||
          `stream-${chunkIndex}-${segmentIndex}-${Math.round(start * 1000)}`,
        start,
        end,
        text,
        translation,
      };
    })
    .filter((cue: SubtitleCue | null): cue is SubtitleCue => Boolean(cue));
};

const convertCuesToTranscriptSections = (
  cues: SubtitleCue[],
): StreamedTranscriptSection[] => {
  if (!Array.isArray(cues)) {
    return [];
  }

  return cues.map((cue, index) => {
    const rawStart = typeof cue.start === "number" && Number.isFinite(cue.start) ? cue.start : 0;
    const rawEnd =
      typeof cue.end === "number" && Number.isFinite(cue.end) ? cue.end : rawStart + 0.5;
    const end = rawEnd > rawStart ? rawEnd : rawStart + 0.5;
    const text =
      typeof cue.text === "string" && cue.text.trim().length > 0
        ? cue.text.trim()
        : `Segment ${index + 1}`;
    const translation =
      (typeof cue.translation === "string" &&
        cue.translation.trim().length > 0) ||
      (typeof cue.translation === "object" && cue.translation !== null)
        ? cue.translation
        : undefined;
    const timestamp: [number, number] = [rawStart, end];
    return {
      text,
      translation,
      timestamp,
      chunks: [
        {
          text,
          translation,
          timestamp,
        },
      ],
    };
  });
};

const formatVttTimestamp = (timeInSeconds: number): string => {
  const totalMilliseconds = Math.max(0, Math.round(timeInSeconds * 1000));
  const hours = Math.floor(totalMilliseconds / 3_600_000)
    .toString()
    .padStart(2, "0");
  const minutes = Math.floor((totalMilliseconds % 3_600_000) / 60_000)
    .toString()
    .padStart(2, "0");
  const seconds = Math.floor((totalMilliseconds % 60_000) / 1000)
    .toString()
    .padStart(2, "0");
  const milliseconds = (totalMilliseconds % 1000).toString().padStart(3, "0");
  return `${hours}:${minutes}:${seconds}.${milliseconds}`;
};

const createVttFromSubtitles = (
  subtitleCues: SubtitleCue[]
): string | null => {
  if (!Array.isArray(subtitleCues) || subtitleCues.length === 0) {
    return null;
  }

  const lines: string[] = ["WEBVTT", ""];
  let cueIndex = 1;

  for (const cue of subtitleCues) {
    const start = Number(cue.start);
    const end = Number(cue.end);
    
    let resolvedText = "";
    const trans = cue.translation;
    if (typeof trans === 'string') {
        resolvedText = trans;
    } else if (typeof trans === 'object' && trans !== null) {
        // Simple fallback for VTT generation: prefer 'zh', then first available
        resolvedText = trans['zh'] || Object.values(trans)[0] || "";
    }
    
    if (!resolvedText) {
        resolvedText = cue.text || "";
    }
    
    resolvedText = resolvedText.trim();

    if (
      !resolvedText ||
      Number.isNaN(start) ||
      Number.isNaN(end) ||
      end <= start
    ) {
      continue;
    }

    lines.push(String(cueIndex++));
    lines.push(`${formatVttTimestamp(start)} --> ${formatVttTimestamp(end)}`);
    lines.push(resolvedText);
    lines.push("");
  }

  if (lines.length <= 2) {
    return null;
  }

  return lines.join("\n");
};

const transcriptHasContent = (
  transcript: string | StreamedTranscriptSection[] | null
): boolean => {
  if (!transcript) {
    return false;
  }

  if (typeof transcript === "string") {
    return transcript.trim().length > 20;
  }

  if (Array.isArray(transcript)) {
    return transcript.some(
      (section) =>
        typeof section?.text === "string" && section.text.trim().length > 0
    );
  }

  return false;
};

const STATUS_DEFINITIONS = [
  {
    value: "NEW" as const,
    labelKey: "mobileImmersive.status.new",
    descriptionKey: "mobileImmersive.status.newDescription",
    fallbackLabel: "New",
    fallbackDescription: "Just added, needs first review",
  },
  {
    value: "FUZZY" as const,
    labelKey: "mobileImmersive.status.fuzzy",
    descriptionKey: "mobileImmersive.status.fuzzyDescription",
    fallbackLabel: "Fuzzy",
    fallbackDescription: "Needs more practice soon",
  },
  {
    value: "MASTERED" as const,
    labelKey: "mobileImmersive.status.mastered",
    descriptionKey: "mobileImmersive.status.masteredDescription",
    fallbackLabel: "Mastered",
    fallbackDescription: "Confident and on long review",
  },
] as const;

type StatusValue = (typeof STATUS_DEFINITIONS)[number]["value"];

interface StatusOption {
  value: StatusValue;
  label: string;
  description: string;
}

const statusStyles: Record<StatusValue, string> = {
  NEW: "",
  FUZZY: "bg-amber-100 text-amber-700 border-amber-200",
  MASTERED: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

const statusAccentMap: Record<StatusValue, string> = {
  NEW: "bg-sky-400",
  FUZZY: "bg-amber-400",
  MASTERED: "bg-emerald-400",
};

const POS_PREFIXES = [
  "adj.",
  "adv.",
  "n.",
  "vt.",
  "vi.",
  "v.",
  "prep.",
  "pron.",
  "conj.",
  "interj.",
  "abbr.",
  "num.",
  "art.",
  "aux.",
  "int.",
  "pl.",
  "phr.",
];

const MORPHOLOGY_PREFIXES = [
  "时态:",
  "动词:",
  "动 词:",
  "形容词:",
  "形 容 词:",
  "形容 词:",
  "形 容词:",
  "名词:",
  "名 词:",
  "副词:",
  "副 词:",
  "比较级:",
  "最高级:",
  "复数:",
  "过去式:",
  "过去分词:",
  "现在分词:",
  "第三人称单数:",
  "缩写:",
  "缩 写:",
  " 缩 写:",
];

const pickFirstString = (...candidates: Array<unknown>): string | undefined => {
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim().length > 0) {
      return candidate;
    }
  }
  return undefined;
};

type VidstackPlayerController = {
  seekTo: (time: number) => void;
  setPlaybackRate: (speed: number) => void;
  getCurrentTime: () => number;
  play: () => void | Promise<void>;
  pause: () => void | Promise<void>;
};

interface VideoPlayerConfig {
  url: string;
  textTracks?: Array<{
    src: string;
    kind?: "subtitles" | "captions" | "descriptions" | "chapters" | "metadata";
    label?: string;
    srclang?: string;
    default?: boolean;
  }>;
  artwork?: Array<{
    src: string;
    sizes: string;
    type: string;
  }>;
}

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const normalizeExplainString = (text: string) => {
  let result = text;

  const comboMatches: string[] = [];
  result = result.replace(/vt\.\s*&\s*vi\./gi, (match, offset, original) => {
    const placeholder = `__POS_COMBO_${comboMatches.length}__`;
    comboMatches.push(match);
    const needsNewline = offset > 0 && original[offset - 1] !== "\n";
    return `${needsNewline ? "\n" : ""}${placeholder}`;
  });

  MORPHOLOGY_PREFIXES.forEach((prefix) => {
    const escaped = escapeRegExp(prefix);
    result = result.replace(new RegExp(`(?<!^)${escaped}`, "g"), `\n${prefix}`);
  });

  POS_PREFIXES.forEach((prefix) => {
    const escaped = escapeRegExp(prefix);
    // 避免在括号内的词性标签前添加换行（如 (v.) 或 (n.)）
    result = result.replace(
      new RegExp(`(?<![A-Za-z(])${escaped}(?!\\))`, "g"),
      (match, offset) => (offset === 0 ? prefix : `\n${prefix}`)
    );
  });

  result = result.replace(/(?<![A-Za-z])ad\.(?!j)/g, (match, offset) =>
    offset === 0 ? "adv." : `\nadv.`
  );

  result = result.replace(/:(\S)/g, ": $1");

  comboMatches.forEach((combo, index) => {
    result = result.replace(`__POS_COMBO_${index}__`, combo);
  });

  return result;
};

const extractSubtitlesFromEpisode = (
  episode: any,
  cleanReadData: any
): SubtitleCue[] => {
  const transcriptSources: any[] | undefined =
    episode?.content?.content?.transcript ||
    episode?.content?.transcript ||
    episode?.transcript;

  if (Array.isArray(transcriptSources) && transcriptSources.length > 0) {
    return flattenTranscriptSegments(transcriptSources);
  }

  const cleanReadTranscript = cleanReadData?.transcript;
  if (Array.isArray(cleanReadTranscript) && cleanReadTranscript.length > 0) {
    return flattenTranscriptSegments(cleanReadTranscript);
  }

  const cleanReadChunks = cleanReadData?.transcript?.chunks;
  if (Array.isArray(cleanReadChunks) && cleanReadChunks.length > 0) {
    return flattenTranscriptSegments(cleanReadChunks);
  }

  return [];
};

interface FullPlayerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subtitles: SubtitleCue[];
  activeSubtitleIndex: number | null;
  episodeTitle?: string;
  episodeAuthor?: string;
  episodeThumbnail?: string;
  artworkColor?: string;
  episodeId?: string;
  currentTime: number;
  duration: number;
  formatTime: (time: number) => string;
  onSeek: (time: number) => void;
  onTogglePlayPause: () => void;
  onSkipBackward: () => void;
  onSkipForward: () => void;
  isPlaying: boolean;
  playbackSpeed: number;
  onChangePlaybackSpeed: (speed: number) => void;
  isDarkTheme: boolean;
  onNavigateToEpisode?: () => void;
  onSetActiveSubtitleIndex?: (index: number) => void;
  vocabularyData?: any;
  isLoading?: boolean;
  isTranscriptLoading?: boolean;
  videoConfig?: VideoPlayerConfig;
  onVideoPlayStateChange?: (isPlaying: boolean) => void;
  onVideoTimeUpdate?: (time: number) => void;
  onVideoDurationChange?: (duration: number) => void;
  onVideoEnded?: () => void;
  isWordCheckerReady?: boolean;
  hasDictionarySelected?: boolean;
  isDictionaryInitializing?: boolean;
  onPausePlayback?: () => void;
  onResumePlayback?: () => void;
  onShadowingClick?: () => void;
  onTranscriptUpdate?: (transcript: any[], audioUrl?: string) => void;
  setCurrentEpisode?: (episode: any) => void;
  isGeneratingAudio?: boolean;
  onVocabularyAudioMissing?: (term: any, explanation?: string) => Promise<void>;
  onChosenExplanationMissing?: (term: any) => Promise<string | undefined>;
  tempAudioOverrides?: Record<string, string>;
  tempExplanationOverrides?: Record<string, { chosen: string; explanation: string }>;
  showTranscriptGenerationDialog?: boolean;
  transcriptGenerationConfirmed?: boolean;
  // 🎯 Chapters
  chapters?: Chapter[];
  isGeneratingChapters?: boolean;
  showChapters?: boolean;
  onToggleChapters?: (value: boolean) => void;
  // 🎯 Vocabulary update callback
  onVocabularyUpdate?: (updatedVocabulary: any) => void;
}

const FullPlayerDialog: React.FC<FullPlayerDialogProps> = ({
  open,
  onOpenChange,
  subtitles,
  activeSubtitleIndex,
  episodeTitle,
  episodeAuthor,
  currentTime,
  duration,
  formatTime,
  episodeThumbnail,
  artworkColor,
  episodeId,
  onSeek,
  onTogglePlayPause,
  onSkipBackward,
  onSkipForward,
  isPlaying,
  playbackSpeed,
  onChangePlaybackSpeed,
  isDarkTheme,
  onNavigateToEpisode,
  onSetActiveSubtitleIndex,
  vocabularyData,
  isLoading: isPlayerLoading = false,
  isTranscriptLoading = false,
  videoConfig,
  onVideoPlayStateChange,
  onVideoTimeUpdate,
  onVideoDurationChange,
  onVideoEnded,
  isWordCheckerReady = false,
  hasDictionarySelected = false,
  isDictionaryInitializing = false,
  onPausePlayback,
  onResumePlayback,
  onShadowingClick,
  onTranscriptUpdate,
  setCurrentEpisode,
  isGeneratingAudio = false,
  onVocabularyAudioMissing,
  onChosenExplanationMissing,
  tempAudioOverrides = {},
  tempExplanationOverrides = {},
  showTranscriptGenerationDialog = false,
  transcriptGenerationConfirmed = false,
  // 🎯 Chapters
  chapters = [],
  isGeneratingChapters = false,
  showChapters = true,
  onToggleChapters,
  // 🎯 Vocabulary update callback
  onVocabularyUpdate,
}) => {
  const transcriptScrollRef = useRef<HTMLDivElement | null>(null);
  const hasAutoScrolledRef = useRef(false);
  const [showTranslations, setShowTranslations] = useState(false);
  const [showDynamicIslandVocabulary, setShowDynamicIslandVocabulary] =
    useState(true);
  const [cueDrivenVocabularyItems, setCueDrivenVocabularyItems] = useState<
    VocabularyIslandItem[]
  >([]);
  const [isVocabularyCueAttached, setIsVocabularyCueAttached] =
    useState(false);
  const cueSupportAvailable = mediaCueRegistry.hasCueSupport();
  const [selectedWord, setSelectedWord] = useState<string>("");
  const [selectedWordContext, setSelectedWordContext] = useState<string>("");
  const [showWordDrawer, setShowWordDrawer] = useState(false);
  const [islandView, setIslandView] = useState("idle");
  const [islandVariantKey, setIslandVariantKey] = useState("idle");
  const hasAutoOpenedVocabularyIslandRef = useRef(false);
  const previousVocabularyDataRef = useRef(vocabularyData);
  const [hasActiveVocabularyTerms, setHasActiveVocabularyTerms] =
    useState(false);
  const [selectedVocabularyTerm, setSelectedVocabularyTerm] =
    useState<VocabularyIslandItem | null>(null);
  const [isVocabularyNarrationOpen, setIsVocabularyNarrationOpen] =
    useState(false);
  const [isPopIslandOpen, setIsPopIslandOpen] = useState(false);
  const [practiceTerm, setPracticeTerm] = useState<string | null>(null);
  const [currentTranscriptView, setCurrentTranscriptView] = useState<'transcript' | 'keywords' | 'vocabulary'>('transcript');
  const [videoCurrentTime, setVideoCurrentTime] = useState<number | null>(null);
  const faceIconRef = useRef<SVGSVGElement | null>(null);
  const rotationTimeoutIdsRef = useRef<NodeJS.Timeout[]>([]);
  const shouldResumePlaybackRef = useRef(false);
  const shouldResumeAfterPopIslandRef = useRef(false);
  const lastIslandAutoOpenAtRef = useRef(0);
  const lastIslandToggleAtRef = useRef(0);
  const vocabCueSourceIdRef = useRef<string>();
  if (!vocabCueSourceIdRef.current) {
    vocabCueSourceIdRef.current = `${VOCABULARY_ISLAND_CUE_SOURCE_ID}-${Math.random()
      .toString(36)
      .slice(2, 10)}`;
  }
  const wasVocabularyNarrationVisibleRef = useRef(false);
  const playPauseTouchHandledRef = useRef(false);
  const playPauseLastActionRef = useRef<'play' | 'pause' | null>(null);
  const playPauseLastActionTimeRef = useRef<number>(0);
  const PLAY_PAUSE_DEBOUNCE_MS = 300; // 防抖时间，防止快速连续点击
  // 使用固定的主题色而不是 artworkColor
  const basePlayerColor = useMemo(() => {
    return isDarkTheme ? "#111827" : "#EEF2FF";
  }, [isDarkTheme]);

  const { gradientBackgroundStyle, overlayGradientStyle, systemThemeColor } = useMemo(() => {
    const highlightColor = shadeColor(basePlayerColor, isDarkTheme ? -12 : 24);
    const midToneColor = shadeColor(basePlayerColor, isDarkTheme ? -38 : 10);
    const shadowColor = shadeColor(basePlayerColor, isDarkTheme ? -64 : -18);
    const softHighlight = shadeColor(basePlayerColor, isDarkTheme ? -18 : 36);
    const softMidTone = shadeColor(basePlayerColor, isDarkTheme ? -46 : 16);
    const softShadow = shadeColor(basePlayerColor, isDarkTheme ? -72 : -6);

    const gradient: React.CSSProperties = {
      background: `linear-gradient(135deg, ${highlightColor} 0%, ${basePlayerColor} 48%, ${shadowColor} 100%)`,
    };

    const overlay: React.CSSProperties = isDarkTheme
      ? {
          background: `linear-gradient(180deg, ${hexToRgba(softHighlight, 0.82)} 0%, ${hexToRgba(softMidTone, 0.88)} 52%, ${hexToRgba(softShadow, 0.95)} 100%)`,
          backdropFilter: "blur(36px)",
          WebkitBackdropFilter: "blur(36px)",
        }
      : {
          background: `linear-gradient(180deg, ${hexToRgba(softHighlight, 0.9)} 0%, ${hexToRgba(softMidTone, 0.86)} 50%, ${hexToRgba(softShadow, 0.92)} 100%)`,
          backdropFilter: "blur(42px)",
          WebkitBackdropFilter: "blur(42px)",
    };

    const overlayAlpha = isDarkTheme ? 0.82 : 0.9;
    const baseMix = compositeHexColor(
      softHighlight,
      overlayAlpha,
      highlightColor
    );
    const systemTheme = isDarkTheme ? shadeColor(baseMix, 12) : baseMix;

    return {
      gradientBackgroundStyle: gradient,
      overlayGradientStyle: overlay,
      systemThemeColor: systemTheme,
    };
  }, [basePlayerColor, isDarkTheme]);

  const transcriptOverlayShades = useMemo(() => {
    const topColor = hexToRgba(
      shadeColor(basePlayerColor, isDarkTheme ? -16 : 32),
      isDarkTheme ? 0.64 : 0.58
    );
    const midColor = hexToRgba(
      shadeColor(basePlayerColor, isDarkTheme ? -38 : 14),
      isDarkTheme ? 0.46 : 0.4
    );

    return {
      gradient: `linear-gradient(to top, transparent 0%, ${midColor} 48%, ${topColor} 100%)`,
      mask: `linear-gradient(${topColor} 40%, transparent)`,
    };
  }, [basePlayerColor, isDarkTheme]);

  const transcriptBackgroundTone = useMemo<"light" | "dark">(() => {
    const luminance = getRelativeLuminance(basePlayerColor);
    if (typeof luminance !== "number") {
      return isDarkTheme ? "dark" : "light";
    }
    return luminance >= 0.63 ? "light" : "dark";
  }, [basePlayerColor, isDarkTheme]);
  const previousThemeColorRef = useRef<string | null>(null);
  const { t } = useI18n();
  const captionTrackId = useMemo(
    () => `global-player-subtitles-${episodeId ?? "unknown"}`,
    [episodeId]
  );
  const captionTrackLabel = t(
    "episode.videoPlayer.controls.captionTrackLabel",
    "English & Chinese"
  );
  const captionBlobUrlRef = useRef<string | null>(null);
  const [captionTrackSrc, setCaptionTrackSrc] = useState<string | null>(null);
  useEffect(() => {
    return () => {
      if (captionBlobUrlRef.current) {
        URL.revokeObjectURL(captionBlobUrlRef.current);
        captionBlobUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (captionBlobUrlRef.current) {
      URL.revokeObjectURL(captionBlobUrlRef.current);
      captionBlobUrlRef.current = null;
    }
    setCaptionTrackSrc(null);
  }, [captionTrackId]);
  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    let meta = document.querySelector(
      'meta[name="theme-color"]'
    ) as HTMLMetaElement | null;

    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "theme-color");
      document.head.appendChild(meta);
    }

    if (open) {
      if (previousThemeColorRef.current === null) {
        previousThemeColorRef.current = meta.getAttribute("content");
      }
      meta.setAttribute("content", systemThemeColor);
    } else if (previousThemeColorRef.current !== null) {
      meta.setAttribute("content", previousThemeColorRef.current);
      previousThemeColorRef.current = null;
    }
  }, [open, systemThemeColor]);
  useEffect(() => {
    return () => {
      if (typeof document === "undefined") {
        return;
      }
      if (previousThemeColorRef.current !== null) {
        const meta = document.querySelector(
          'meta[name="theme-color"]'
        ) as HTMLMetaElement | null;
        if (meta) {
          meta.setAttribute("content", previousThemeColorRef.current);
        }
        previousThemeColorRef.current = null;
      }
    };
  }, []);
  const ensureCaptionTrack = useCallback(() => {
    if (captionTrackSrc) {
      return captionTrackSrc;
    }

    if (!Array.isArray(subtitles) || subtitles.length === 0) {
      return null;
    }

    const vtt = createVttFromSubtitles(subtitles);
    if (!vtt) {
      return null;
    }

    try {
      const blob = new Blob([vtt], { type: "text/vtt" });
      if (captionBlobUrlRef.current) {
        URL.revokeObjectURL(captionBlobUrlRef.current);
      }
      const src = URL.createObjectURL(blob);
      captionBlobUrlRef.current = src;
      setCaptionTrackSrc(src);
      return src;
    } catch (error) {
      console.error(
        "GlobalPlayerControls: failed to create captions track",
        error
      );
      return null;
    }
  }, [captionTrackSrc, subtitles]);

  useEffect(() => {
    if (!Array.isArray(subtitles) || subtitles.length === 0) {
      if (captionTrackSrc) {
        setCaptionTrackSrc(null);
      }
      if (captionBlobUrlRef.current) {
        URL.revokeObjectURL(captionBlobUrlRef.current);
        captionBlobUrlRef.current = null;
      }
      return;
    }

    ensureCaptionTrack();
  }, [captionTrackSrc, ensureCaptionTrack, subtitles]);

  const captionTextTracks = useMemo(() => {
    if (!captionTrackSrc) {
      return undefined;
    }

    return [
      {
        id: captionTrackId,
        src: captionTrackSrc,
        kind: "subtitles" as const,
        srclang: "zh",
        label: captionTrackLabel,
        default: false,
      },
    ];
  }, [captionTrackId, captionTrackLabel, captionTrackSrc]);

  const mergedTextTracks = useMemo(() => {
    const baseTracks = videoConfig?.textTracks ?? [];
    const captionTracks = captionTextTracks ?? [];
    const combined = captionTracks.length
      ? [
          ...baseTracks.filter((track) => track.src !== captionTrackSrc),
          ...captionTracks,
        ]
      : baseTracks;
    return combined.length ? combined : undefined;
  }, [captionTextTracks, captionTrackSrc, videoConfig?.textTracks]);
  const speakerRef = useRef<HTMLDivElement | null>(null);
  const [isTimeSliderMounted, setIsTimeSliderMounted] = useState(false);
  const videoControllerRef = useRef<VidstackPlayerController | null>(null);
  const hasVideoPlayer = Boolean(videoConfig?.url);
  const [isVideoVisible, setIsVideoVisible] = useState(true);
  const showVideoInUi = hasVideoPlayer && isVideoVisible;
  const [videoHostElement, setVideoHostElement] = useState<HTMLDivElement | null>(null);
  const [videoPortalContainer, setVideoPortalContainer] = useState<HTMLElement | null>(null);
  const hiddenVideoHostRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const hiddenHost = document.createElement("div");
    hiddenHost.id = "global-player-hidden-video-host";
    Object.assign(hiddenHost.style, {
      position: "fixed",
      width: "1px",
      height: "1px",
      top: "-10000px",
      left: "-10000px",
      pointerEvents: "none",
      opacity: "0",
      overflow: "hidden",
      zIndex: "-1",
    });

    const container = document.createElement("div");
    Object.assign(container.style, {
      width: "100%",
      height: "100%",
      position: "relative",
    });

    hiddenHost.appendChild(container);
    document.body.appendChild(hiddenHost);

    hiddenVideoHostRef.current = hiddenHost;
    setVideoPortalContainer(container);

    return () => {
      hiddenVideoHostRef.current = null;
      setVideoPortalContainer(null);
      try {
        hiddenHost.remove();
      } catch {
        if (hiddenHost.parentNode) {
          hiddenHost.parentNode.removeChild(hiddenHost);
        }
      }
    };
  }, []);

  useEffect(() => {
    if (!videoPortalContainer) {
      return;
    }
    const targetHost = videoHostElement ?? hiddenVideoHostRef.current;
    if (!targetHost) {
      return;
    }
    if (videoPortalContainer.parentElement !== targetHost) {
      targetHost.appendChild(videoPortalContainer);
    }
  }, [videoPortalContainer, videoHostElement]);

  const registerVideoHost = useCallback((node: HTMLDivElement | null) => {
    setVideoHostElement(node ?? null);
  }, []);

  const handleHideVideo = useCallback(() => {
    setIsVideoVisible(false);
    try {
      videoControllerRef.current?.pause();
    } catch {
      // ignore pause errors
    }
  }, []);

  const handleShowVideo = useCallback(() => {
    setIsVideoVisible(true);
  }, []);

  const handleInfoRowActivate = useCallback(() => {
    if (!showVideoInUi && hasVideoPlayer) {
      handleShowVideo();
    }
  }, [handleShowVideo, hasVideoPlayer, showVideoInUi]);

  const openMusicIsland = useCallback(() => {
    setIslandVariantKey("idle-music");
    setIslandView("music");
  }, []);

  const closeMusicIsland = useCallback(() => {
    setIslandVariantKey("music-idle");
    setIslandView("idle");
  }, []);

  const attemptVideoPlaybackToggle = useCallback(() => {
    if (!hasVideoPlayer || !videoControllerRef.current) {
      return;
    }

    try {
      const maybePromise =
        (isPlaying
          ? videoControllerRef.current.pause?.()
          : videoControllerRef.current.play?.()) as void | Promise<void>;

      if (
        maybePromise &&
        typeof (maybePromise as Promise<void>).catch === "function"
      ) {
        (maybePromise as Promise<void>).catch((error) => {
          if (process.env.NODE_ENV !== "production") {
            console.warn(
              "[FullPlayerDialog] direct video toggle failed",
              error
            );
          }
        });
      }
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[FullPlayerDialog] direct video toggle failed", error);
      }
    }
  }, [hasVideoPlayer, isPlaying]);

  const { user } = useUser();
  const { userSettings, isLoading: isSettingsLoading } = useUserSettings();
  const dictionaryDisplayName = useMemo(() => {
    const dictionaryFile = userSettings?.dictionarySetting;
    if (!dictionaryFile) {
      return undefined;
    }
    return resolveDictionaryDisplayName(dictionaryFile);
  }, [userSettings?.dictionarySetting]);

  const {
    explanations,
    loading: explanationLoading,
    savingWord,
    getExplanation,
    saveCard,
    isCardSaved,
  } = useWordExplanation(userSettings, selectedWordContext, user?.id);

  const {
    translations,
    loading: youdaoLoading,
    getTranslation,
    isInitialized,
  } = useYoudaoTranslation();

  const [selectedStatusValue, setSelectedStatusValue] =
    useState<StatusValue>("NEW");

  const statusOptions = useMemo<StatusOption[]>(
    () =>
      STATUS_DEFINITIONS.map((definition) => ({
        value: definition.value,
        label: t(definition.labelKey, definition.fallbackLabel),
        description: t(
          definition.descriptionKey,
          definition.fallbackDescription
        ),
      })),
    [t]
  );

  const selectedStatusOption = useMemo<StatusOption>(() => {
    const fallback: StatusOption = statusOptions[0] ?? {
      value: "NEW",
      label: t("mobileImmersive.status.new", "New"),
      description: t(
        "mobileImmersive.status.newDescription",
        "Just added, needs first review"
      ),
    };
    return (
      statusOptions.find((option) => option.value === selectedStatusValue) ??
      fallback
    );
  }, [statusOptions, selectedStatusValue, t]);

  const StatusIconMobile =
    selectedStatusOption.value === "MASTERED"
      ? Check
      : selectedStatusOption.value === "FUZZY"
      ? Lightbulb
      : Sparkles;

  useEffect(() => {
    if (!open) {
      setShowTranslations(false);
      setShowWordDrawer(false);
      setSelectedWord("");
      setSelectedWordContext("");
      playPauseTouchHandledRef.current = false;
      playPauseLastActionRef.current = null;
      playPauseLastActionTimeRef.current = 0;
    }
  }, [open]);

  useEffect(() => {
    const handleMounted = () => setIsTimeSliderMounted(true);
    const handleUnmounted = () => setIsTimeSliderMounted(false);

    window.addEventListener(
      GLOBAL_PLAYER_TIME_SLIDER_MOUNTED_EVENT,
      handleMounted
    );
    window.addEventListener(
      GLOBAL_PLAYER_TIME_SLIDER_UNMOUNTED_EVENT,
      handleUnmounted
    );

    return () => {
      window.removeEventListener(
        GLOBAL_PLAYER_TIME_SLIDER_MOUNTED_EVENT,
        handleMounted
      );
      window.removeEventListener(
        GLOBAL_PLAYER_TIME_SLIDER_UNMOUNTED_EVENT,
        handleUnmounted
      );
    };
  }, []);

  useEffect(() => {
    if (!open) {
      setIsTimeSliderMounted(false);
      return;
    }

    const signalReady = () => {
      const container = document.getElementById(
        GLOBAL_PLAYER_TIME_SLIDER_CONTAINER_ID
      );
      if (container) {
        window.dispatchEvent(
          new Event(GLOBAL_PLAYER_TIME_SLIDER_CONTAINER_READY_EVENT)
        );
      }
    };

    signalReady();
    const timeoutId = window.setTimeout(signalReady, 0);
    const existingSlider = document.getElementById(
      GLOBAL_PLAYER_TIME_SLIDER_ELEMENT_ID
    );
    if (existingSlider) {
      setIsTimeSliderMounted(true);
    }

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [open]);

  useEffect(() => {
    if (!showVideoInUi) {
      return;
    }
    const existingSlider = document.getElementById(
      GLOBAL_PLAYER_TIME_SLIDER_ELEMENT_ID
    );
    if (existingSlider) {
      setIsTimeSliderMounted(true);
    }
  }, [showVideoInUi]);

  const scrollActiveSubtitleIntoView = useCallback(
    (behavior: ScrollIntoViewOptions["behavior"] = "smooth") => {
      if (activeSubtitleIndex == null) return;

      const container = transcriptScrollRef.current;
      if (!container) return;

      const target = container.querySelector<HTMLElement>(
        `[data-line-index="${activeSubtitleIndex}"]`
      );
      if (!target) return;

      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const targetOffsetWithinContainer =
        targetRect.top - containerRect.top + container.scrollTop;
      const targetHeight = targetRect.height;
      const containerHeight = container.clientHeight;

      // 滚动到上方 1/3 的位置（而不是正中间）
      const targetPosition = containerHeight / 3;
      const desiredScrollTop =
        targetOffsetWithinContainer - targetPosition + targetHeight / 2;
      const maxScrollTop = container.scrollHeight - containerHeight;
      const nextScrollTop = Math.max(
        0,
        Math.min(desiredScrollTop, maxScrollTop)
      );

      if (Math.abs(nextScrollTop - container.scrollTop) < 1) {
        return;
      }

      const scrollOptions: ScrollToOptions = { top: nextScrollTop };
      if (behavior) {
        scrollOptions.behavior = behavior;
      }

      container.scrollTo(scrollOptions);
    },
    [activeSubtitleIndex]
  );

  const performSeek = useCallback(
    (seconds: number) => {
      if (
        Number.isFinite(seconds) &&
        hasVideoPlayer &&
        videoControllerRef.current
      ) {
        try {
          videoControllerRef.current.seekTo(seconds);
        } catch {
          // silently ignore seek errors from the embedded player
        }
      }
      onSeek(seconds);
    },
    [hasVideoPlayer, onSeek]
  );

  const handleVideoApiReady = useCallback(
    (api: VidstackPlayerController) => {
      videoControllerRef.current = api;
      try {
        api.setPlaybackRate(playbackSpeed);
      } catch {
        // ignore unsupported playback rate changes
      }
    },
    [playbackSpeed]
  );

  const handleVideoPlayPause = useCallback(
    (playing: boolean) => {
      onVideoPlayStateChange?.(playing);
    },
    [onVideoPlayStateChange]
  );

  const videoTimeThrottleRef = useRef<{ lastTime: number; lastTs: number }>({
    lastTime: -1,
    lastTs: 0,
  });
  const VIDEO_TIME_MIN_INTERVAL = 250; // ms - 从 120ms 增加到 250ms 减少更新频率
  const VIDEO_TIME_MIN_DELTA = 0.15; // seconds - 从 0.08s 增加到 0.15s

  const handleVideoTimeUpdate = useCallback(
    (time: number) => {
      const now =
        typeof performance !== "undefined" && typeof performance.now === "function"
          ? performance.now()
          : Date.now();
      const { lastTime, lastTs } = videoTimeThrottleRef.current;
      if (
        Math.abs(time - lastTime) < VIDEO_TIME_MIN_DELTA &&
        now - lastTs < VIDEO_TIME_MIN_INTERVAL
      ) {
        // 🔥 完全跳过，不更新本地状态也不调用父组件回调
        // 减少不必要的函数调用和状态传播
        return;
      }
      videoTimeThrottleRef.current = { lastTime: time, lastTs: now };
      setVideoCurrentTime(time);
      onVideoTimeUpdate?.(time);
    },
    [onVideoTimeUpdate]
  );

  const handleVideoDurationChange = useCallback(
    (duration: number) => {
      onVideoDurationChange?.(duration);
    },
    [onVideoDurationChange]
  );

  const handleVideoEnded = useCallback(() => {
    onVideoEnded?.();
  }, [onVideoEnded]);

  useEffect(() => {
    if (!hasVideoPlayer || !videoControllerRef.current) {
      return;
    }
    try {
      videoControllerRef.current.setPlaybackRate(playbackSpeed);
    } catch {
      // ignore unsupported playback rate changes
    }
  }, [hasVideoPlayer, playbackSpeed]);

  useEffect(() => {
    if (!hasVideoPlayer) {
      videoControllerRef.current = null;
    }
  }, [hasVideoPlayer]);

  // 🔥 当视频 URL 变化时（切换视频），重置 videoControllerRef
  // 这样 attemptVideoPlaybackToggle 会正确等待新的控制器被注册
  const previousVideoUrlRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (videoConfig?.url !== previousVideoUrlRef.current) {
      previousVideoUrlRef.current = videoConfig?.url;
      // 当视频源变化时，重置控制器引用
      // 新的控制器会在 VideoPlayerVidstack 组件的 onSeekTo 回调中被设置
      videoControllerRef.current = null;
    }
  }, [videoConfig?.url]);

  useEffect(() => {
    // 🎯 打开 full dialog 时默认收起视频
    if (open) {
      setIsVideoVisible(false);
    }
  }, [open]);


  const explanation = selectedWord ? explanations[selectedWord] : undefined;
  const translation = selectedWord ? translations[selectedWord] : undefined;
  const lookupMetadata = explanation?.lookupMetadata;
  const hasLookupMetadata = Boolean(
    lookupMetadata &&
      ((typeof lookupMetadata.definition === "string" &&
        lookupMetadata.definition.trim()) ||
        lookupMetadata.tag ||
        (typeof lookupMetadata.bnc === "number" && lookupMetadata.bnc > 0) ||
        (typeof lookupMetadata.frq === "number" && lookupMetadata.frq > 0))
  );
  const lookupTags = useMemo(() => {
    if (!lookupMetadata?.tag) return [] as string[];
    return lookupMetadata.tag
      .split(/[,\s]+/)
      .map((token) => token.trim())
      .filter(Boolean);
  }, [lookupMetadata?.tag]);
  const isWordLoading = selectedWord
    ? Boolean(explanationLoading[selectedWord] || youdaoLoading[selectedWord])
    : false;
  const isWordSaving = selectedWord ? Boolean(savingWord[selectedWord]) : false;

  const isWordSaved = selectedWord ? isCardSaved(selectedWord) : false;

  const primaryTranslationParts = useMemo(() => {
    const parts: string[] = [];
    const pushNormalized = (value: string) => {
      normalizeExplainString(value)
        .split(/\n+/)
        .map((line) => line.trim())
        .filter(Boolean)
        .forEach((line) => parts.push(line));
    };

    if (Array.isArray(explanation?.translation)) {
      explanation.translation.forEach((part) => pushNormalized(part));
    } else if (
      typeof explanation?.translation === "string" &&
      explanation.translation.trim()
    ) {
      explanation.translation
        .split(";")
        .map((part) => part.trim())
        .filter(Boolean)
        .forEach((part) => pushNormalized(part));
    } else if (translation?.translation) {
      pushNormalized(translation.translation);
    } else if (translation?.explains && translation.explains[0]) {
      pushNormalized(translation.explains[0]);
    }

    return parts;
  }, [
    explanation?.translation,
    translation?.translation,
    translation?.explains,
  ]);

  const extraExplainParts = useMemo(() => {
    if (!translation?.explains || translation.explains.length <= 1) {
      return [] as string[];
    }

    const rest = translation.explains.slice(1);
    const parts: string[] = [];
    rest.forEach((explain) => {
      normalizeExplainString(explain)
        .split(/\n+/)
        .map((line) => line.trim())
        .filter(Boolean)
        .forEach((line) => parts.push(line));
    });

    return parts;
  }, [translation?.explains]);


  const exampleText = useMemo(() => {
    const example = explanation?.example?.trim();
    if (!example) return undefined;
    if (
      example === "Could not load example. Please try again later." ||
      example === "Error fetching example"
    ) {
      return undefined;
    }
    return example;
  }, [explanation?.example]);

  const groupedTranscript = useMemo(() => {
    if (!Array.isArray(subtitles)) {
      return [] as Array<{
        text: string;
        translation?: string;
        timestamp: [number, number];
        chunks: Array<{
          text: string;
          translation?: string;
          timestamp: [number, number];
        }>;
      }>;
    }

    return subtitles.map((cue) => {
      const start = Number.isFinite(cue.start) ? cue.start : 0;
      const hasFiniteEnd = Number.isFinite(cue.end);
      const rawEnd = hasFiniteEnd ? Number(cue.end) : Number.NaN;
      const fallbackEnd = start + 0.6;
      const end =
        hasFiniteEnd && Number.isFinite(rawEnd) && rawEnd >= start
          ? rawEnd
          : fallbackEnd;
      const text = typeof cue.text === "string" ? cue.text : "";
      const translation =
        typeof cue.translation === "string" || typeof cue.translation === "object"
          ? cue.translation
          : undefined;
      const timestamp: [number, number] = [start, end];

      return {
        text,
        translation,
        timestamp,
        chunks: [
          {
            text,
            translation,
            timestamp,
          },
        ],
      };
    });
  }, [subtitles]);

  const staticVocabularyHighlights = useMemo<ActiveVocabularyTermData[]>(() => {
    const scenarios = vocabularyData?.scenarios;
    if (!Array.isArray(scenarios) || scenarios.length === 0) {
      return [];
    }

    const unique = new Map<string, ActiveVocabularyTermData>();

    scenarios.forEach((scenario: any) => {
      if (!scenario?.terms) return;

      scenario.terms.forEach((term: any) => {
        const termName = term?.term;
        if (!termName) {
          return;
        }

        const key = createVocabularyTermKey(termName, scenario.name);
        if (!key || unique.has(key)) {
          return;
        }

        const colorInfo = getTermColorInfo(termName, scenario.name);
        unique.set(key, {
          term: termName,
          scenarioName: scenario.name,
          colorIndex: colorInfo.colorIndex,
          backgroundClass: colorInfo.backgroundClass,
          lightBackgroundClass: colorInfo.lightBackgroundClass,
          darkBackgroundClass: colorInfo.darkBackgroundClass,
          textClass: colorInfo.textClass,
          level: normalizeCefrLevel(term?.level),
        });
      });
    });

    return Array.from(unique.values());
  }, [vocabularyData]);

  const hasVocabularyToDisplay = useMemo(() => {
    const scenarios = vocabularyData?.scenarios;
    if (!Array.isArray(scenarios)) {
      return false;
    }

    return scenarios.some(
      (scenario: any) =>
        Array.isArray(scenario?.terms) && scenario.terms.length > 0
    );
  }, [vocabularyData]);

  const showVocabularyCards =
    Boolean(vocabularyData) && hasVocabularyToDisplay;

  // useEffect(() => {
  //   if (process.env.NODE_ENV !== "production") {
  //     console.log("[GlobalPlayerControls] vocabulary display toggle", {
  //       shareId: episodeId,
  //       hasVocabularyData: Boolean(vocabularyData),
  //       hasVocabularyToDisplay,
  //       showVocabularyCards,
  //     });
  //   }
  // }, [episodeId, vocabularyData, hasVocabularyToDisplay, showVocabularyCards]);

  // useEffect(() => {
  //   if (process.env.NODE_ENV === "production") {
  //     return;
  //   }

  //   const scenarioSummaries = Array.isArray(vocabularyData?.scenarios)
  //     ? vocabularyData.scenarios.map((scenario: any) => ({
  //         name: scenario?.name ?? "unknown",
  //         termCount: Array.isArray(scenario?.terms) ? scenario.terms.length : 0,
  //       }))
  //     : null;

  //   console.log("[GlobalPlayerControls] vocabulary cards availability", {
  //     hasVocabularyData: Boolean(vocabularyData),
  //     hasVocabularyToDisplay,
  //     showVocabularyCards,
  //     scenarioCount: Array.isArray(vocabularyData?.scenarios)
  //       ? vocabularyData.scenarios.length
  //       : 0,
  //     scenarioSummaries,
  //   });
  // }, [vocabularyData, hasVocabularyToDisplay, showVocabularyCards]);

  const vocabularyIslandItems = useMemo(() => {
    if (!vocabularyData) {
      return [] as VocabularyIslandItem[];
    }
    return buildVocabularyIslandItems(vocabularyData);
  }, [vocabularyData]);

  useEffect(() => {
    if (!selectedVocabularyTerm) {
      return;
    }

    const currentId = selectedVocabularyTerm.id;
    if (!currentId) {
      return;
    }

    const updatedItem = vocabularyIslandItems.find(
      (item) => item.id === currentId
    );

    if (!updatedItem) {
      return;
    }

    if (updatedItem === selectedVocabularyTerm) {
      return;
    }

    const prevAudio =
      typeof selectedVocabularyTerm.data?.chosen_explanation_audio === "string"
        ? selectedVocabularyTerm.data.chosen_explanation_audio
        : undefined;
    const nextAudio =
      typeof updatedItem.data?.chosen_explanation_audio === "string"
        ? updatedItem.data.chosen_explanation_audio
        : undefined;

    if (prevAudio === nextAudio) {
      return;
    }

    setSelectedVocabularyTerm(updatedItem);
  }, [selectedVocabularyTerm, vocabularyIslandItems]);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !showVocabularyCards ||
      !showDynamicIslandVocabulary ||
      !cueSupportAvailable
    ) {
      const reason = !showVocabularyCards
        ? "no-vocabulary"
        : !showDynamicIslandVocabulary
        ? "feature-disabled"
        : !cueSupportAvailable
        ? "cue-unsupported"
        : "ssr";
      const payload = {
        reason,
        showVocabularyCards,
        showDynamicIslandVocabulary,
        cueSupportAvailable,
        episodeId,
        vocabularyItemCount: vocabularyIslandItems.length,
      };
      cueDebugLog("skip cue registration", payload);
      // if (process.env.NODE_ENV !== "production") {
      //   console.log("[GlobalPlayerControls] skip cue registration", payload);
      // }
      setCueDrivenVocabularyItems([]);
      setIsVocabularyCueAttached(false);
      return;
    }

    const descriptors: Array<{
      id: string;
      start: number;
      end: number;
      data: { itemId: string };
    }> = [];
    const itemLookup = new Map<string, VocabularyIslandItem>();

    vocabularyIslandItems.forEach((item) => {
      if (typeof item.timestamp !== "number" || !Number.isFinite(item.timestamp)) {
        cueDebugLog("ignore vocabulary item without timestamp", {
          id: item.id,
          term: item.term,
        });
        return;
      }
      const start = item.timestamp;
      const hasExplicitEnd =
        typeof item.endTimestamp === "number" && Number.isFinite(item.endTimestamp);
      const explicitEnd = hasExplicitEnd ? (item.endTimestamp as number) : null;
      const end =
        explicitEnd != null ? Math.max(explicitEnd, start + 0.05) : start + 4;

      descriptors.push({
        id: item.id,
        start,
        end,
        data: { itemId: item.id },
      });
      itemLookup.set(item.id, item);
    });

    if (!descriptors.length) {
      cueDebugLog("no cue descriptors generated");
      // if (process.env.NODE_ENV !== "production") {
      //   console.log("[GlobalPlayerControls] no cue descriptors generated");
      // }
      setCueDrivenVocabularyItems([]);
      setIsVocabularyCueAttached(false);
      return;
    }

    let isEffectActive = true;
    const activeItems = new Map<string, VocabularyIslandItem>();
    const updateActiveItems = () => {
      if (!isEffectActive) {
        return;
      }
      setCueDrivenVocabularyItems(Array.from(activeItems.values()));
    };

    const unregister = mediaCueRegistry.registerCueSet(
      vocabCueSourceIdRef.current || VOCABULARY_ISLAND_CUE_SOURCE_ID,
      descriptors,
      {
        onEnter(descriptor) {
          if (!isEffectActive) return;
          const itemId =
            ((descriptor.data as { itemId?: string } | undefined)?.itemId ??
              descriptor.id) as string;
          const item = itemLookup.get(itemId);
          if (!item) {
            cueDebugLog("cue enter ignored (missing item)", { descriptor });
            return;
          }
          activeItems.set(item.id, item);
          cueDebugLog("cue enter", {
            id: item.id,
            term: item.term,
            start: descriptor.start,
            end: descriptor.end,
          });
          updateActiveItems();
        },
        onExit(descriptor) {
          if (!isEffectActive) return;
          const itemId =
            ((descriptor.data as { itemId?: string } | undefined)?.itemId ??
              descriptor.id) as string;
          if (activeItems.delete(itemId)) {
            cueDebugLog("cue exit", {
              id: itemId,
              start: descriptor.start,
              end: descriptor.end,
            });
            updateActiveItems();
          }
        },
      },
      {
        onAttachChange(attached) {
          if (!isEffectActive) return;
          setIsVocabularyCueAttached(attached);
           cueDebugLog("cue attachment change", { attached });
          if (!attached && activeItems.size) {
            activeItems.clear();
            updateActiveItems();
          }
        },
      }
    );

    // if (process.env.NODE_ENV !== "production") {
    //   console.log("[GlobalPlayerControls] cue registration complete", {
    //     episodeId,
    //     descriptorCount: descriptors.length,
    //   });
    // }

    cueDebugLog("cue registration complete", {
      descriptorCount: descriptors.length,
    });

    return () => {
      cueDebugLog("cue registration cleanup");
      unregister();
      setCueDrivenVocabularyItems([]);
      setIsVocabularyCueAttached(false);
      isEffectActive = false;
    };
  }, [
    showVocabularyCards,
    showDynamicIslandVocabulary,
    vocabularyIslandItems,
    cueSupportAvailable,
  ]);

  // 🔥 性能优化：降低 playbackTime 更新频率，避免频繁的 useMemo 重计算
  // 使用节流的 currentTime 而不是实时的 currentTime
  const [throttledCurrentTime, setThrottledCurrentTime] = useState(currentTime);
  const lastTimeUpdateRef = useRef(0);
  const TIME_UPDATE_THROTTLE = 500; // 500ms 更新一次，足够词汇岛使用
  
  useEffect(() => {
    const now = Date.now();
    if (now - lastTimeUpdateRef.current < TIME_UPDATE_THROTTLE) {
      return;
    }
    lastTimeUpdateRef.current = now;
    setThrottledCurrentTime(currentTime);
  }, [currentTime]);

  const playbackTimeForVocabulary = useMemo(() => {
    const candidate = videoCurrentTime ?? throttledCurrentTime;
    return Number.isFinite(candidate) ? candidate : 0;
  }, [videoCurrentTime, throttledCurrentTime]);

  useEffect(() => {
    if (!hasVideoPlayer) {
      setVideoCurrentTime(null);
      return;
    }
    setVideoCurrentTime(0);
  }, [hasVideoPlayer, videoConfig?.url]);

  const fallbackActiveVocabularyIslandItems = useMemo(
    () =>
      showVocabularyCards
        ? selectActiveVocabularyItems(vocabularyIslandItems, playbackTimeForVocabulary)
        : [],
    [showVocabularyCards, vocabularyIslandItems, playbackTimeForVocabulary]
  );

  const shouldUseCueDrivenVocabulary =
    showVocabularyCards &&
    cueSupportAvailable &&
    !isPlayerLoading &&
    cueDrivenVocabularyItems.length > 0;

  useEffect(() => {
    const mode = shouldUseCueDrivenVocabulary ? "cue" : "time-window";
    cueDebugLog("vocabulary activation mode", {
      mode,
      cueSupportAvailable,
      isVocabularyCueAttached,
      cueDrivenCount: cueDrivenVocabularyItems.length,
    });
    // if (process.env.NODE_ENV !== "production") {
    //   console.log("[GlobalPlayerControls] vocabulary activation mode", {
    //     mode,
    //     cueSupportAvailable,
    //     isVocabularyCueAttached,
    //     cueDrivenCount: cueDrivenVocabularyItems.length,
    //   });
    // }
  }, [
    shouldUseCueDrivenVocabulary,
    cueSupportAvailable,
    isVocabularyCueAttached,
    cueDrivenVocabularyItems.length,
  ]);

  const activeVocabularyIslandItems = useMemo(() => {
    if (!showVocabularyCards) {
      return [];
    }

    if (shouldUseCueDrivenVocabulary) {
      return cueDrivenVocabularyItems;
    }

    return fallbackActiveVocabularyIslandItems;
  }, [
    showVocabularyCards,
    shouldUseCueDrivenVocabulary,
    cueDrivenVocabularyItems,
    fallbackActiveVocabularyIslandItems,
  ]);

  const handleWordClick = useCallback(
    async (
      word: string,
      contextText: string,
      event: React.MouseEvent | React.TouchEvent
    ) => {
      // 先清理单词：去除首尾空白和标点符号（句号、逗号、感叹号、问号等）
      const cleanedWord = word.trim().replace(/^[^\w]+|[^\w]+$/g, '');
      if (!cleanedWord) {
        return;
      }

      if ("preventDefault" in event) {
        event.preventDefault();
      }
      if ("stopPropagation" in event) {
        event.stopPropagation();
      }

      // 允许字母、单引号和连字符（用于复合词如 jelly-like, well-known 等）
      const isValidWord = /^[a-zA-Z]+(?:[-'][a-zA-Z]+)*$/.test(cleanedWord);

      if (!isValidWord) {
        return;
      }

      // 使用清理后的单词
      const normalizedWord = cleanedWord;

      setSelectedWord(normalizedWord);
      setSelectedWordContext(contextText);
      setSelectedStatusValue("NEW");
      setShowWordDrawer(true);

      try {
        const hasLocal = await vocabularyLevelChecker.hasTranslationAsync(
          normalizedWord.toLowerCase()
        );
        if (!explanations[normalizedWord]) {
          await getExplanation(normalizedWord);
        }

        if (
          !hasLocal &&
          !translations[normalizedWord] &&
          isInitialized &&
          getTranslation
        ) {
          getTranslation(normalizedWord);
        }
      } catch (error) {
        console.warn(
          "Failed to load local vocabulary data:",
          normalizedWord,
          error
        );
        if (!translations[normalizedWord] && isInitialized && getTranslation) {
          getTranslation(normalizedWord);
        }
        if (!explanations[normalizedWord]) {
          getExplanation(normalizedWord);
        }
      }
    },
    [explanations, translations, getExplanation, getTranslation, isInitialized]
  );

  const handleTranscriptSeek = useCallback(
    (seconds: number) => {
      performSeek(seconds);
      if (!onSetActiveSubtitleIndex) {
        return;
      }

      const targetIndex = subtitles.findIndex((cue) => {
        const cueStart = Number.isFinite(cue.start) ? cue.start : 0;
        const cueEnd = Number.isFinite(cue.end)
          ? Number(cue.end)
          : cueStart + 0.6;
        return seconds >= cueStart - 0.05 && seconds <= cueEnd + 0.05;
      });

      if (targetIndex !== -1) {
        onSetActiveSubtitleIndex(targetIndex);
      }
    },
    [performSeek, onSetActiveSubtitleIndex, subtitles]
  );

  const handleVocabularyTimestampClick = useCallback(
    (timestamp: number, _endTimestamp?: number) => {
      if (!Number.isFinite(timestamp)) {
        return;
      }

      if (isPlaying) {
        shouldResumePlaybackRef.current = true;
      } else if (!isVocabularyNarrationOpen) {
        shouldResumePlaybackRef.current = false;
      }

      onPausePlayback?.();
      handleTranscriptSeek(timestamp);
      scrollActiveSubtitleIntoView();
    },
    [
      handleTranscriptSeek,
      onPausePlayback,
      scrollActiveSubtitleIntoView,
      isPlaying,
      isVocabularyNarrationOpen,
    ]
  );

  const pauseForPopIsland = useCallback(() => {
    if (isPlaying) {
      shouldResumeAfterPopIslandRef.current = true;
      onPausePlayback?.();
      return;
    }

    if (shouldResumePlaybackRef.current) {
      shouldResumeAfterPopIslandRef.current = true;
      shouldResumePlaybackRef.current = false;
      return;
    }

    shouldResumeAfterPopIslandRef.current = false;
  }, [isPlaying, onPausePlayback]);

  const handleVocabularyTermActivate = useCallback(
    (item: VocabularyIslandItem) => {
      setSelectedVocabularyTerm(item);
      setIsPopIslandOpen(false);
      pauseForPopIsland();
      setIsVocabularyNarrationOpen(true);
    },
    [pauseForPopIsland]
  );

  const handleTranscriptWordClick = useCallback(
    (
      word: {
        text: string;
        groupIndex: number;
      },
      event:
        | React.MouseEvent<HTMLSpanElement>
        | React.TouchEvent<HTMLSpanElement>
    ) => {
      const line = groupedTranscript[word.groupIndex];
      const contextText =
        line?.text || (subtitles[word.groupIndex]?.text ?? word.text);

      // 🎯 注意：TimeDrivenTranscript 已经通过 isLineActive 控制是否调用此函数
      // 只有 active line 内的单词点击才会触发此回调
      // 所以这里直接处理查词，不需要再次检查 isCurrentLine
      handleWordClick(word.text, contextText, event);
    },
    [
      groupedTranscript,
      handleWordClick,
      subtitles,
    ]
  );

  const handleWordDrawerOpenChange = useCallback((open: boolean) => {
    setShowWordDrawer(open);
    if (!open) {
      setSelectedWord("");
      setSelectedWordContext("");
      setSelectedStatusValue("NEW");
    }
  }, []);

  const handleSpeakWord = useCallback(() => {
    if (speakerRef.current) {
      speakerRef.current.click();
    }
  }, []);

  const handleSaveWord = useCallback(async () => {
    if (!userSettings?.myVocabularySetting) return;
    if (!selectedWord || isWordSaved || isWordSaving) return;

    const hasExplanation = Boolean(explanation);
    const hasTranslation = Boolean(translation);

    if (!hasExplanation && !hasTranslation) {
      return;
    }

    const options: any = {
      episodeId,
      episodeTitle,
      episodeThumbnail,
      contextText: selectedWordContext,
      status: selectedStatusValue,
    };

    if (!hasExplanation && translation) {
      options.youdaoTranslation = {
        translation: translation.translation,
        phonetic: translation.phonetic,
        usphone: translation.usphone,
        explains: translation.explains,
      };
    }

    await saveCard(selectedWord, options);
  }, [
    episodeId,
    episodeThumbnail,
    episodeTitle,
    explanation,
    translation,
    isWordSaved,
    isWordSaving,
    saveCard,
    selectedWord,
    selectedWordContext,
    selectedStatusValue,
    userSettings?.myVocabularySetting,
  ]);

  useEffect(() => {
    if (!selectedWord) return;

    const hasExplanation = Boolean(explanations[selectedWord]);
    if (!hasExplanation) {
      getExplanation(selectedWord);
    }

    if (!hasExplanation && !translations[selectedWord] && isInitialized) {
      getTranslation(selectedWord);
    }
  }, [
    selectedWord,
    explanations,
    translations,
    getExplanation,
    getTranslation,
    isInitialized,
  ]);

  useEffect(() => {
    if (previousVocabularyDataRef.current !== vocabularyData) {
      previousVocabularyDataRef.current = vocabularyData;
      hasAutoOpenedVocabularyIslandRef.current = false;
    }
  }, [vocabularyData]);

  useEffect(() => {
    if (!showVocabularyCards || !vocabularyData) {
      setHasActiveVocabularyTerms(false);
    }
  }, [showVocabularyCards, vocabularyData]);

  // 🌟 New Effect: Check if active vocabulary items are missing explanations for the current language
  // useEffect(() => {
  //   const itemsToCheck =
  //     activeVocabularyIslandItems.length > 0
  //       ? activeVocabularyIslandItems
  //       : vocabularyIslandItems;

    // if (isSettingsLoading) {
    //     if (process.env.NODE_ENV !== 'production') {
    //       console.log('[VocabularyIsland] Skipping check, settings loading...');
    //     }
    //   return;
    // }

    // Safety check: if languageSetting is 'english' but isLoading was false,
    // double check against default? No, trust isLoading.
    // But if 'english' is default and user IS english, it's fine.
    // If user is Chinese, it should be 'zh'.
    
    // if (!itemsToCheck.length || !episodeId || !userSettings?.languageSetting) {
    //     if (process.env.NODE_ENV !== 'production') {
    //       console.log('[VocabularyIsland] Check skipped', {
    //         activeItems: activeVocabularyIslandItems.length,
    //         totalItems: vocabularyIslandItems.length,
    //         episodeId,
    //         lang: userSettings?.languageSetting
    //       });
    //     }
    //     return;
    // }
    
    // const currentLangCode = userSettings.languageSetting.split('-')[0].toLowerCase();

    // if (process.env.NODE_ENV !== 'production') {
    //   console.log('[VocabularyIsland] Running missing explanation check', {
    //     items: itemsToCheck.length,
    //     lang: currentLangCode
    //   });
    // }

    // If user is using Chinese, and we have legacy data (strings), that's fine.
    // But if user is NOT using Chinese, we need to check if the object has the key.
    // Our normalizeChosenExplanationText helper now returns "" if language mismatch for legacy strings.
    
    /*
    // Disabled automatic bulk check to save tokens/costs.
    // We will rely on on-demand generation in NarrationModeIndicatorCard.
    const missingItems = itemsToCheck.filter(item => {
        // Using the helper from lib/vocabulary/chosen-explanation.ts
        // If it returns empty string, it means missing for this language.
        const rawValue = item.data?.chosen_explanation;
        const text = normalizeChosenExplanationText(rawValue, currentLangCode);
        
        // if (process.env.NODE_ENV !== 'production') {
        //   console.log('[VocabularyIsland] Item check', {
        //     term: item.term,
        //     lang: currentLangCode,
        //     rawType: typeof rawValue,
        //     rawKeys: typeof rawValue === 'object' ? Object.keys(rawValue || {}) : 'N/A',
        //     hasExplanation: Boolean(text)
        //   });
        // }

        return !text;
    });

    if (missingItems.length > 0) {
        // We found items that need explanation generation in the current language.
        // Trigger the generation API.
        // Note: The generation API (chosen-explanation) usually takes the whole vocabulary payload
        // but here we might want to just trigger a re-run for the specific language.
        // The existing API /api/episodes/[id]/vocabulary/chosen-explanation handles merging.
        // So we can just call it. It will scan the vocabulary and fill in missing parts.
        
        // Debounce or check if already requested to avoid spamming?
        // For simplicity, let's just log and trigger. 
        // Ideally we should have a 'isGeneratingExplanations' state.
        
        console.log(`[VocabularyIsland] Missing explanations detected for ${currentLangCode}`, {
          missingCount: missingItems.length,
          missingTerms: missingItems.map(item => item.term)
        });
        
        const triggerGeneration = async () => {
            const headers: HeadersInit = { 'Content-Type': 'application/json' };
            
            // Get token from Supabase session instead of direct localStorage
            const { data: { session } } = await supabase.auth.getSession();
            const token = session?.access_token;

            if (!token) {
              console.warn(
                '[VocabularyIsland] Missing auth token, cannot trigger chosen-explanation generation.'
              );
              return;
            }
    
            headers.Authorization = `Bearer ${token}`;
    
            try {
                const res = await fetch(`/api/episodes/${episodeId}/vocabulary/chosen-explanation`, {
                    method: 'POST',
                    headers,
                    // The API will read the vocabulary from Redis, generate missing ones for the target language, and save back.
                    // We pass targetLanguage explicitly to ensure backend uses the correct one even if Redis is stale.
                    body: JSON.stringify({ targetLanguage: userSettings.languageSetting }) 
                });
                
                if (res.ok) {
                    console.log('[VocabularyIsland] Generation triggered successfully');
                    // Optionally refresh vocabulary data here if we have a way to do it.
                    // The API might return the updated vocabulary.
                    const data = await res.json();
                    if (data?.vocabulary && process.env.NODE_ENV !== 'production') {
                        console.log('[VocabularyIsland] Received updated vocabulary payload from API');
                    }
                        if (data?.vocabulary) {
                            // We can't use setCurrentEpisode here as it's not available in this scope.
                            // But we can rely on the backend update and the user refreshing or subsequent updates.
                            // Or we could pass setCurrentEpisode as a prop if we refactor.
                            // For now, let's just log.
                            console.log('[VocabularyIsland] Vocabulary updated in backend.');
                        }
                } else {
                    // Log status code and text for debugging 401s
                    const text = await res.text();
                    console.error(`[VocabularyIsland] Generation failed with status ${res.status}`, text);
                    // throw new Error(`API call failed with status: ${res.status}`);
                }
            } catch (e) {
                console.error('[VocabularyIsland] Failed to trigger generation:', e);
            }
        };
        
        triggerGeneration();
    } else {
        if (process.env.NODE_ENV !== 'production') {
          console.log('[VocabularyIsland] All items already have explanations for current language', {
            lang: currentLangCode,
            total: activeVocabularyIslandItems.length
          });
        }
    }
    */
  // }, [activeVocabularyIslandItems, vocabularyIslandItems, episodeId, userSettings?.languageSetting, isSettingsLoading]);

  useEffect(() => {
    setHasActiveVocabularyTerms(activeVocabularyIslandItems.length > 0);
  }, [activeVocabularyIslandItems.length]);

  useEffect(() => {
    if (!hasActiveVocabularyTerms) {
      if (!showTranslations) {
        setIsPopIslandOpen(false);
      }
      // Don't close narration drawer if user is actively viewing a term
      // Only close if there's no selected term (user hasn't opened it yet)
      if (!selectedVocabularyTerm) {
        setIsVocabularyNarrationOpen(false);
      }
    }
  }, [hasActiveVocabularyTerms, showTranslations, selectedVocabularyTerm]);

  useEffect(() => {
    const isNarrationVisible =
      isVocabularyNarrationOpen && Boolean(selectedVocabularyTerm);
    if (wasVocabularyNarrationVisibleRef.current && !isNarrationVisible) {
      if (shouldResumePlaybackRef.current && !isPlaying) {
        onResumePlayback?.();
      }
      shouldResumePlaybackRef.current = false;
    }
    wasVocabularyNarrationVisibleRef.current = isNarrationVisible;
  }, [
    isVocabularyNarrationOpen,
    selectedVocabularyTerm,
    onResumePlayback,
    isPlaying,
  ]);

  useEffect(() => {
    if (isPopIslandOpen) {
      return;
    }

    if (shouldResumeAfterPopIslandRef.current && !isPlaying) {
      onResumePlayback?.();
    }
    shouldResumeAfterPopIslandRef.current = false;
  }, [isPopIslandOpen, isPlaying, onResumePlayback]);

  // 笑脸图标旋转动画（摇头）
  useEffect(() => {
    if (!open) {
      // 清理所有旋转动画
      rotationTimeoutIdsRef.current.forEach(id => clearTimeout(id));
      rotationTimeoutIdsRef.current = [];
      return;
    }

    let isActive = true;

    // 等待 DOM 元素渲染完成
    const initTimeoutId = setTimeout(() => {
      const svg = faceIconRef.current;
      if (!svg || !isActive) return;

      const randomRotate = () => {
        if (!isActive || !svg) return;
        // 生成 -30 到 30 度之间的随机角度
        const angle = Math.random() * 60 - 30;
        svg.style.transform = `rotate(${angle}deg)`;
        svg.style.transformOrigin = 'center center';
      };

      // 初始转动
      randomRotate();

      // 每 2-5 秒随机转动一次
      const scheduleNextRotation = () => {
        if (!isActive || !svg) return;
        const delay = Math.random() * 3000 + 2000; // 2000-5000ms
        const id = setTimeout(() => {
          if (isActive && svg) {
            randomRotate();
            scheduleNextRotation();
          }
        }, delay);
        rotationTimeoutIdsRef.current.push(id);
      };

      scheduleNextRotation();
    }, 100);
    rotationTimeoutIdsRef.current.push(initTimeoutId);

    return () => {
      isActive = false;
      rotationTimeoutIdsRef.current.forEach(id => clearTimeout(id));
      rotationTimeoutIdsRef.current = [];
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      hasAutoScrolledRef.current = false;
      return;
    }

    if (activeSubtitleIndex == null || subtitles.length === 0) {
      return;
    }

    const hasAlreadyAutoScrolled = hasAutoScrolledRef.current;
    const behavior: ScrollIntoViewOptions["behavior"] = hasAlreadyAutoScrolled
      ? "smooth"
      : "auto";

    const rafId = requestAnimationFrame(() => {
      scrollActiveSubtitleIntoView(behavior);
    });

    let timeoutId: number | null = null;
    if (!hasAlreadyAutoScrolled) {
      timeoutId = window.setTimeout(() => {
        scrollActiveSubtitleIntoView(behavior);
      }, 300);
      hasAutoScrolledRef.current = true;
    }

    return () => {
      cancelAnimationFrame(rafId);
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [
    open,
    activeSubtitleIndex,
    subtitles.length,
    scrollActiveSubtitleIntoView,
  ]);

  useEffect(() => {
    const shouldDisplayIsland =
      showVocabularyCards &&
      hasActiveVocabularyTerms &&
      showDynamicIslandVocabulary;

    const now = Date.now();
    const timeSinceLastToggle = now - lastIslandToggleAtRef.current;

    if (shouldDisplayIsland) {
      if (
        !hasAutoOpenedVocabularyIslandRef.current &&
        timeSinceLastToggle > 600
      ) {
        openMusicIsland();
        hasAutoOpenedVocabularyIslandRef.current = true;
        lastIslandToggleAtRef.current = now;
        lastIslandAutoOpenAtRef.current = now;
      }
    } else {
      hasAutoOpenedVocabularyIslandRef.current = false;
      const timeSinceOpen = now - lastIslandAutoOpenAtRef.current;
      if (
        islandView === "music" &&
        timeSinceOpen > 1200 &&
        timeSinceLastToggle > 600
      ) {
        closeMusicIsland();
        lastIslandToggleAtRef.current = now;
      }
    }
  }, [
    showVocabularyCards,
    hasActiveVocabularyTerms,
    islandView,
    openMusicIsland,
    closeMusicIsland,
    showDynamicIslandVocabulary,
  ]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    window.dispatchEvent(
      new CustomEvent("skiper-drawer-open-change", {
        detail: { open: isPopIslandOpen || isVocabularyNarrationOpen },
      })
    );

    if (isPopIslandOpen || isVocabularyNarrationOpen) {
      document.body.classList.add("skiper-drawer-open");
    } else {
      document.body.classList.remove("skiper-drawer-open");
    }

    return () => {
      document.body.classList.remove("skiper-drawer-open");
    };
  }, [isPopIslandOpen, isVocabularyNarrationOpen]);

  const currentTimeLabel = formatTime(currentTime);
  const durationLabel = formatTime(duration || 0);
  const showDurationSpinner = isPlayerLoading || !duration;

  const formatPaddedLabel = useCallback((label: string | undefined) => {
    if (!label || !label.includes(":")) {
      return label ?? "00:00";
    }
    const [mins = "0", secs = "0", ...rest] = label.split(":");
    const padded = [
      `${mins}`.padStart(2, "0"),
      `${secs}`.padStart(2, "0"),
      ...rest,
    ];
    return padded.join(":");
  }, []);

  const paddedCurrentTime = formatPaddedLabel(currentTimeLabel);
  const paddedDuration = formatPaddedLabel(
    duration ? durationLabel : undefined
  );
  const isMetadataMissing =
    !episodeTitle || !episodeAuthor || !duration || duration <= 0;
  const shouldShowInfoSkeleton = isPlayerLoading || isMetadataMissing;
  const infoSkeletonTone = isDarkTheme ? "bg-white/15" : "bg-black/10";

  const shouldRenderVocabularyIsland =
    showVocabularyCards &&
    showDynamicIslandVocabulary &&
    activeVocabularyIslandItems.length > 0;

  const canInteractWithDynamicIsland = shouldRenderVocabularyIsland;
  const dynamicIslandDisabledHint = canInteractWithDynamicIsland
    ? undefined
    : t(
        "player.dynamicIsland.emptyState",
        "Vocabulary tips will appear here when they're available."
      );
  const dynamicIslandIdleHandler = canInteractWithDynamicIsland
    ? openMusicIsland
    : undefined;

  useEffect(() => {
    if (!canInteractWithDynamicIsland && islandView !== "idle") {
      closeMusicIsland();
    }
  }, [canInteractWithDynamicIsland, closeMusicIsland, islandView]);

  // useEffect(() => {
  //   if (process.env.NODE_ENV === "production") {
  //     return;
  //   }

  //   const reason = !showVocabularyCards
  //     ? "no-vocabulary-cards"
  //     : !showDynamicIslandVocabulary
  //     ? "feature-disabled"
  //     : activeVocabularyIslandItems.length === 0 || !hasActiveVocabularyTerms
  //     ? "no-active-terms"
  //     : !shouldRenderVocabularyIsland
  //     ? "no-render"
  //     : "ready";

  //   console.log("[FullPlayerDialog] dynamic island state", {
  //     reason,
  //     episodeId,
  //     hasVideoPlayer,
  //     shouldRenderVocabularyIsland,
  //     showVocabularyCards,
  //     showDynamicIslandVocabulary,
  //     hasActiveVocabularyTerms,
  //     activeVocabularyIslandItemsLength: activeVocabularyIslandItems.length,
  //     vocabularyItemCount: vocabularyIslandItems.length,
  //     hasVocabularyData: Boolean(vocabularyData),
  //     cueSupportAvailable,
  //   });
  // }, [
  //   shouldRenderVocabularyIsland,
  //   showVocabularyCards,
  //   showDynamicIslandVocabulary,
  //   activeVocabularyIslandItems.length,
  //   hasActiveVocabularyTerms,
  //   episodeId,
  //   hasVideoPlayer,
  //   vocabularyIslandItems.length,
  //   vocabularyData,
  //   cueSupportAvailable,
  // ]);

  const vocabularyIslandContent = useMemo(() => {
    if (!shouldRenderVocabularyIsland) {
      return undefined;
    }

    const currentTimeForContent = shouldUseCueDrivenVocabulary
      ? undefined
      : playbackTimeForVocabulary;

    return (
      <VocabularyIslandContent
        items={activeVocabularyIslandItems}
        currentTime={currentTimeForContent}
        onTimestampClick={handleVocabularyTimestampClick}
        formatTime={formatTime}
        showTranslation={showTranslations}
        onTermActivate={handleVocabularyTermActivate}
        languageCode={userSettings?.languageSetting?.split('-')[0]?.toLowerCase()}
        setCurrentEpisode={setCurrentEpisode}
      />
    );
  }, [
    shouldRenderVocabularyIsland,
    activeVocabularyIslandItems,
    handleVocabularyTimestampClick,
    formatTime,
    showTranslations,
    handleVocabularyTermActivate,
    shouldUseCueDrivenVocabulary ? "cue-mode" : playbackTimeForVocabulary,
    userSettings?.languageSetting,
    setCurrentEpisode,
  ]);

  const renderMobileControls = () => {
    const nextPlaybackSpeed = () => {
      const steps = [0.75, 1, 1.25, 1.5, 2];
      const currentIndex = steps.findIndex(
        (step) => Math.abs(step - playbackSpeed) < 0.001
      );
      const nextIndex =
        currentIndex === -1 ? 1 : (currentIndex + 1) % steps.length;
      const nextSpeed = steps[nextIndex];
      onChangePlaybackSpeed(nextSpeed);
      if (hasVideoPlayer && videoControllerRef.current) {
        try {
          videoControllerRef.current.setPlaybackRate(nextSpeed);
        } catch {
          // ignore unsupported playback rate changes
        }
      }
    };

    const handleSeekChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(event.target.value);
      if (!Number.isNaN(value)) {
        performSeek(value);
      }
    };

    const controlsTextClass = isDarkTheme ? "text-white" : "text-foreground";

    const accentRangeClass = isDarkTheme ? "accent-white" : "accent-black";

    const speedButtonClass = isDarkTheme
      ? "bg-white/10 text-white hover:bg-white/20"
      : "bg-black/5 text-foreground hover:bg-black/10";

    const seekButtonClass = isDarkTheme
      ? "bg-white/10 text-white hover:bg-white/20"
      : "bg-black/5 text-foreground hover:bg-black/10";

    const playButtonClass = isDarkTheme
      ? "bg-white text-black hover:bg-white/90"
      : "bg-black text-white hover:bg-black/90";

    const translationButtonClass = isDarkTheme
      ? "bg-white/10 text-white hover:bg-white/20"
      : "bg-black/5 text-foreground hover:bg-black/10";

    const handleTranslationButtonClick = () => {
      if (isPopIslandOpen) {
        setIsPopIslandOpen(false);
        return;
      }

      pauseForPopIsland();
      setIsVocabularyNarrationOpen(false);
      setIsPopIslandOpen(true);
    };

    const handlePlayPauseClick = (e: React.MouseEvent) => {
      // 在移动设备上，如果 touchstart 已经处理过，忽略 click 事件
      if (playPauseTouchHandledRef.current) {
        e.stopPropagation();
        playPauseTouchHandledRef.current = false;
        return;
      }
      
      e.stopPropagation();
      
      // 防抖：防止快速连续点击
      const now = Date.now();
      const timeSinceLastAction = now - playPauseLastActionTimeRef.current;
      
      if (timeSinceLastAction < PLAY_PAUSE_DEBOUNCE_MS) {
        // console.log('[PlayPause] Debounced click, too quick:', timeSinceLastAction + 'ms');
        return;
      }
      
      playPauseLastActionTimeRef.current = now;
      playPauseLastActionRef.current = isPlaying ? 'pause' : 'play';

      attemptVideoPlaybackToggle();
      onTogglePlayPause();
    };

    const handlePlayPauseTouchStart = (e: React.TouchEvent) => {
      e.stopPropagation();
      
      // 防抖：防止快速连续触摸
      const now = Date.now();
      const timeSinceLastAction = now - playPauseLastActionTimeRef.current;
      
      if (timeSinceLastAction < PLAY_PAUSE_DEBOUNCE_MS) {
        // console.log('[PlayPause] Debounced touch, too quick:', timeSinceLastAction + 'ms');
        return;
      }
      
      playPauseTouchHandledRef.current = true;
      playPauseLastActionTimeRef.current = now;
      playPauseLastActionRef.current = isPlaying ? 'pause' : 'play';
      
      attemptVideoPlaybackToggle();
      onTogglePlayPause();
      
      // 清除标记，防止后续的 click 事件被忽略（如果用户真的想点击）
      setTimeout(() => {
        playPauseTouchHandledRef.current = false;
      }, 300);
    };

    return (
      <div className="relative flex flex-col sm:gap-5 gap-1 px-6 pb-6">

        <div className="relative z-10">
          <div
            className={cn(
              "flex items-center justify-between text-xs tabular-nums",
              isDarkTheme ? "text-white/60" : "text-muted-foreground"
            )}
          >
            <span>{formatTime(currentTime)}</span>
            <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5">
              {isPlayerLoading ||
              !Number.isFinite(duration) ||
              duration <= 0 ? (
                <SpinnerIcon
                  size={11}
                  color={
                    isDarkTheme
                      ? "rgba(255,255,255,0.7)"
                      : "hsl(var(--muted-foreground))"
                  }
                  className="flex-shrink-0"
                />
              ) : (
                <span>{formatTime(duration)}</span>
              )}
            </span>
          </div>
          <div
            id={GLOBAL_PLAYER_TIME_SLIDER_CONTAINER_ID}
            className="mt-0 w-full"
          >
            {!isTimeSliderMounted && (
              <input
                type="range"
                min={0}
                max={duration || 0}
                step={0.1}
                value={Math.min(currentTime, duration || 0)}
                onChange={handleSeekChange}
                className={cn("w-full", accentRangeClass)}
                aria-label={t("episode.player.seekSliderLabel", "Seek")}
              />
            )}
          </div>
        </div>

        <div
          className={cn(
            "relative z-10 flex items-center justify-between",
            controlsTextClass
          )}
        >
          <div className="flex flex-col items-center gap-0 translate-x-4">
            <button
              type="button"
              onClick={nextPlaybackSpeed}
              className={cn(
                "rounded-full px-3 py-1 text-sm font-semibold transition -translate-y-3",
                speedButtonClass
              )}
            >
              {playbackSpeed.toFixed(2).replace(/\.00$/, "")}x
            </button>
            {onShadowingClick && (
              <button
                type="button"
                onClick={onShadowingClick}
                className={cn(
                  "rounded-full px-3 py-3 transition",
                  "bg-white/20 hover:bg-white/40",
                  "text-foreground hover:text-primary shadow-sm",
                  speedButtonClass
                )}
                title={t('shadowing.title', 'Shadowing Practice')}
                aria-label={t('shadowing.title', 'Shadowing Practice')}
              >
                <Mic className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-6">
            <button
              type="button"
              onClick={onSkipBackward}
              className={cn(
                "relative rounded-full p-3 transition",
                seekButtonClass
              )}
            >
              <RotateCcw className="h-5 w-5" />
              <span className="absolute bottom-2 text-black left-1/2 -translate-x-1/2 rounded-[3px] border border-current bg-white px-1 text-[10px] font-semibold leading-none">
                15
              </span>
            </button>
            <button
              type="button"
              onClick={handlePlayPauseClick}
              onTouchStart={handlePlayPauseTouchStart}
              className={cn(
                "rounded-full p-2 shadow-lg transition touch-manipulation",
                "relative z-20 select-none",
                "isolate", // 创建新的堆叠上下文，防止被其他元素影响
                playButtonClass
              )}
              style={{
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                isolation: 'isolate', // CSS isolation 确保按钮独立
              }}
              aria-label={isPlaying ? t("episode.player.pause", "Pause") : t("episode.player.play", "Play")}
            >
              {isPlaying ? (
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="48" 
                height="48" 
                fill="currentColor" 
                className="bi bi-pause-fill pointer-events-none" 
                viewBox="0 0 16 16"
                style={{ pointerEvents: 'none' }}
              >
                <path d="M5.5 3.5A1.5 1.5 0 0 1 7 5v6a1.5 1.5 0 0 1-3 0V5a1.5 1.5 0 0 1 1.5-1.5m5 0A1.5 1.5 0 0 1 12 5v6a1.5 1.5 0 0 1-3 0V5a1.5 1.5 0 0 1 1.5-1.5"/>
              </svg>
              ) : (
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="48" 
                height="48" 
                fill="currentColor" 
                className="bi bi-play-fill translate-x-0.5 pointer-events-none" 
                viewBox="0 0 16 16"
                style={{ pointerEvents: 'none' }}
              >
                <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393"/>
              </svg>
              )}
            </button>
            <button
              type="button"
              onClick={onSkipForward}
              className={cn(
                "relative rounded-full p-3 transition",
                seekButtonClass
              )}
            >
              <RotateCw className="h-5 w-5" />
              <span className="absolute bottom-2 text-black left-1/2 -translate-x-1/2 rounded-[3px] border border-current bg-white px-1 text-[10px] font-semibold leading-none">
                15
              </span>
            </button>
          </div>

          <button
            type="button"
            onClick={handleTranslationButtonClick}
            className={cn(
              "rounded-full p-0 w-10 h-10 flex items-center justify-center transition",
              translationButtonClass
            )}
            aria-pressed={isPopIslandOpen}
            >
            <svg
              ref={faceIconRef}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 256 256"
              fill="none"
              role="img"
              aria-hidden="true"
              className="w-full h-full"
              style={{ 
                transition: 'transform 0.8s ease-in-out',
                transformOrigin: 'center center'
              }}
            >
              <circle cx="128" cy="128" r="128" fill="#F97316"></circle>
              <ellipse
                cx="100.5546299346353" 
                cy="140.5667763724447" 
                rx="18" 
                ry="18" 
                fill="#fff" 
                className="face-icon-blink"
              ></ellipse>
              <ellipse 
                cx="147.3546299346353" 
                cy="140.5667763724447" 
                rx="18" 
                ry="18" 
                fill="#fff" 
                className="face-icon-blink"
              ></ellipse>
            </svg>
            <span className="sr-only">
              {isPopIslandOpen
                ? "Hide translation pop island"
                : "Show translation pop island"}
            </span>
          </button>
        </div>
      </div>
    );
  };

  // 🎯 视频组件始终渲染以保证音频播放（play() 函数依赖视频组件处理播放）
  // 通过 CSS 隐藏视频画面，但保持音频播放
  const videoPlayerPortal =
    videoPortalContainer && hasVideoPlayer && videoConfig
      ? createPortal(
          <VideoPlayerVidstack
            url={videoConfig.url}
            thumbnailUrl={episodeThumbnail}
            className="w-full global-player-video"
            currentTime={currentTime}
            isPlaying={isPlaying}
            onSeekTo={handleVideoApiReady}
            onTimeUpdate={handleVideoTimeUpdate}
            onDurationChange={handleVideoDurationChange}
            onPlayPause={handleVideoPlayPause}
            onEnded={handleVideoEnded}
            title={episodeTitle || ""}
            artist={episodeAuthor || undefined}
            artwork={videoConfig.artwork}
            textTracks={mergedTextTracks}
            enableTapToggle
            maintainAspectRatio
            borderRadius="20px 20px 0 0"
            immersiveSlot={
              <motion.button
                type="button"
                onClick={handleHideVideo}
                className={cn(
                  "inline-flex items-center gap-1 px-2 py-2 text-xs font-bold transition",
                  isDarkTheme ? " text-white" : " text-white"
                )}
                whileTap={{ scale: 0.92 }}
                whileHover={{ scale: 1.02 }}
              >
                <ChevronsDownUp className="h-3.5 w-3.5 font-bold" strokeWidth={3.5} />
                {t("episode.videoPlayer.hideVideo", "")}
              </motion.button>
            }
          />,
          videoPortalContainer
        )
      : null;

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        shouldScaleBackground={false}
      >
        <DrawerContent
          forceMount
          style={{
            height: "var(--app-viewport-height, 100vh)",
            maxHeight: "var(--app-viewport-height, 100vh)",
          }}
          className={cn(
            "h-screen max-h-screen overflow-hidden rounded-none border-none bg-transparent p-0",
            "supports-[height:100dvh]:h-[100dvh]",
            "supports-[max-height:100dvh]:max-h-[100dvh]",
            "origin-bottom transition-transform duration-300 ease-out",
            "data-[state=closed]:translate-y-full",
            "data-[state=open]:translate-y-0"
          )}
          onPointerDownOutside={(e) => {
            // Prevent closing if clicking on ShadowingPracticeCard or control buttons
            const target = e.target as HTMLElement;
            if (
              target.closest('[data-shadowing-card]') ||
              target.closest('button[type="button"]') ||
              target.closest('.rounded-full.p-2.shadow-lg')
            ) {
              e.preventDefault();
            }
          }}
          onInteractOutside={(e) => {
            // Prevent closing if clicking on ShadowingPracticeCard or control buttons
            const target = e.target as HTMLElement;
            if (
              target.closest('[data-shadowing-card]') ||
              target.closest('button[type="button"]') ||
              target.closest('.rounded-full.p-2.shadow-lg')
            ) {
              e.preventDefault();
            }
          }}
        >
          <div
            className={cn(
              "relative flex h-full flex-col",
              isDarkTheme ? "text-white" : "text-foreground"
            )}
          >
            <div
              className="pointer-events-none absolute inset-0 transition-[background] duration-500 ease-out"
              style={gradientBackgroundStyle}
            />
            {episodeThumbnail && (
              <div
                className={cn(
                  "pointer-events-none absolute inset-0 bg-cover bg-center",
                  isDarkTheme
                    ? "blur-lg scale-110 opacity-80"
                    : "blur-2xl scale-105"
                )}
                style={{ backgroundImage: `url(${episodeThumbnail})` }}
              />
            )}
            <div
              className="pointer-events-none absolute inset-0 transition-[background] duration-500 ease-out"
              style={overlayGradientStyle}
            />
            <div className="relative z-10 flex h-full flex-col overflow-hidden">
              {/* Drag Handle - 可拖动区域，用户可以从这里向下拖动关闭 */}
              <div 
                className="flex justify-center pt-1 pb-0.5 flex-shrink-0 cursor-grab active:cursor-grabbing"
                style={{ 
                  touchAction: 'pan-y',
                  WebkitTouchCallout: 'none',
                  WebkitUserSelect: 'none',
                  userSelect: 'none'
                }}
              >
                <div 
                  className={cn(
                    "w-10 h-1 rounded-full transition-all duration-200",
                    isDarkTheme ? "bg-white/40 hover:bg-white/60" : "bg-black/30 hover:bg-black/40"
                  )} 
                />
              </div>

              <div className="mt-0 sm:mt-3 flex flex-col gap-0 lg:px-16 xl:px-24 2xl:px-32 flex-shrink-0">
                <motion.div
                  layout
                  initial={false}
                  animate={{
                    opacity: showVideoInUi ? 1 : 0,
                  }}
                  transition={{ type: "spring", bounce: 0.22, duration: 0.45 }}
                  style={{
                    overflow: "hidden",
                    height: showVideoInUi ? "auto" : 0,
                  }}
                  className={cn(
                    "bg-transparent rounded-t-[20px]",
                    isDarkTheme ? "border-white/15" : "border-black/10"
                  )}
                >
                  <div
                    className={cn(
                      "relative w-full",
                      showVideoInUi
                        ? "pointer-events-auto"
                        : "pointer-events-none"
                    )}
                    aria-hidden={!showVideoInUi}
                    style={{ visibility: showVideoInUi ? "visible" : "hidden" }}
                  >
                    {hasVideoPlayer && videoConfig ? (
                      <div
                        ref={registerVideoHost}
                        className="relative h-full w-full"
                      />
                    ) : null}
                  </div>
                </motion.div>
                {showVideoInUi && hasVideoPlayer && (
                  <div className="mt-1 px-2 sm:px-4 lg:px-8 pb-1">
                    {shouldShowInfoSkeleton ? (
                      <>
                        <Skeleton
                          className={cn("h-4 w-40 max-w-full", infoSkeletonTone)}
                        />
                        <Skeleton
                          className={cn(
                            "h-3 w-24 max-w-full mt-1.5",
                            infoSkeletonTone
                          )}
                        />
                      </>
                    ) : (
                      <>
                        <p
                          className={cn(
                            "truncate text-base font-semibold leading-tight",
                            isDarkTheme ? "text-white" : "text-foreground"
                          )}
                          title={
                            episodeTitle ??
                            t("episode.player.untitledEpisode", "Untitled Episode")
                          }
                        >
                          {episodeTitle ||
                            t("episode.player.untitledEpisode", "Untitled Episode")}
                        </p>
                        {episodeAuthor ? (
                          <p
                            className={cn(
                              "truncate text-xs",
                              isDarkTheme ? "text-white/60" : "text-muted-foreground"
                            )}
                            title={episodeAuthor}
                          >
                            {episodeAuthor}
                          </p>
                        ) : null}
                      </>
                    )}
                  </div>
                )}
                {(!showVideoInUi || !hasVideoPlayer) && (
                  <motion.div
                    className={cn(
                      "flex gap-4 pb-1 px-3",
                      showVideoInUi ? "items-start" : "items-center",
                      !showVideoInUi && hasVideoPlayer
                        ? "cursor-pointer"
                        : undefined
                    )}
                    layout
                    transition={{
                      layout: { type: "spring", bounce: 0.25, duration: 0.45 },
                    }}
                    whileTap={
                      hasVideoPlayer && !showVideoInUi ? { scale: 0.98 } : undefined
                    }
                    onClick={handleInfoRowActivate}
                    onKeyDown={(event) => {
                      if (
                        !showVideoInUi &&
                        hasVideoPlayer &&
                        (event.key === "Enter" || event.key === " ")
                      ) {
                        event.preventDefault();
                        handleInfoRowActivate();
                      }
                    }}
                    role={!showVideoInUi && hasVideoPlayer ? "button" : undefined}
                    tabIndex={!showVideoInUi && hasVideoPlayer ? 0 : -1}
                    aria-pressed={
                      showVideoInUi && hasVideoPlayer ? true : undefined
                    }
                    aria-label={
                      !showVideoInUi && hasVideoPlayer
                        ? t("episode.videoPlayer.showVideo", "Show video")
                        : undefined
                    }
                  >
                    {(!hasVideoPlayer || !isVideoVisible) &&
                      (episodeThumbnail ? (
                        <img
                          src={episodeThumbnail}
                          alt={episodeTitle || "Episode thumbnail"}
                          className={cn(
                            "h-14 w-14 rounded-xl border object-cover",
                            isDarkTheme ? "border-white/20" : "border-black/20"
                          )}
                        />
                      ) : (
                        <div
                          className={cn(
                            "h-14 w-14 rounded-xl border",
                            isDarkTheme
                              ? "border-white/20 bg-white/10"
                              : "border-black/20 bg-black/5"
                          )}
                        />
                      ))}
                    {(!hasVideoPlayer || !isVideoVisible) && (
                      <div className="flex min-w-0 flex-col sm:gap-1 gap-0">
                        {shouldShowInfoSkeleton ? (
                          <>
                            <Skeleton
                              className={cn("h-4 w-40 max-w-full", infoSkeletonTone)}
                            />
                            <Skeleton
                              className={cn(
                                "h-3 w-24 max-w-full mt-1.5",
                                infoSkeletonTone
                              )}
                            />
                            <Skeleton
                              className={cn(
                                "h-3 w-28 max-w-full mt-1",
                                infoSkeletonTone
                              )}
                            />
                          </>
                        ) : (
                          <>
                            <p className="truncate text-[15px] font-medium">
                              {episodeTitle || "Now Playing"}
                            </p>
                            {episodeAuthor && (
                              <p
                                className={cn(
                                  "text-sm",
                                  isDarkTheme
                                    ? "text-muted/70"
                                    : "text-foreground/80"
                                )}
                              >
                                {episodeAuthor}
                              </p>
                            )}
                            <p
                              className={cn(
                                "text-xs",
                                isDarkTheme
                                  ? "text-muted/50"
                                  : "text-foreground/50"
                              )}
                            >
                              <span
                                className={cn(
                                  "inline-flex items-center gap-1 px-0 py-0.5 tabular-nums",
                                  isDarkTheme ? "" : ""
                                )}
                              >
                                <span>{paddedCurrentTime}</span>
                                <span>/</span>
                                {showDurationSpinner ? (
                                  <SpinnerIcon
                                    size={11}
                                    color={
                                      isDarkTheme
                                        ? "rgba(255,255,255,0.7)"
                                        : "hsl(var(--muted-foreground))"
                                    }
                                    className="flex-shrink-0 !inline-flex !w-auto !h-auto !cursor-default"
                                  />
                                ) : (
                                  <span>{paddedDuration}</span>
                                )}
                              </span>
                            </p>
                          </>
                        )}
                      </div>
                    )}
                  </motion.div>
                )}
              </div>

              <div className="flex flex-1 flex-col min-h-0 overflow-hidden pb-0">
                <div className="relative w-full px-2 sm:max-w-4xl mx-auto flex-1 min-h-0 flex flex-col pb-0.5">
                 <div
                    className="pointer-events-none absolute left-0 top-0 w-full select-none"
                  />
                  {/* 🎯 Chapters Carousel - displayed above transcript */}
                  {showChapters && (chapters.length > 0 || isGeneratingChapters) && (
                    <ChaptersCarousel
                      chapters={chapters}
                      currentTime={currentTime}
                      onSeek={handleTranscriptSeek}
                      backgroundTone={transcriptBackgroundTone}
                      isLoading={isGeneratingChapters}
                      className="mb-1.5"
                    />
                  )}
                  <div className="flex-1 min-h-0 flex flex-col">
                    <TimeDrivenTranscript
                      groupedSentences={showTranscriptGenerationDialog && !transcriptGenerationConfirmed ? [] : groupedTranscript}
                      currentTime={currentTime}
                      onSeek={handleTranscriptSeek}
                      showTranslation={showTranslations}
                      showDynamicIslandVocabulary={showDynamicIslandVocabulary}
                      backgroundTone={transcriptBackgroundTone}
                      className="!mt-0 !h-full !flex-1 overflow-hidden flex flex-col"
                      contentWrapperClassName="flex-1 overflow-y-auto overscroll-contain touch-pan-y !pb-2 pr-1"
                      scrollContainerRef={transcriptScrollRef}
                      onWordClick={handleTranscriptWordClick}
                      autoScrollToActive={true}
                      staticVocabularyTerms={staticVocabularyHighlights}
                      onToggleTranslation={() => setShowTranslations(!showTranslations)}
                      onToggleDynamicIslandVocabulary={(value) =>
                        setShowDynamicIslandVocabulary(value)
                      }
                      onScrollToActive={scrollActiveSubtitleIntoView}
                      isLoading={isTranscriptLoading || (showTranscriptGenerationDialog && !transcriptGenerationConfirmed)}
                      emptyStateMessage="No subtitles available for this episode."
                      enableSwipeToVocabulary={showVocabularyCards}
                      keywordsContent={
                        showVocabularyCards && vocabularyData?.scenarios ? (
                          <div key={`keywords-wrapper-${episodeId || 'default'}`} className="w-full h-full">
                            <VocabularyDisplay
                              data={vocabularyData}
                              isLoading={false}
                              onTimestampClick={(timestamp, endTimestamp) => {
                                handleTranscriptSeek(timestamp);
                              }}
                              forceReveal={false}
                              userSettings={userSettings}
                              session={user ? { access_token: user.id, user } : undefined}
                              episodeId={episodeId}
                              episodeTitle={episodeTitle}
                              episodeThumbnail={episodeThumbnail}
                              isActive={currentTranscriptView === 'keywords'}
                              onVocabularyUpdate={onVocabularyUpdate}
                            />
                          </div>
                        ) : undefined
                      }
                      vocabularyContent={
                        showVocabularyCards && userSettings?.languageSetting?.startsWith('zh') ? (
                          <div key={`vocabulary-wrapper-${episodeId || 'default'}`} className="w-full h-full p-2 px-8">
                              <PhraseSection
                                groupedSentences={groupedTranscript}
                                vocabularyTerms={staticVocabularyHighlights.map(item => item.term)}
                                vocabulary={vocabularyData}
                                isVocabularyLoading={isDictionaryInitializing}
                                isWordCheckerReady={isWordCheckerReady}
                                hasDictionarySelected={hasDictionarySelected}
                                isActive={currentTranscriptView === 'vocabulary'}
                                onTimestampClick={(timestamp, endTimestamp) => {
                                  handleTranscriptSeek(timestamp);
                                }}
                                dictionaryLabel={dictionaryDisplayName}
                              />
                            </div>
                          ) : undefined
                        }
                      onViewChange={(view) => {
                        setCurrentTranscriptView(view);
                      }}
                      episodeId={episodeId}
                      onTranscriptUpdate={onTranscriptUpdate}
                      // 🎯 Chapters
                      showChapters={showChapters}
                      onToggleChapters={onToggleChapters}
                    />
                  </div>
                </div>
                <div className="relative h-0">
                  <div
                    className="z-20 pointer-events-auto absolute inset-x-0 bottom-full translate-y-[40px] flex justify-center"
                    aria-disabled={canInteractWithDynamicIsland ? undefined : true}
                    title={dynamicIslandDisabledHint}
                  >
                    <DynamicIsland
                      view={islandView}
                      variantKey={islandVariantKey}
                      isMusicActive={isPlaying}
                      onIdleActivate={dynamicIslandIdleHandler}
                      onMusicDeactivate={closeMusicIsland}
                      musicContent={
                        React.isValidElement(vocabularyIslandContent)
                          ? React.cloneElement(vocabularyIslandContent as React.ReactElement<any>, {
                              tempExplanationOverrides
                            })
                          : vocabularyIslandContent
                      }
                    />
                  </div>
                </div>
                <div className="flex-shrink-0 mt-2">
                  {renderMobileControls()}
                </div>
              </div>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {selectedWord && (
        <Drawer
          open={showWordDrawer}
          onOpenChange={handleWordDrawerOpenChange}
          shouldScaleBackground={false}
        >
          <DrawerContent className="max-h-[90vh] overflow-y-auto rounded-t-[32px] border border-border">
            <DrawerHeader>
              <DrawerTitle className="flex flex-col items-start space-y-1">
                {lookupTags.length > 0 && (
                  <div className="flex flex-wrap gap-1 text-[0.7rem] font-semibold uppercase tracking-wide text-primary/80">
                    {lookupTags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-lg bg-primary/10 px-1.5 py-0.5"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                <span className="flex items-center gap-2">
                  <GraduationCap className="h-6 w-6" />
                  <span className="text-xl font-bold">{selectedWord}</span>
                </span>
              </DrawerTitle>
              {(explanation?.usphone ||
                translation?.usphone ||
                translation?.phonetic) && (
                <DrawerDescription className="text-base text-start pl-8 font-medium text-stone-700">
                  /
                  {explanation?.usphone ||
                    translation?.usphone ||
                    translation?.phonetic}
                  /
                </DrawerDescription>
              )}
            </DrawerHeader>

            <div className="px-4 pb-2">
              {isWordLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="ml-2 text-gray-600">
                    {t("common.loading", "Loading...")}
                  </span>
                </div>
              ) : primaryTranslationParts.length > 0 ||
                extraExplainParts.length > 0 ||
                explanation?.example ||
                hasLookupMetadata ? (
                <div className="space-y-4">
                  {primaryTranslationParts.length > 0 && (
                    <div className="flex items-start space-x-3">
                      <div className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-foreground space-y-1">
                        {primaryTranslationParts.map((line, index) => (
                          <div key={`primary-${index}`}>{line}</div>
                        ))}
                      </div>
                    </div>
                  )}

                  {hasLookupMetadata && (
                    <div className="rounded-2xl border border-border/70 bg-muted/40 p-3 text-xs text-foreground/70">
                      {lookupMetadata?.definition && (
                        <p className="whitespace-pre-line text-foreground/80">
                          {lookupMetadata.definition}
                        </p>
                      )}
                      {/* {lookupMetadata?.tag && (
                        <div className="mt-2 flex flex-wrap gap-2 text-xs">
                          <span className="rounded bg-secondary/20 px-2 py-0.5 text-secondary-foreground/80">
                            {lookupMetadata.tag}
                          </span>
                        </div>
                      )} */}
                      {(typeof lookupMetadata?.bnc === "number" ||
                        typeof lookupMetadata?.frq === "number") && (
                        <div className="mt-2 text-[0.625rem] text-muted-foreground space-x-3">
                          {typeof lookupMetadata?.bnc === "number" && (
                            <span>BNC词频排名: {lookupMetadata.bnc}</span>
                          )}
                          {typeof lookupMetadata?.frq === "number" && (
                            <span>COCA词频排名: {lookupMetadata.frq}</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {extraExplainParts.length > 0 && (
                    <div className="flex items-start space-x-3 pt-3 border-t border-gray-100">
                      <Quote className="h-5 w-5 text-gray-600 mt-1 flex-shrink-0" />
                      <div className="text-sm text-gray-600 space-y-1">
                        {extraExplainParts.map((line, index) => (
                          <div key={`extra-${index}`}>{line}</div>
                        ))}
                      </div>
                    </div>
                  )}

                  {exampleText && (
                    <div className="flex items-start space-x-3 pt-3 border-t border-gray-100">
                      <Quote className="h-5 w-5 text-gray-600 mt-1 flex-shrink-0" />
                      <span className="text-base text-gray-600 italic">
                        {exampleText}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  {t("vocabulary.noTranslation", "No translation available")}
                </div>
              )}
            </div>

            <DrawerFooter>
              <div className="flex w-full items-stretch gap-1.5">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(
                        "flex-1 min-w-0 justify-between rounded-xl border text-xs h-9 px-3",
                        statusStyles[selectedStatusOption.value]
                      )}
                    >
                      <span className="flex items-center gap-2">
                        <StatusIconMobile className="h-3.5 w-3.5" />
                        {selectedStatusOption.label}
                      </span>
                      <span
                        className={cn(
                          "h-2 w-2 rounded-full",
                          statusAccentMap[selectedStatusOption.value]
                        )}
                      />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48">
                    {statusOptions.map((option) => {
                      const OptionIcon =
                        option.value === "MASTERED"
                          ? Check
                          : option.value === "FUZZY"
                          ? Lightbulb
                          : Sparkles;
                      const isSelected =
                        selectedStatusOption.value === option.value;
                      return (
                        <DropdownMenuItem
                          key={option.value}
                          onSelect={(event) => {
                            event.preventDefault();
                            setSelectedStatusValue(option.value);
                          }}
                          className={cn(
                            "flex items-start gap-2",
                            isSelected && "bg-accent/50 text-foreground"
                          )}
                        >
                          <OptionIcon
                            className={cn(
                              "h-4 w-4 mt-0.5",
                              isSelected
                                ? "text-primary"
                                : "text-muted-foreground"
                            )}
                          />
                          <span className="flex flex-col text-sm">
                            <span className="font-medium flex items-center gap-1">
                              {option.label}
                              {isSelected && <Check className="h-3 w-3" />}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {option.description}
                            </span>
                          </span>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  size="sm"
                  variant="ghost"
                  className="flex-1 border border-border min-w-0 gap-1.5 font-normal sm:font-medium rounded-xl text-xs h-9 px-3"
                  disabled={
                    !userSettings?.myVocabularySetting ||
                    !selectedWord ||
                    isWordSaving ||
                    isWordSaved ||
                    (!explanation && !translation)
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void handleSaveWord();
                  }}
                >
                  {isWordSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isWordSaved ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <ClipboardCopy className="h-4 w-4" />
                  )}
                  <span>
                    {isWordSaved
                      ? t("common.saved", "Saved")
                      : t("common.save")}
                  </span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1 min-w-0 border rounded-xl text-xs h-9 px-3"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSpeakWord();
                  }}
                >
                  <SpeakerIcon
                    divRef={speakerRef}
                    size={9}
                    provider="EdgeTTS"
                    text={selectedWord || ""}
                    lang={
                      getSpeakerLang(
                        userSettings?.languageSetting || "english"
                      ) as any
                    }
                    voice={undefined}
                    rate={10}
                    volume={100}
                  />
                  <span className="ml-2">{t("common.speak")}</span>
                </Button>
              </div>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      )}

      <Skiper21Drawer
        open={isVocabularyNarrationOpen && Boolean(selectedVocabularyTerm)}
        onOpenChange={(open) => {
          setIsVocabularyNarrationOpen(open);
          if (!open) {
            setSelectedVocabularyTerm(null);
          }
        }}
        variant="bare"
      >
        <AnimatePresence>
          {selectedVocabularyTerm ? (
            <motion.div
              key={
                selectedVocabularyTerm.id ??
                `${selectedVocabularyTerm.term}-${selectedVocabularyTerm.timestamp ?? "na"}`
              }
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              transition={{ duration: 0.25, ease: [0.26, 0.08, 0.25, 1] }}
            >
              {(() => {
                const foundInActive = activeVocabularyIslandItems.find(
                  item => item.id === selectedVocabularyTerm.id ||
                          (item.term === selectedVocabularyTerm.term &&
                           item.timestamp === selectedVocabularyTerm.timestamp)
                );
                const foundInAll = vocabularyIslandItems.find(
                  item => item.id === selectedVocabularyTerm.id ||
                          (item.term === selectedVocabularyTerm.term &&
                           item.timestamp === selectedVocabularyTerm.timestamp)
                );
                const latestTerm = foundInActive || foundInAll || selectedVocabularyTerm;

                const resolvedAudioUrl = 
                  tempAudioOverrides[latestTerm.term] ||
                  normalizeChosenExplanationAudioUrl(
                    latestTerm.data?.chosen_explanation_audio,
                    userSettings?.languageSetting?.split('-')[0]?.toLowerCase()
                  ) || undefined;

                // Resolve explanation text prioritizing temp overrides
                const resolvedExplanation = 
                  tempExplanationOverrides[latestTerm.term]?.chosen ||
                  normalizeChosenExplanationText(
                    latestTerm.data?.chosen_explanation,
                    userSettings?.languageSetting?.split('-')[0]?.toLowerCase()
                  ) || undefined;

                // if (process.env.NODE_ENV !== 'production') {
                //   console.log('[GlobalPlayer] Rendering NarrationPlayer', {
                //     term: latestTerm.term,
                //     audioObj: latestTerm.data?.chosen_explanation_audio,
                //     overrideUrl: tempAudioOverrides[latestTerm.term],
                //     resolvedUrl: resolvedAudioUrl,
                //     explanationObj: latestTerm.data?.chosen_explanation,
                //     overrideExplanation: tempExplanationOverrides[latestTerm.term],
                //     resolvedExplanation,
                //     isGenerating: isGeneratingAudio,
                //     tempExplanationOverridesKeys: Object.keys(tempExplanationOverrides),
                //     lookup: {
                //         foundInActive: !!foundInActive,
                //         foundInAll: !!foundInAll,
                //         activeCount: activeVocabularyIslandItems.length,
                //         allCount: vocabularyIslandItems.length
                //     }
                //   });
                // }

                return (
                  <VocabularyNarrationPlayer
                    term={latestTerm.term}
                    scenarioName={latestTerm.scenarioName}
                    audioUrl={resolvedAudioUrl}
                    onClose={() => {
                      setIsVocabularyNarrationOpen(false);
                      setSelectedVocabularyTerm(null);
                      setPracticeTerm(null);
                    }}
                    onPracticeSpeaking={() => {
                      pauseForPopIsland();
                      setIsVocabularyNarrationOpen(false);
                      setPracticeTerm(latestTerm.term);
                      setIsPopIslandOpen(true);
                    }}
                    containerClassName="bg-transparent"
                    cardClassName="shadow-none"
                    disableEntranceAnimation
                    explanation={resolvedExplanation}
                    isGeneratingAudio={isGeneratingAudio}
                    onAudioMissing={async (explanation?: string) => {
                      if (onVocabularyAudioMissing && latestTerm) {
                        await onVocabularyAudioMissing(latestTerm, explanation);
                      }
                    }}
                    onExplanationMissing={async () => {
                      if (onChosenExplanationMissing && latestTerm) {
                        return await onChosenExplanationMissing(latestTerm);
                      }
                      return undefined;
                    }}
                  />
                );
              })()}
            </motion.div>
          ) : null}
        </AnimatePresence>
      </Skiper21Drawer>

      <Skiper21
        open={isPopIslandOpen}
        hideTriggerButton
        vocabularyData={vocabularyData}
        initialTerm={practiceTerm || undefined}
        initialPracticeType={practiceTerm ? 'speaking' : undefined}
        onOpenChange={(open) => {
          setIsPopIslandOpen(open);
          if (!open) {
            setSelectedVocabularyTerm(null);
            setPracticeTerm(null);
          }
        }}
      />
      {videoPlayerPortal}
    </>
  );
};

// 🔥 性能优化说明：
// FullPlayerDialog 组件由于以下原因不适合使用 React.memo：
// 1. 有大量 props（50+ 个），包括许多回调函数
// 2. 回调函数每次渲染都会创建新引用（useCallback 成本也很高）
// 3. 许多 props（如 currentTime, isPlaying）需要频繁更新
// 
// 已实施的优化：
// - 视频时间更新节流：从 120ms 增加到 250ms
// - 词汇岛时间更新节流：使用 500ms 节流的 throttledCurrentTime
// - player-context 时间更新节流：从 200ms 增加到 500ms
// - MediaSession 位置更新节流：1000ms
//
// 这些优化可以显著减少组件重渲染和 CPU 负载


export const GlobalPlayerControls: React.FC = () => {
  const router = useRouter();
  const pathname = usePathname();
  const { t } = useI18n();
  
  // ==========================================
  // 🚀 性能优化：使用细粒度 Context 订阅
  // ==========================================
  // 不再使用 usePlayer()，它会导致每 500ms 重渲染整个组件
  // 改用拆分的 hooks，只订阅真正需要的部分
  
  // ⚡ Actions - 永远不变，不会导致重渲染
  const {
    play,
    pause,
    skipBackward,
    skipForward,
    setPlaybackSpeed,
    formatTime,
    seekTo,
    scrubTo,
    isPlaybackLocked,
    playbackLockReason,
    setSubtitles,
    setActiveSubtitleIndex,
    updateActiveSubtitleByTime,
    openFullPlayer,
    closeFullPlayer,
    beginScrubbing,
    endScrubbing,
    updateTime,
    updateDuration,
    updatePlayingState,
    handleMediaEnded,
    setCurrentEpisode,
    showPlayer,
    acquirePlaybackLock,
  } = usePlayerActions();
  
  // 🔷 稳定状态 - 很少变化（只在 episode 切换时）
  const {
    currentEpisode,
    duration,
    playbackSpeed,
    cleanReadData,
    isVisible,
    isLoading,
    subtitles,
    isFullPlayerOpen,
  } = usePlayerStable();
  
  // 🔥 实时状态 - 频繁变化（每 500ms）
  // ⚠️ 这会导致组件重渲染，但现在只有这一个 hook 会触发
  // 之前是整个 usePlayer() 都会触发（包括 actions 和 stable）
  const {
    currentTime,
    isPlaying,
    activeSubtitleIndex,
  } = usePlayerRealtime();
  
  // 🔥 使用 ref 缓存值，供子组件按需读取（避免传递导致的重渲染）
  const currentTimeRef = useRef(currentTime);
  currentTimeRef.current = currentTime;

  // Keep a ref to the latest currentEpisode to avoid stale closures in callbacks
  const currentEpisodeRef = useRef(currentEpisode);
  currentEpisodeRef.current = currentEpisode;

  // useEffect(() => {
  //   if (process.env.NODE_ENV === "production") {
  //     return;
  //   }
  //   console.log("[GlobalPlayerControls] currentEpisode snapshot", currentEpisode);
  // }, [currentEpisode]);

  const [isGeneratingChosenExplanation, setIsGeneratingChosenExplanation] =
    useState(false);
  const [isGeneratingChosenExplanationAudio, setIsGeneratingChosenExplanationAudio] =
    useState(false);
  const [isGeneratingVocabulary, setIsGeneratingVocabulary] = useState(false);
  const [hasAttemptedChosenExplanationGeneration, setHasAttemptedChosenExplanationGeneration] =
    useState(false);
  const [hasAttemptedChosenExplanationAudioGeneration, setHasAttemptedChosenExplanationAudioGeneration] =
    useState(false);
  const [hasAttemptedVocabularyGeneration, setHasAttemptedVocabularyGeneration] =
    useState(false);
  const [isHydratingEpisode, setIsHydratingEpisode] = useState(false);
  const hydratingEpisodeIdRef = useRef<string | null>(null);
  const lastHydrationAttemptRef = useRef<Record<string, number>>({});
  const isGeneratingVocabularyRef = useRef(isGeneratingVocabulary);
  useEffect(() => {
    isGeneratingVocabularyRef.current = isGeneratingVocabulary;
  }, [isGeneratingVocabulary]);
  const hasAttemptedVocabularyGenerationRef = useRef(
    hasAttemptedVocabularyGeneration,
  );
  useEffect(() => {
    hasAttemptedVocabularyGenerationRef.current =
      hasAttemptedVocabularyGeneration;
  }, [hasAttemptedVocabularyGeneration]);
  const [streamingSubtitles, setStreamingSubtitles] = useState<SubtitleCue[]>([]);
  // 🔥 独立的 streaming vocabulary state，防止被 hydration 覆盖（参考 streamingSubtitles 的做法）
  const [streamingVocabulary, setStreamingVocabulary] = useState<any>(null);
  // 🔥 保持 streamingVocabulary 的最新引用，供 hydrateEpisode 回调使用
  const streamingVocabularyRef = useRef<any>(null);
  streamingVocabularyRef.current = streamingVocabulary;
  const [isStreamingTranscript, setIsStreamingTranscript] = useState(false);
  const streamAbortControllerRef = useRef<AbortController | null>(null);
  const streamingAttemptRef = useRef(false);
  const [streamingError, setStreamingError] = useState<string | null>(null);
  
  // 🔥 Toast 队列机制 - 确保每个 progress toast 至少显示一段时间
  const toastQueueRef = useRef<Array<{ message: string; isSuccess: boolean }>>([]);
  const lastToastTimeRef = useRef<number>(0);
  const toastProcessingRef = useRef<boolean>(false);
  const MIN_TOAST_DISPLAY_MS = 600; // 每个 toast 最少显示 600ms
  
  const processToastQueue = useCallback(() => {
    if (toastProcessingRef.current || toastQueueRef.current.length === 0) {
      return;
    }
    
    const now = Date.now();
    const timeSinceLastToast = now - lastToastTimeRef.current;
    
    if (timeSinceLastToast < MIN_TOAST_DISPLAY_MS) {
      // 还没到时间，延迟处理
      toastProcessingRef.current = true;
      setTimeout(() => {
        toastProcessingRef.current = false;
        processToastQueue();
      }, MIN_TOAST_DISPLAY_MS - timeSinceLastToast);
      return;
    }
    
    // 取出队列中的下一个 toast（跳过中间的，只显示最新的）
    const nextToast = toastQueueRef.current.pop();
    toastQueueRef.current = []; // 清空队列
    
    if (nextToast) {
      lastToastTimeRef.current = now;
      if (nextToast.isSuccess) {
        toast.success(nextToast.message, { id: 'transcribe-progress' });
      } else {
        toast.loading(nextToast.message, { id: 'transcribe-progress' });
      }
    }
  }, []);
  
  const enqueueToast = useCallback((message: string, isSuccess = false) => {
    // 如果是第一个 toast 或距离上次已经足够久，直接显示
    const now = Date.now();
    if (lastToastTimeRef.current === 0 || now - lastToastTimeRef.current >= MIN_TOAST_DISPLAY_MS) {
      lastToastTimeRef.current = now;
      if (isSuccess) {
        toast.success(message, { id: 'transcribe-progress' });
      } else {
        toast.loading(message, { id: 'transcribe-progress' });
      }
    } else {
      // 加入队列
      toastQueueRef.current.push({ message, isSuccess });
      processToastQueue();
    }
  }, [processToastQueue]);
  const transcriptCacheShareIdRef = useRef<string | null>(null);
  const [externalAudioUrl, setExternalAudioUrl] = useState<string | null>(null);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [tempAudioOverrides, setTempAudioOverrides] = useState<Record<string, string>>({});
  // New temporary override state for explanations
  const [tempExplanationOverrides, setTempExplanationOverrides] = useState<Record<string, { chosen: string; explanation: string }>>({});
  const { usageInfo, refreshUsage, loading: usageLoading } = useUsage();
  const [showTranscriptGenerationDialog, setShowTranscriptGenerationDialog] = useState(false);
  const [showYouTubeUpgradeDialog, setShowYouTubeUpgradeDialog] = useState(false);
  const [isTranscriptDialogLoading, setIsTranscriptDialogLoading] = useState(false);
  const [transcriptGenerationConfirmed, setTranscriptGenerationConfirmed] = useState(false);
  const [hasTranscriptGenerationRecord, setHasTranscriptGenerationRecord] = useState(false);
  const transcriptDialogTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingDeductRef = useRef(false);
  const isBasicPlanLiteAutoConfirmedRef = useRef(false);
  const dialogUseEffectCountRef = useRef(0);
  
  // Track the language setting to clear temp overrides when it changes
  const prevLanguageSettingRef = useRef<string | undefined>(undefined);

  const [audioFetchError, setAudioFetchError] = useState<string | null>(null);
  const audioFetchAttemptRef = useRef(false);

  const { theme, resolvedTheme } = useTheme();
  // 使用实际的主题状态
  const isDarkTheme = resolvedTheme === "dark";
  const isOnEpisodePage = pathname.startsWith("/episodes/");
  const isMobile = useIsMobile();

  // Dictionary state for PhraseSection
  const [isWordCheckerReady, setIsWordCheckerReady] = useState(false);
  const [hasDictionarySelected, setHasDictionarySelected] = useState(false);
  const [isDictionaryInitializing, setIsDictionaryInitializing] = useState(false);
  const { userSettings, isLoading: isSettingsLoading } = useUserSettings();
  const { user } = useUser();

  // Clear temp overrides when language setting changes
  useEffect(() => {
    const currentLang = userSettings?.languageSetting;
    if (prevLanguageSettingRef.current !== undefined && prevLanguageSettingRef.current !== currentLang) {
      // console.log('[GlobalPlayer] Language setting changed, clearing temp overrides', {
      //   from: prevLanguageSettingRef.current,
      //   to: currentLang
      // });
      setTempExplanationOverrides({});
      setTempAudioOverrides({});
      // Also reset the generation attempt flag so new explanations can be generated
      setHasAttemptedChosenExplanationGeneration(false);
    }
    prevLanguageSettingRef.current = currentLang;
  }, [userSettings?.languageSetting]);

  // 🔥 Handle page visibility change (e.g., user returns from lock screen)
  // When SSE connection is lost due to lock screen, check if transcription completed
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // User returned to the page
        // console.log('[GlobalPlayer] Page became visible, checking streaming state');
        
        // If we were streaming but connection might have been lost
        if (isStreamingTranscript) {
          const currentEpisodeId = currentEpisode?.shareId || currentEpisode?.content?.shareId || currentEpisode?.id;
          // console.log('[GlobalPlayer] Was streaming, dismissing stale progress toast', { currentEpisodeId });
          
          // Dismiss the potentially stale progress toast
          toast.dismiss('transcribe-progress');
          toast.loading('Checking transcription status...', { id: 'transcribe-check', duration: 3000 });
          
          // Reset streaming state - the hydration effect will re-check
          setIsStreamingTranscript(false);
          streamingAttemptRef.current = false;
          
          // Trigger re-hydration by resetting the timestamp in the ref
          if (currentEpisodeId && lastHydrationAttemptRef.current[currentEpisodeId]) {
            // Clear the last hydration timestamp to allow immediate re-hydration
            delete lastHydrationAttemptRef.current[currentEpisodeId];
          }
          
          // Small delay then force re-check by triggering state update
          setTimeout(() => {
            toast.dismiss('transcribe-check');
            // The hydration effect will re-run due to state changes
          }, 500);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isStreamingTranscript, currentEpisode]);

  // Shadowing state
  const [showShadowingDialog, setShowShadowingDialog] = useState(false);
  
  // Handle transcript update from TimeDrivenTranscript
  const handleTranscriptUpdate = useCallback((transcript: any[], audioUrl?: string) => {
    if (!currentEpisode) return;

    const updatedEpisode = {
      ...currentEpisode,
      generationRecords: {
        ...(currentEpisode.generationRecords || {}),
        transcript: true,
      },
      content: {
        ...currentEpisode.content,
        audioUrl: audioUrl || currentEpisode.content?.audioUrl,
        generationRecords: {
          ...((currentEpisode.content as any)?.generationRecords || {}),
          transcript: true,
        },
        content: {
          ...(currentEpisode.content?.content || {}),
          transcript: transcript,
        },
      },
    };

    setCurrentEpisode(updatedEpisode);
  }, [currentEpisode, setCurrentEpisode]);

  const [shadowingState, setShadowingState] = useState({
    currentSentence: null as { text: string; translation?: string; timestamp: [number, number] } | null,
    showTranslation: false,
    showSentence: false,
    playbackSpeed: 1.0,
    showTranscription: false,
    transcriptionAccuracy: null as number | null,
  });
  const shadowingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const recordingStartTimeRef = useRef<number | null>(null);
  const lastSavedTranscriptionRef = useRef<string | null>(null);

  // Audio recorder hook
  const {
    isRecording,
    startRecording,
    stopRecording,
    transcribedText,
    isTranscribing,
    transcriptionError,
    audioLevel,
  } = useAudioRecorder();

  // Shadowing stats hook
  const { saveRecord, saving } = useSaveShadowingRecord();

  // Initialize dictionary for PhraseSection
  useEffect(() => {
    let cancelled = false;

    const initializeDictionary = async () => {
      const dictionaryFile = userSettings?.dictionarySetting;
      
      // Check if dictionary is selected
      if (!dictionaryFile) {
        setHasDictionarySelected(false);
        setIsWordCheckerReady(false);
        return;
      }

      setHasDictionarySelected(true);

      // Infer dictionary type from file name if not set
      let dictionaryType = userSettings?.vocabularyDictionary;
      if (!dictionaryType && dictionaryFile) {
        const lowerFileName = dictionaryFile.toLowerCase();
        if (lowerFileName.includes('cet4') || lowerFileName.includes('cet-4')) {
          dictionaryType = 'CET4';
        } else if (lowerFileName.includes('cet6') || lowerFileName.includes('cet-6')) {
          dictionaryType = 'CET6';
        } else if (lowerFileName.includes('toefl') || lowerFileName.includes('托福')) {
          dictionaryType = 'TOEFL';
        } else if (lowerFileName.includes('ielts') || lowerFileName.includes('雅思')) {
          dictionaryType = 'IELTS';
        } else if (lowerFileName.includes('gre')) {
          dictionaryType = 'GRE';
        } else if (lowerFileName.includes('sat')) {
          dictionaryType = 'SAT';
        } else if (lowerFileName.includes('kaoyan') || lowerFileName.includes('考研')) {
          dictionaryType = 'KAOYAN';
        } else if (lowerFileName.includes('coca')) {
          dictionaryType = 'COCA';
        }
      }

      if (!dictionaryType) {
        console.warn('Cannot infer dictionary type from file:', dictionaryFile);
        setIsWordCheckerReady(false);
        return;
      }

      setIsDictionaryInitializing(true);

      try {
        const isManagerInitialized = vocabularyLevelManager.isInitialized();
        const currentDictionaryFile = vocabularyLevelManager.getCurrentDictionaryFile();

        if (!isManagerInitialized || currentDictionaryFile !== dictionaryFile) {
          await vocabularyLevelManager.ensureInitialized(dictionaryType, dictionaryFile);
        }

        if (!cancelled && vocabularyLevelManager.isInitialized()) {
          setIsWordCheckerReady(true);
        }
      } catch (error) {
        console.error('Failed to initialize dictionary for PhraseSection:', error);
        if (!cancelled) {
          setIsWordCheckerReady(false);
        }
      } finally {
        if (!cancelled) {
          setIsDictionaryInitializing(false);
        }
      }
    };

    initializeDictionary();

    return () => {
      cancelled = true;
    };
  }, [userSettings?.dictionarySetting, userSettings?.vocabularyDictionary]);

  const RESTORE_STORAGE_KEY = "neofolio-player-state";

  useEffect(() => {
    if (!isMobile && isFullPlayerOpen) {
      closeFullPlayer();
    }
  }, [isMobile, isFullPlayerOpen, closeFullPlayer]);

  const restoreEpisodeFromStorage = useCallback(() => {
    if (currentEpisode || typeof window === "undefined") {
      return;
    }

    try {
      const saved = window.localStorage.getItem(RESTORE_STORAGE_KEY);
      if (!saved) {
        return;
      }

      const parsed = JSON.parse(saved) as
        | { currentEpisode?: any; currentTime?: number }
        | null;
      const savedEpisode = parsed?.currentEpisode;
      if (!savedEpisode) {
        return;
      }

      const savedTime =
        typeof parsed?.currentTime === "number" && parsed.currentTime > 0
          ? parsed.currentTime
          : 0;

      setCurrentEpisode(savedEpisode, savedTime);
      showPlayer();
    } catch (error) {
      console.warn("Failed to restore episode for global player", error);
    }
  }, [currentEpisode, setCurrentEpisode, showPlayer]);

  // 修复hydration错误：确保服务端和客户端初始渲染一致
  const [isClientMounted, setIsClientMounted] = useState(false);

  useEffect(() => {
    setIsClientMounted(true);
  }, []);

  const mobileControlsContainerRef = useRef<HTMLDivElement | null>(null);
  const [mobileControlsHeight, setMobileControlsHeight] = useState(0);
  const autoGenerateVocabularyEnabled =
    process.env.NEXT_PUBLIC_AUTO_VOCAB_ENABLED === "true";

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (!isFullPlayerOpen) {
      setMobileControlsHeight(0);
      return;
    }

    const element = mobileControlsContainerRef.current;
    if (!element) {
      setMobileControlsHeight(0);
      return;
    }

    const updateHeight = () => {
      setMobileControlsHeight(element.getBoundingClientRect().height);
    };

    updateHeight();

    if ("ResizeObserver" in window && window.ResizeObserver) {
      const observer = new window.ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) {
          setMobileControlsHeight(entry.contentRect.height);
        }
      });

      observer.observe(element);
      return () => observer.disconnect();
    }

    const win = window as typeof globalThis.window;
    win.addEventListener("resize", updateHeight);
    return () => {
      win.removeEventListener("resize", updateHeight);
    };
  }, [isFullPlayerOpen]);

  // Keep drawer height tied to the visual viewport to avoid iOS Safari chrome overlap.
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const updateViewportHeight = () => {
      const height = window.visualViewport?.height ?? window.innerHeight;
      if (height) {
        document.documentElement.style.setProperty(
          "--app-viewport-height",
          `${height}px`
        );
      }
    };

    updateViewportHeight();

    window.addEventListener("resize", updateViewportHeight);
    window.addEventListener("orientationchange", updateViewportHeight);

    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener("resize", updateViewportHeight);
    visualViewport?.addEventListener("scroll", updateViewportHeight);

    return () => {
      window.removeEventListener("resize", updateViewportHeight);
      window.removeEventListener("orientationchange", updateViewportHeight);
      visualViewport?.removeEventListener("resize", updateViewportHeight);
      visualViewport?.removeEventListener("scroll", updateViewportHeight);
    };
  }, []);

  // 追踪shouldShow状态变化
  // const [prevShouldShow, setPrevShouldShow] = useState<boolean | null>(null);

  const handleScrubStart = useCallback(() => {
    beginScrubbing();
  }, [beginScrubbing]);

  const handleScrubChange = useCallback(
    (time: number) => {
      scrubTo(time);
    },
    [scrubTo]
  );

  const handleScrubEnd = useCallback(
    (time: number) => {
      scrubTo(time);
      endScrubbing();
    },
    [scrubTo, endScrubbing]
  );

  // 确定播放器是否应该显示
  // 服务端始终隐藏，客户端挂载后再根据实际状态显示
  const shouldShow = isClientMounted && currentEpisode && isVisible;

  useEffect(() => {
    if (shouldShow && !currentEpisode) {
      restoreEpisodeFromStorage();
    }
  }, [shouldShow, currentEpisode, restoreEpisodeFromStorage]);

  useEffect(() => {
    if (isFullPlayerOpen && !currentEpisode) {
      restoreEpisodeFromStorage();
    }
  }, [isFullPlayerOpen, currentEpisode, restoreEpisodeFromStorage]);

  const freePreviewLocked =
    isPlaybackLocked && playbackLockReason === FREE_PREVIEW_LOCK_REASON;
  const prevFreePreviewLockedRef = useRef(freePreviewLocked);
  const previewLimitToastIdRef = useRef<string | number | null>(null);

  const showFreePreviewToast = useCallback(() => {
    if (previewLimitToastIdRef.current !== null) {
      toast.dismiss(previewLimitToastIdRef.current);
    }
    previewLimitToastIdRef.current = toast.info(
      t("player.freePreview.toastTitle", "Free preview limit reached"),
      {
        description: t(
          "player.freePreview.toastDescription",
          "Upgrade to continue listening beyond the first 2 minutes."
        ),
        action: {
          label: t("player.freePreview.toastUpgradeCta", "Upgrade"),
          onClick: () => router.push("/pricing"),
        },
      }
    );
  }, [router, t]);

  useEffect(() => {
    const wasLocked = prevFreePreviewLockedRef.current;
    if (freePreviewLocked && !wasLocked) {
      showFreePreviewToast();
    } else if (!freePreviewLocked && wasLocked) {
      if (previewLimitToastIdRef.current !== null) {
        toast.dismiss(previewLimitToastIdRef.current);
        previewLimitToastIdRef.current = null;
      }
    }
    prevFreePreviewLockedRef.current = freePreviewLocked;
  }, [freePreviewLocked, showFreePreviewToast]);


  const episodeVocabularyData = useMemo(() => {
    // 从 currentEpisode 获取 vocabulary
    const fromEpisode = 
      currentEpisode?.content?.content?.vocabulary ??
      currentEpisode?.content?.vocabulary ??
      currentEpisode?.vocabulary ??
      currentEpisode?.content?.content?.content?.vocabulary ??
      null;
    
    // 🔥 检查 fromEpisode 是否有有效数据
    const hasValidEpisodeVocabulary = fromEpisode?.scenarios?.some?.(
      (s: any) => Array.isArray(s?.terms) && s.terms.length > 0
    );
    
    // 🔥 检查 streamingVocabulary 是否有有效数据
    const hasValidStreamingVocabulary = streamingVocabulary?.scenarios?.some?.(
      (s: any) => Array.isArray(s?.terms) && s.terms.length > 0
    );
    
    // 🔥 优先使用有数据的来源，防止 hydration 后 vocabulary 消失
    // 如果 currentEpisode 中没有有效的 vocabulary，回退到 streamingVocabulary
    if (hasValidEpisodeVocabulary) {
      return fromEpisode;
    }
    if (hasValidStreamingVocabulary) {
      // console.log('[GlobalPlayerControls] Using streamingVocabulary as fallback (currentEpisode vocabulary is empty)');
      return streamingVocabulary;
    }
    return fromEpisode; // Return whatever we have (even if empty)
  }, [currentEpisode, streamingVocabulary]);

  // 🎯 Extract chapters data from episode
  const episodeChaptersData = useMemo(() => {
    return (
      currentEpisode?.content?.content?.chapters ??
      currentEpisode?.content?.chapters ??
      currentEpisode?.chapters ??
      null
    );
  }, [currentEpisode]);

  const isEpisodeHydrationPlaceholder = useMemo(() => {
    return Boolean(
      currentEpisode?.isHydrationPlaceholder ||
        currentEpisode?.content?.isHydrationPlaceholder,
    );
  }, [currentEpisode]);

  const isVocabularyMissing = useMemo(() => {
    const scenarios = episodeVocabularyData?.scenarios;
    if (!Array.isArray(scenarios) || scenarios.length === 0) {
      return true;
    }
    return !scenarios.some(
      (scenario: any) => Array.isArray(scenario?.terms) && scenario.terms.length > 0
    );
  }, [episodeVocabularyData]);

  // 🎯 Check if chapters are missing (for auto-generation)
  const isChaptersMissing = useMemo(() => {
    return !episodeChaptersData || !Array.isArray(episodeChaptersData) || episodeChaptersData.length === 0;
  }, [episodeChaptersData]);

  const updateEpisodeVocabulary = useCallback(
    (updatedVocabulary: any) => {
      // 🔥 使用 ref 获取最新值，而不是闭包中的旧值（修复 chapters 丢失问题）
      const latestEpisode = currentEpisodeRef.current;
      
      if (!latestEpisode) {
        return;
      }

      // 🔥 显式保留 chapters 和其他重要字段
      const existingChapters = latestEpisode.chapters ?? 
        latestEpisode.content?.chapters;

      const nextEpisode: any = {
        ...latestEpisode,
        vocabulary: updatedVocabulary,
        chapters: existingChapters,
        generationRecords: latestEpisode.generationRecords,
      };

      if (latestEpisode.content) {
        nextEpisode.content = {
          ...latestEpisode.content,
          vocabulary: updatedVocabulary,
          chapters: existingChapters,
          generationRecords: (latestEpisode.content as any)?.generationRecords,
          content: {
            ...(latestEpisode.content.content ?? {}),
            vocabulary: updatedVocabulary,
            chapters: existingChapters,
          },
        };
      }

      setCurrentEpisode(nextEpisode);
    },
    [setCurrentEpisode]
  );

  // 🎯 Update chapters data in episode
  // 🔥 使用 ref 获取最新的 currentEpisode，避免闭包问题
  const updateEpisodeChapters = useCallback(
    (updatedChapters: any[]) => {
      const latestEpisode = currentEpisodeRef.current;
      
      if (!latestEpisode) {
        return;
      }

      // 🔥 显式保留 vocabulary，优先使用 streamingVocabularyRef（SSE 实时数据）
      const existingVocabulary = streamingVocabularyRef.current ??
        latestEpisode.vocabulary ?? 
        latestEpisode.content?.vocabulary ?? 
        latestEpisode.content?.content?.vocabulary;

      const nextEpisode: any = {
        ...latestEpisode,
        chapters: updatedChapters,
        vocabulary: existingVocabulary,
        generationRecords: latestEpisode.generationRecords,
      };

      if (latestEpisode.content) {
        nextEpisode.content = {
          ...latestEpisode.content,
          chapters: updatedChapters,
          vocabulary: existingVocabulary,
          generationRecords: (latestEpisode.content as any)?.generationRecords,
          content: {
            ...(latestEpisode.content.content ?? {}),
            chapters: updatedChapters,
            vocabulary: existingVocabulary,
          },
        };
      }

      setCurrentEpisode(nextEpisode);
    },
    [setCurrentEpisode]
  );

  // 🔥 监听来自 TranscriptSection 的 vocabulary 和 chapters 自定义事件
  // 当 TranscriptSection 处理 SSE 流时，它会发出这些事件让我们更新状态
  useEffect(() => {
    const handleVocabularyEvent = (event: CustomEvent) => {
      const payload = event.detail;
      if (payload?.vocabulary) {
        streamingVocabularyRef.current = payload.vocabulary;
        updateEpisodeVocabulary(payload.vocabulary);
        setStreamingVocabulary(payload.vocabulary);
      }
    };

    const handleChaptersEvent = (event: CustomEvent) => {
      const payload = event.detail;
      if (payload?.chapters && Array.isArray(payload.chapters)) {
        updateEpisodeChapters(payload.chapters);
      }
    };

    window.addEventListener('transcript-stream-vocabulary', handleVocabularyEvent as EventListener);
    window.addEventListener('transcript-stream-chapters', handleChaptersEvent as EventListener);

    return () => {
      window.removeEventListener('transcript-stream-vocabulary', handleVocabularyEvent as EventListener);
      window.removeEventListener('transcript-stream-chapters', handleChaptersEvent as EventListener);
    };
  }, [updateEpisodeVocabulary, updateEpisodeChapters]);

  const episodeId =
    currentEpisode?.shareId ||
    currentEpisode?.content?.shareId ||
    currentEpisode?.id ||
    currentEpisode?.content?.id;

  // 🎯 State for chapters generation and display
  const [isGeneratingChapters, setIsGeneratingChapters] = useState(false);
  const [showChapters, setShowChapters] = useState(true);
  const hasAttemptedChaptersGeneration = useRef(false);

  // 🎯 Auto-fetch chapters when episode has transcript but no chapters
  useEffect(() => {
    if (!episodeId || !isFullPlayerOpen) return;
    if (!isChaptersMissing) return;
    if (isEpisodeHydrationPlaceholder) return;
    if (hasAttemptedChaptersGeneration.current) return;
    if (isGeneratingChapters) return;
    
    // 🔥 Check if transcript exists (required for chapter generation)
    // 只有当字幕已经**完整存在**时才生成 chapters：
    // 1. subtitles.length > 0 - 有字幕数据（来自 usePlayer context）
    // 2. 且 isStreamingTranscript === false - 不在 streaming 过程中
    // 这确保只有在字幕完全生成后才调用 chapters API
    const hasTranscript = subtitles && subtitles.length > 0;
    if (!hasTranscript) return;
    
    // 🔥 如果正在 streaming，等待完成后再生成 chapters
    if (isStreamingTranscript) {
      // console.log('[GlobalPlayer] Skipping chapters generation - transcript streaming in progress');
      return;
    }

    hasAttemptedChaptersGeneration.current = true;
    setIsGeneratingChapters(true);

    // console.log('[GlobalPlayer] Auto-fetching chapters for episode', episodeId);

    fetch(`/api/episodes/${episodeId}/chapters`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })
      .then(async (response) => {
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.chapters) {
            updateEpisodeChapters(data.chapters);
            // console.log('[GlobalPlayer] Chapters fetched successfully', { count: data.chapters.length });
          }
        } else {
          console.warn('[GlobalPlayer] Failed to fetch chapters', { status: response.status });
        }
      })
      .catch((error) => {
        console.error('[GlobalPlayer] Error fetching chapters', error);
      })
      .finally(() => {
        setIsGeneratingChapters(false);
      });
  }, [
    episodeId,
    isFullPlayerOpen,
    isChaptersMissing,
    isEpisodeHydrationPlaceholder,
    isGeneratingChapters,
    subtitles,
    isStreamingTranscript,
    updateEpisodeChapters,
  ]);

  // Reset chapters generation flag when episode changes
  useEffect(() => {
    hasAttemptedChaptersGeneration.current = false;
  }, [episodeId]);

  const handleVocabularyAudioMissing = useCallback(async (termItem: any, explicitExplanation?: string) => {
    if (!episodeId || !userSettings?.languageSetting) return;
    setIsGeneratingAudio(true);
    try {
      const headers: HeadersInit = { "Content-Type": "application/json" };
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const targetLanguageCode = userSettings.languageSetting.split('-')[0].toLowerCase();
      const term = termItem.term;
      
      // 优先使用显式传入的 explanation（来自 NarrationModeIndicatorCard 的 generatedExplanation）
      // 其次检查 tempExplanationOverrides（流式API实时更新的数据）
      // 最后检查 termItem.data?.chosen_explanation（已保存的数据）
      const explanation = 
        explicitExplanation ||
        tempExplanationOverrides[term]?.chosen ||
        normalizeChosenExplanationText(termItem.data?.chosen_explanation, targetLanguageCode);

      if (!explanation) {
        console.warn('[GlobalPlayer] Cannot generate audio: explanation missing for', term, {
          explicitExplanation,
          tempOverride: tempExplanationOverrides[term],
          dataChosen: termItem.data?.chosen_explanation
        });
        return;
      }

      const response = await fetch(
        `/api/episodes/${episodeId}/vocabulary/chosen-explanation-audio`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            items: [{
              term,
              chosen_explanation: explanation
            }],
            targetLanguageCode
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Audio generation failed: ${response.status}`);
      }

      const data = await response.json();
      // console.log('[GlobalPlayer] Audio generation success, updating episode', data);
      
      if (data?.vocabulary) {
        // 1. Update temporary overrides for immediate playback
        if (data.audios && Array.isArray(data.audios)) {
            const newOverrides = { ...tempAudioOverrides };
            data.audios.forEach((audio: any) => {
                // Try to find the term text from the updated vocabulary using indices
                const scenario = data.vocabulary.scenarios[audio.scenarioIndex];
                const termData = scenario?.terms[audio.termIndex];
                if (termData?.term && audio.url) {
                    newOverrides[termData.term] = audio.url;
                } else if (termItem.term && audio.url) {
                    // Fallback: assume the audio corresponds to the requested term
                    // (Since we usually request one at a time)
                    newOverrides[termItem.term] = audio.url;
                }
            });
            setTempAudioOverrides(newOverrides);
        }

        // 2. Use currentEpisodeRef to ensure we update the latest state
        if (setCurrentEpisode && currentEpisodeRef.current) {
            // console.log('[GlobalPlayer] Updating currentEpisode with new vocabulary');
            const updatedEpisode = {
                ...currentEpisodeRef.current,
                // 🔥 保留 generationRecords
                generationRecords: currentEpisodeRef.current.generationRecords,
                content: {
                    ...currentEpisodeRef.current.content,
                    vocabulary: data.vocabulary,
                    // 🔥 保留 content 中的 generationRecords
                    generationRecords: (currentEpisodeRef.current.content as any)?.generationRecords
                }
            };
            setCurrentEpisode(updatedEpisode);
        } else {
            console.warn('[GlobalPlayer] Cannot update episode: setCurrentEpisode or currentEpisodeRef missing');
        }
      }
    } catch (e) {
      console.error('[GlobalPlayer] Failed to generate on-demand audio:', e);
      toast.error("生成解释音频失败，请稍后重试");
    } finally {
      setIsGeneratingAudio(false);
    }
  }, [episodeId, userSettings?.languageSetting, setCurrentEpisode, tempExplanationOverrides]);

  // Generate single chosen_explanation on-demand when opening NarrationModeIndicatorCard
  const handleChosenExplanationMissing = useCallback(async (termItem: any): Promise<string | undefined> => {
    if (!episodeId || !userSettings?.languageSetting) return undefined;
    
    try {
      const headers: HeadersInit = { "Content-Type": "application/json" };
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const targetLanguageCode = userSettings.languageSetting.split('-')[0].toLowerCase();
      const term = termItem.term;
      
      // Get example from term data
      const example = termItem.data?.examples?.[0]?.english;
      const translation = typeof termItem.data?.examples?.[0]?.translation === 'string' 
        ? termItem.data.examples[0].translation 
        : termItem.data?.examples?.[0]?.translation?.[targetLanguageCode];
      const explanation = typeof termItem.data?.explanation === 'string'
        ? termItem.data.explanation
        : termItem.data?.explanation?.['en'] || termItem.data?.explanation?.['english'];

      // Find scenarioIndex and termIndex
      let scenarioIndex = -1;
      let termIndex = -1;
      
      if (episodeVocabularyData?.scenarios) {
        for (let si = 0; si < episodeVocabularyData.scenarios.length; si++) {
          const scenario = episodeVocabularyData.scenarios[si];
          if (scenario?.terms) {
            for (let ti = 0; ti < scenario.terms.length; ti++) {
              if (scenario.terms[ti]?.term === term) {
                scenarioIndex = si;
                termIndex = ti;
                break;
              }
            }
          }
          if (scenarioIndex >= 0) break;
        }
      }

      if (scenarioIndex < 0 || termIndex < 0) {
        console.warn('[GlobalPlayer] Could not find term in vocabulary:', term);
        return undefined;
      }

      // console.log('[GlobalPlayer] Generating single chosen_explanation for:', term);

      const response = await fetch(
        `/api/episodes/${episodeId}/vocabulary/chosen-explanation-single`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            targetLanguage: userSettings.languageSetting,
            scenarioIndex,
            termIndex,
            term,
            example,
            translation,
            explanation
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.error || `API call failed with status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data?.chosen_explanation) {
        // console.log('[GlobalPlayer] Single chosen_explanation generated:', data.chosen_explanation.substring(0, 50));
        
        // Update temp overrides for immediate display
        setTempExplanationOverrides(prev => ({
          ...prev,
          [term]: { 
            chosen: data.chosen_explanation, 
            explanation: data.chosen_explanation 
          }
        }));

        // Update vocabulary in episode state
        if (currentEpisodeRef.current?.content?.vocabulary) {
          const currentVocab = currentEpisodeRef.current.content.vocabulary;
          const scenarios = [...(currentVocab.scenarios || [])].map(s => ({...s, terms: [...(s.terms || [])]}));
          
          if (scenarios[scenarioIndex]?.terms?.[termIndex]) {
            const termData = { ...scenarios[scenarioIndex].terms[termIndex] };
            
            let newChosen = termData.chosen_explanation;
            if (typeof newChosen === 'string') {
              newChosen = { 'zh': newChosen, [targetLanguageCode]: data.chosen_explanation };
            } else {
              newChosen = { ...newChosen, [targetLanguageCode]: data.chosen_explanation };
            }
            termData.chosen_explanation = newChosen;
            
            scenarios[scenarioIndex].terms[termIndex] = termData;
            const newVocab = { ...currentVocab, scenarios };
            updateEpisodeVocabulary(newVocab);
          }
        }

        return data.chosen_explanation;
      }

      return undefined;
    } catch (e) {
      console.error('[GlobalPlayer] Failed to generate single chosen_explanation:', e);
      return undefined;
    }
  }, [episodeId, userSettings?.languageSetting, episodeVocabularyData, updateEpisodeVocabulary]);

  // Check if chosen_explanation is missing (used for on-demand single generation)
  const isChosenExplanationMissing = useMemo(() => {
    const scenarios = episodeVocabularyData?.scenarios;
    if (!Array.isArray(scenarios)) {
      return false;
    }

    // Extract language code (e.g., "zh", "ko") from settings
    const userLangCode = userSettings?.languageSetting?.split('-')[0]?.toLowerCase() || 'zh';

    return scenarios.some((scenario: any) =>
      Array.isArray(scenario?.terms) &&
      scenario.terms.some((term: any) => {
        if (!term) return false;

        const val = term.chosen_explanation;
        
        // Legacy data (string): assume it is Chinese ('zh')
        if (typeof val === 'string') {
           const isUserChinese = userLangCode === 'zh' || userLangCode === 'chinese';
           // If user is not Chinese, we consider it missing so we can generate the user's language
           return !isUserChinese;
        }

        // New data (object): check specific key
        if (val && typeof val === 'object') {
            const specific = val[userLangCode];
            return !specific || typeof specific !== 'string' || specific.trim().length === 0;
        }
        
        // No explanation at all
        return true;
      })
    );
  }, [episodeVocabularyData, userSettings?.languageSetting]);

  // Check if explanation or example.translation is missing (for automatic batch generation)
  const isTranslationMissing = useMemo(() => {
    const scenarios = episodeVocabularyData?.scenarios;
    if (!Array.isArray(scenarios)) {
      return false;
    }

    const userLangCode = userSettings?.languageSetting?.split('-')[0]?.toLowerCase() || 'zh';

    return scenarios.some((scenario: any) =>
      Array.isArray(scenario?.terms) &&
      scenario.terms.some((term: any) => {
        if (!term) return false;

        // Check explanation translation
        const explanation = term.explanation;
        if (explanation) {
          if (typeof explanation === 'object' && !explanation[userLangCode]) {
            // Has English explanation but missing target language
            if (explanation['en'] || explanation['english']) {
              return true;
            }
          }
        }

        // Check example translations
        if (Array.isArray(term.examples)) {
          for (const example of term.examples) {
            if (!example?.english) continue;
            
            const translation = example.translation;
            if (!translation) {
              return true;
            }
            if (typeof translation === 'string') {
              // Legacy string - assume Chinese
              if (userLangCode !== 'zh') {
                return true;
              }
            } else if (typeof translation === 'object') {
              if (!translation[userLangCode]) {
                return true;
              }
            }
          }
        }

        return false;
      })
    );
  }, [episodeVocabularyData, userSettings?.languageSetting]);

  const isChosenExplanationAudioMissing = useMemo(() => {
    const scenarios = episodeVocabularyData?.scenarios;
    if (!Array.isArray(scenarios)) {
      return false;
    }

    return scenarios.some((scenario: any) =>
      Array.isArray(scenario?.terms) &&
      scenario.terms.some((term: any) => {
        if (!term) return false;
        const lang = userSettings?.languageSetting?.split('-')[0].toLowerCase() || 'zh';
        const audio = normalizeChosenExplanationAudioUrl(term.chosen_explanation_audio, lang);
        return !audio;
      })
    );
  }, [episodeVocabularyData]);

  const vocabularyGenerationTranscript = useMemo(() => {
    const episodeTranscript =
      currentEpisode?.content?.content?.transcript ??
      currentEpisode?.content?.transcript ??
      currentEpisode?.transcript;
    if (episodeTranscript && transcriptHasContent(episodeTranscript)) {
      return episodeTranscript;
    }

    if (cleanReadData?.transcript && transcriptHasContent(cleanReadData.transcript)) {
      return cleanReadData.transcript;
    }

    if (Array.isArray(subtitles) && subtitles.length > 0) {
      const subtitleTranscript = subtitles.map((cue) => ({
        text: cue.text,
        timestamp: [
          Number.isFinite(cue.start) ? cue.start : 0,
          Number.isFinite(cue.end) ? cue.end : (Number.isFinite(cue.start) ? (cue.start as number) + 0.6 : 0.6),
        ] as [number, number],
        sentences: [],
        chunks: [],
        translation: cue.translation ?? "",
      }));

      if (transcriptHasContent(subtitleTranscript)) {
        return subtitleTranscript;
      }
    }

    return null;
  }, [cleanReadData?.transcript, currentEpisode, subtitles]);

  const vocabularyGenerationPayload = useMemo(() => {
    if (!vocabularyGenerationTranscript) {
      return null;
    }

    const content = currentEpisode?.content ?? currentEpisode ?? {};
    const durationSeconds =
      typeof content?.content?.duration === "number"
        ? content.content.duration
        : typeof content?.duration === "number"
        ? content.duration
        : null;
    const tags = Array.isArray(content?.content?.tags)
      ? content.content.tags
      : Array.isArray(content?.tags)
      ? content.tags
      : undefined;

    return {
      text: vocabularyGenerationTranscript,
      language:
        content?.language ||
        content?.content?.language ||
        userSettings?.languageSetting ||
        "chinese",
      title: content?.content?.title || content?.title || null,
      channel: content?.content?.author || content?.author || null,
      description: content?.content?.description || content?.description || null,
      category: content?.content?.category || content?.category || null,
      tags,
      publishDate: content?.content?.publishDate || content?.publishDate || null,
      durationSeconds,
    };
  }, [currentEpisode, userSettings?.languageSetting, vocabularyGenerationTranscript]);

  const origin =
    currentEpisode?.content?.origin || currentEpisode?.origin || "";

  const { videoUrl, audioUrl } = useMemo(() => {
    return {
      videoUrl: pickFirstString(
        currentEpisode?.content?.content?.videoUrl,
        currentEpisode?.content?.videoUrl,
        currentEpisode?.videoUrl
      ),
      audioUrl: pickFirstString(
        currentEpisode?.content?.content?.audioUrl,
        currentEpisode?.content?.audioUrl,
        currentEpisode?.audioUrl
      ),
    };
  }, [currentEpisode]);

  const transcriptionAudioUrl = useMemo(() => {
    if (audioUrl && audioUrl.trim().length > 0) {
      return audioUrl;
    }
    return externalAudioUrl || "";
  }, [audioUrl, externalAudioUrl]);

  const resolvedMediaUrl = useMemo(() => {
    if (videoUrl && videoUrl.trim().length > 0) {
      return videoUrl;
    }
    return transcriptionAudioUrl || "";
  }, [videoUrl, transcriptionAudioUrl]);

  // useEffect(() => {
  //   if (process.env.NODE_ENV !== "production") {
  //     console.log("[GlobalPlayer] media resolution", {
  //       videoUrl,
  //       audioUrl,
  //       transcriptionAudioUrl,
  //       resolvedMediaUrl,
  //     });
  //   }
  // }, [videoUrl, audioUrl, transcriptionAudioUrl, resolvedMediaUrl]);

  const episodeLanguage = useMemo(() => {
    return (
      currentEpisode?.content?.content?.language ||
      currentEpisode?.content?.language ||
      currentEpisode?.language ||
      undefined
    );
  }, [currentEpisode]);
  const vocabularyTargetLanguage = useMemo(() => {
    return userSettings?.languageSetting || "chinese";
  }, [userSettings?.languageSetting]);
  const episodeVocabularyMetadata = useMemo(() => {
    const primaryContent =
      currentEpisode?.content?.content ??
      currentEpisode?.content ??
      currentEpisode ??
      null;
    const normalizeDurationSeconds = (value: unknown): number | null => {
      if (typeof value === "number" && Number.isFinite(value) && value > 0) {
        return value;
      }
      if (typeof value === "string") {
        const parsed = Number(value);
        if (Number.isFinite(parsed) && parsed > 0) {
          return parsed;
        }
      }
      return null;
    };
    const resolveString = (value: unknown): string => {
      return typeof value === "string" ? value : "";
    };
    const description =
      resolveString(primaryContent?.description) ||
      resolveString(currentEpisode?.description);
    const category =
      resolveString(primaryContent?.category) ||
      resolveString(currentEpisode?.category);
    const publishDate =
      resolveString(primaryContent?.publishDate) ||
      resolveString(currentEpisode?.publishDate);
    const tagsSource: unknown[] =
      (Array.isArray(primaryContent?.tags)
        ? primaryContent?.tags
        : Array.isArray(currentEpisode?.tags)
        ? currentEpisode?.tags
        : []) ?? [];
    const tags = tagsSource
      .filter(
        (tag): tag is string =>
          typeof tag === "string" && tag.trim().length > 0
      )
      .map((tag: string) => tag.trim());
    // 优先使用播放器的实时 duration（特别是对于 blob 视频）
    const durationSeconds =
      (typeof duration === "number" && Number.isFinite(duration) && duration > 0
        ? duration
        : null) ??
      normalizeDurationSeconds(primaryContent?.duration) ??
      normalizeDurationSeconds((currentEpisode as any)?.duration) ??
      normalizeDurationSeconds((currentEpisode as any)?.content?.content?.durationSeconds);
    return {
      description,
      category,
      publishDate,
      tags,
      durationSeconds,
    };
  }, [currentEpisode, duration]);
  const {
    description: episodeDescriptionText,
    category: episodeCategoryText,
    publishDate: episodePublishDateText,
    tags: episodeTagsList,
    durationSeconds: episodeDurationSeconds,
  } = episodeVocabularyMetadata;
  const hydrationPending = useMemo(() => {
    return Boolean(
      currentEpisode?.hydrationPending || currentEpisode?.content?.hydrationPending,
    );
  }, [currentEpisode?.hydrationPending, currentEpisode?.content?.hydrationPending]);
  const explicitYoutubeVideoId = useMemo(() => {
    if (currentEpisode?.sourceType !== "youtube") {
      return null;
    }
    return pickFirstString(
      currentEpisode?.videoId,
      currentEpisode?.content?.videoId,
      currentEpisode?.content?.content?.videoId
    );
  }, [currentEpisode]);
  const youtubeVideoIdFromEpisode = useMemo(() => {
    return pickFirstString(
      currentEpisode?.content?.content?.videoId,
      currentEpisode?.content?.videoId,
      currentEpisode?.videoId
    );
  }, [currentEpisode]);
  const youtubeVideoIdFromUrl = useMemo(() => {
    return extractYoutubeVideoId(videoUrl);
  }, [videoUrl]);
  const youtubeVideoId = useMemo(() => {
    return (
      explicitYoutubeVideoId || youtubeVideoIdFromEpisode || youtubeVideoIdFromUrl
    );
  }, [explicitYoutubeVideoId, youtubeVideoIdFromEpisode, youtubeVideoIdFromUrl]);
  const derivedSubtitlesData = useMemo(() => {
    return extractSubtitlesFromEpisode(currentEpisode, cleanReadData);
  }, [currentEpisode, cleanReadData]);

  const baseEpisodeTranscriptLength = useMemo(() => {
    const transcriptSource =
      currentEpisode?.content?.content?.transcript ??
      currentEpisode?.content?.transcript ??
      currentEpisode?.transcript;
    return Array.isArray(transcriptSource) ? transcriptSource.length : 0;
  }, [currentEpisode]);

  const transcriptGenerationKey = useMemo(() => {
    return (
      explicitYoutubeVideoId ||
      youtubeVideoIdFromEpisode ||
      youtubeVideoIdFromUrl ||
      currentEpisode?.content?.videoId ||
      currentEpisode?.videoId ||
      currentEpisode?.shareId ||
      currentEpisode?.content?.shareId ||
      null
    );
  }, [
    explicitYoutubeVideoId,
    youtubeVideoIdFromEpisode,
    youtubeVideoIdFromUrl,
    currentEpisode?.content?.videoId,
    currentEpisode?.videoId,
    currentEpisode?.shareId,
    currentEpisode?.content?.shareId,
  ]);

  useEffect(() => {
    if (!transcriptGenerationKey) {
      setHasTranscriptGenerationRecord(false);
      return;
    }
    const serverRecord =
      Boolean((currentEpisode as any)?.generationRecords?.transcript) ||
      Boolean((currentEpisode?.content as any)?.generationRecords?.transcript);
    
    // console.log('🔍 [hasTranscriptGenerationRecord] Setting value:', {
    //   serverRecord,
    //   currentEpisodeGenerationRecords: (currentEpisode as any)?.generationRecords,
    //   contentGenerationRecords: (currentEpisode?.content as any)?.generationRecords,
    // });
    
    setHasTranscriptGenerationRecord(serverRecord);
    
    // 🔥 如果检测到用户有生成记录，立即关闭可能已经打开的 dialog
    // 这比依赖另一个 useEffect 更可靠，因为它在同一个执行上下文中处理
    if (serverRecord) {
      // console.log('🔄 [hasTranscriptGenerationRecord] User has generation record, closing dialog if open');
      setShowTranscriptGenerationDialog(false);
    }
  }, [
    transcriptGenerationKey,
    currentEpisode,
    derivedSubtitlesData.length,
    baseEpisodeTranscriptLength,
    episodeId,
    isHydratingEpisode,
  ]);

  // 🔥 当 hasTranscriptGenerationRecord 变成 true 时，自动关闭已经打开的 dialog
  // 这处理了 dialog 在 generationRecords 加载前就显示的情况（备用机制）
  useEffect(() => {
    if (hasTranscriptGenerationRecord && showTranscriptGenerationDialog) {
      // console.log('🔄 [AutoClose] hasTranscriptGenerationRecord became true, closing dialog');
      setShowTranscriptGenerationDialog(false);
    }
  }, [hasTranscriptGenerationRecord, showTranscriptGenerationDialog]);

  const minutesToDeduct = useMemo(() => {
    if (
      typeof episodeDurationSeconds === "number" &&
      Number.isFinite(episodeDurationSeconds) &&
      episodeDurationSeconds > 0
    ) {
      return Math.max(1, Math.ceil(episodeDurationSeconds / 60));
    }
    return 1;
  }, [episodeDurationSeconds]);
  const transcriptMinutesRemaining =
    usageInfo?.transcriptMinutes?.remaining ?? null;
  const insufficientTranscriptMinutes =
    typeof transcriptMinutesRemaining === "number" &&
    transcriptMinutesRemaining >= 0 &&
    transcriptMinutesRemaining < minutesToDeduct;

  // 🔥 检查是否是 Free Plan（使用次数配额而不是时间配额）
  const isFreePlan = usageInfo?.planType === 'free';
  
  // 🔥 统一的配额不足检查（适用于 Free Plan 的次数配额和其他计划的时间配额）
  const insufficientQuota = isFreePlan 
    ? (usageInfo?.remainingTranscripts ?? 0) <= 0  // Free Plan: 检查次数
    : insufficientTranscriptMinutes;               // 其他: 检查时间

  const hasTranscriptData = useMemo(() => {
    return derivedSubtitlesData.length > 0 || baseEpisodeTranscriptLength > 0;
  }, [derivedSubtitlesData.length, baseEpisodeTranscriptLength]);

  const hydrationAttemptTimestamp = episodeId
    ? lastHydrationAttemptRef.current[episodeId]
    : undefined;

  // 🔥 对 streaming subtitles 应用 merge 策略，与后端保存到 Redis 时一致
  // const mergedStreamingSubtitles = useMemo((): SubtitleCue[] => {
  //   if (!streamingSubtitles || streamingSubtitles.length === 0) {
  //     return [];
  //   }
  //   // 将 SubtitleCue 格式转换为 TranscriptSegment 格式
  //   const segmentsForMerge = streamingSubtitles.map((cue) => ({
  //     text: cue.text || '',
  //     translation: cue.translation,
  //     timestamp: [cue.start, cue.end] as [number, number],
  //     chunks: [{
  //       text: cue.text || '',
  //       translation: cue.translation,
  //       timestamp: [cue.start, cue.end] as [number, number],
  //     }],
  //     sentences: [],
  //   }));
  //   // 应用 merge 策略（最多 30 个单词合并成一个段落）
  //   const merged = mergeTranscriptSegments(segmentsForMerge, { maxWordsPerSegment: 30 });
  //   // 转换回 SubtitleCue 格式
  //   return merged.map((segment, index) => ({
  //     id: `merged-${index}-${segment.timestamp[0]}`,
  //     text: segment.text,
  //     translation: segment.translation,
  //     start: segment.timestamp[0],
  //     end: segment.timestamp[1],
  //     chunks: segment.chunks,
  //   }));
  // }, [streamingSubtitles]);

  const effectiveSubtitles =
    derivedSubtitlesData.length > 0 ? derivedSubtitlesData : streamingSubtitles;

  useEffect(() => {
    setSubtitles(effectiveSubtitles);
  }, [effectiveSubtitles, setSubtitles]);

  const transcriptLoadingState = useMemo(() => {
    return (
      isLoading ||
      hydrationPending ||
      isHydratingEpisode ||
      // Show loading for YouTube videos waiting for transcript
      (derivedSubtitlesData.length === 0 &&
        streamingSubtitles.length === 0 &&
        (isStreamingTranscript || !transcriptionAudioUrl) &&
        Boolean(youtubeVideoId)) ||
      // Show loading for podcasts when transcript generation is confirmed but subtitles not yet available
      (derivedSubtitlesData.length === 0 &&
        streamingSubtitles.length === 0 &&
        transcriptGenerationConfirmed &&
        !youtubeVideoId)
    );
  }, [
    derivedSubtitlesData.length,
    isLoading,
    isStreamingTranscript,
    streamingSubtitles.length,
    transcriptionAudioUrl,
    youtubeVideoId,
    hydrationPending,
    isHydratingEpisode,
    transcriptGenerationConfirmed,
  ]);

  const clearHydrationPendingFlags = useCallback((episode: any | null | undefined) => {
    if (!episode || typeof episode !== "object") {
      return episode;
    }
    let updatedEpisode: any = episode;
    let changed = false;

    if (episode.hydrationPending) {
      updatedEpisode = {
        ...updatedEpisode,
        hydrationPending: false,
      };
      changed = true;
    }

    const episodeContent = updatedEpisode.content;
    if (episodeContent && typeof episodeContent === "object" && episodeContent.hydrationPending) {
      updatedEpisode = {
        ...updatedEpisode,
        content: {
          ...episodeContent,
          hydrationPending: false,
        },
      };
      changed = true;
    }

    return changed ? updatedEpisode : episode;
  }, []);

  useEffect(() => {
    // Automatic vocabulary generation is temporarily disabled.
  }, []);

  // Use a ref to track if SSE stream is in progress - this prevents cleanup from interrupting
  const translationStreamActiveRef = useRef(false);

  // Automatic batch generation of translations (explanation + example.translation)
  // NOTE: chosen_explanation is now generated on-demand when opening NarrationModeIndicatorCard
  useEffect(() => {
    // Only trigger if translation is missing AND it's not a hydration placeholder
    if (!isTranslationMissing || isEpisodeHydrationPlaceholder || isSettingsLoading || !isFullPlayerOpen) {
      return;
    }

    // Prevent infinite loops by checking if we are already generating or have attempted.
    const canGenerateTranslations =
      Boolean(episodeId) &&
      !isGeneratingChosenExplanation &&
      !hasAttemptedChosenExplanationGeneration;

    if (!canGenerateTranslations) {
      return;
    }

    // If a stream is already in progress, don't start another one
    if (translationStreamActiveRef.current) {
      // console.log('[GlobalPlayer] Translation SSE stream already in progress, skipping');
      return;
    }

    // Mark stream as active - this ref survives useEffect re-runs
    translationStreamActiveRef.current = true;

    const generateTranslations = async () => {
      try {
        // console.log("[GlobalPlayerControls] Missing translations detected, generating", {
        //   episodeId,
        // });
        // 🚀 使用统一的 toast id，避免与 VocabularyDisplay 的 toast 冲突
        toast.loading(t('floatingActions.buttons.generatingTranslation', 'Generating translation...'), { id: 'vocabulary-translation-progress' });
        setIsGeneratingChosenExplanation(true);
        setHasAttemptedChosenExplanationGeneration(true);

        const headers: HeadersInit = { 'Content-Type': 'application/json' };
        
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;

        if (token) {
          headers.Authorization = `Bearer ${token}`;
        } else {
          console.warn('[GlobalPlayerControls] Missing auth token for translations generation');
        }

        const response = await fetch(
          `/api/episodes/${episodeId}/vocabulary/translations`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({ 
              targetLanguage: userSettings?.languageSetting,
              vocabularyFallback: episodeVocabularyData
            })
          }
        );

        if (!response.ok || !response.body) {
          let errorMessage = "Failed to generate translations";
          try {
            const errorData = await response.json();
            errorMessage = errorData?.error || errorMessage;
          } catch {}
          throw new Error(errorMessage);
        }

        // Handle Streaming Response
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { value, done } = await reader.read();
          if (value) {
            const decodedChunk = decoder.decode(value, { stream: !done });
            buffer += decodedChunk;
            let boundaryIndex: number;
            while ((boundaryIndex = buffer.indexOf("\n\n")) !== -1) {
              const rawEvent = buffer.slice(0, boundaryIndex).trim();
              buffer = buffer.slice(boundaryIndex + 2);
              
              if (rawEvent.startsWith("event:") && translationStreamActiveRef.current) {
                const lines = rawEvent.split('\n');
                let eventType = '';
                let eventData = '';
                
                for (const line of lines) {
                    const trimmedLine = line.trim();
                    if (trimmedLine.startsWith('event:')) {
                        eventType = trimmedLine.substring(6).trim();
                    }
                    else if (trimmedLine.startsWith('data:')) {
                        eventData = trimmedLine.substring(5).trim();
                    }
                }

                try {
                    const payload = JSON.parse(eventData);
                    
                    if (eventType === 'example_translation' || eventType === 'explanation_translation') {
                        // Update vocabulary with new translation
                        if (currentEpisodeRef.current?.content?.vocabulary) {
                            const currentVocab = currentEpisodeRef.current.content.vocabulary;
                            const scenarios = [...(currentVocab.scenarios || [])].map(s => ({...s, terms: [...(s.terms || [])]}));
                            const targetLang = userSettings?.languageSetting?.split('-')[0]?.toLowerCase() || 'zh';
                            
                            if (scenarios[payload.scenarioIndex]) {
                                const terms = scenarios[payload.scenarioIndex].terms;
                                if (terms[payload.termIndex]) {
                                    const term = { ...terms[payload.termIndex] };
                                    
                                    if (eventType === 'explanation_translation') {
                                        let newExplanation = term.explanation;
                                        if (typeof newExplanation === 'string') {
                                            newExplanation = { 'zh': newExplanation, [targetLang]: payload.translation };
                                        } else {
                                            newExplanation = { ...newExplanation, [targetLang]: payload.translation };
                                        }
                                        term.explanation = newExplanation;
                                    } else if (eventType === 'example_translation' && term.examples) {
                                        const updatedExamples = [...term.examples];
                                        if (updatedExamples[payload.exampleIndex]) {
                                            const example = { ...updatedExamples[payload.exampleIndex] };
                                            let newTranslation = example.translation;
                                            if (typeof newTranslation === 'string') {
                                                newTranslation = { 'zh': newTranslation, [targetLang]: payload.translation };
                                            } else if (typeof newTranslation === 'object') {
                                                newTranslation = { ...newTranslation, [targetLang]: payload.translation };
                                            } else {
                                                newTranslation = { [targetLang]: payload.translation };
                                            }
                                            example.translation = newTranslation;
                                            updatedExamples[payload.exampleIndex] = example;
                                        }
                                        term.examples = updatedExamples;
                                    }
                                    
                                    terms[payload.termIndex] = term;
                                    const newVocab = { ...currentVocab, scenarios };
                                    updateEpisodeVocabulary(newVocab);
                                }
                            }
                        }
                    } else if (eventType === 'complete') {
                        if (payload.vocabulary) {
                            updateEpisodeVocabulary(payload.vocabulary);
                            // 🚀 使用统一的 toast id
                            toast.success(t('floatingActions.success.translateGenerated', 'Translation generated successfully!'), { id: 'vocabulary-translation-progress' });
                        }
                    } else if (eventType === 'error') {
                        console.error('Translation stream error:', payload.message);
                    }
                } catch (e) {
                    console.error('Failed to parse SSE data', e);
                }
              }
            }
          }
          if (done) {
            break;
          }
        }

      } catch (error) {
        if ((error as any)?.name === "AbortError") {
          return;
        }
        console.error("Translations generation failed", error);
        if (translationStreamActiveRef.current) {
            toast.error(
            error instanceof Error ? error.message : t('floatingActions.errors.translateFailed', 'Translation generation failed, please try again.')
            );
        }
      } finally {
        const wasActive = translationStreamActiveRef.current;
        translationStreamActiveRef.current = false;
        if (wasActive) {
          setIsGeneratingChosenExplanation(false);
        }
      }
    };

    generateTranslations();

    return () => {
      // Don't abort the stream - let it complete naturally
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    episodeId,
    hasAttemptedChosenExplanationGeneration,
    isTranslationMissing,
    isFullPlayerOpen,
    isGeneratingChosenExplanation,
    isEpisodeHydrationPlaceholder,
    isSettingsLoading,
    userSettings?.languageSetting
  ]);

  useEffect(() => {
    // DISABLED: Automatic audio generation is too expensive.
    // We will generate audio on-demand when the user opens the NarrationModeIndicatorCard.
    return;

    /*
    if (!isChosenExplanationAudioMissing) {
      return;
    }
    // ... (rest of the logic)
    */

    /*
    if (!canGenerateChosenExplanationAudio) {
      console.log("[GlobalPlayerControls] Skipping chosen_explanation_audio generation", {
        episodeId,
        isFullPlayerOpen,
        isGeneratingChosenExplanationAudio,
        hasAttemptedChosenExplanationAudioGeneration,
      });
      return;
    }

    let isActive = true;
    const controller = new AbortController();

    const generateChosenExplanationAudio = async () => {
      try {
        console.log("[GlobalPlayerControls] Missing chosen_explanation_audio detected, generating", {
          episodeId,
        });
        setIsGeneratingChosenExplanationAudio(true);
        setHasAttemptedChosenExplanationAudioGeneration(true);
        const headers: HeadersInit = { "Content-Type": "application/json" };

        // Use Supabase session for auth
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(
          `/api/episodes/${episodeId}/vocabulary/chosen-explanation-audio`,
          {
            method: "POST",
            headers,
            // signal: controller.signal, // Removed to prevent strict mode cancellation
            body: JSON.stringify({
                items: episodeVocabularyData?.scenarios?.flatMap((s: any) => s.terms || []).map((term: any) => ({
                    term: term.term,
                    chosen_explanation: normalizeChosenExplanationText(term.chosen_explanation, userSettings?.languageSetting?.split('-')[0]?.toLowerCase())
                })).filter((i: any) => !!i.chosen_explanation) || [],
                targetLanguageCode: userSettings?.languageSetting?.split('-')[0]?.toLowerCase()
            })
          }
        );

        let data: any = null;
        try {
          data = await response.json();
        } catch (error) {
          if (response.ok) {
            console.error("Failed to parse chosen explanation audio response", error);
          }
        }

        if (!response.ok) {
          throw new Error(data?.error || "Failed to generate explanation audio");
        }

        if (data?.vocabulary) {
          updateEpisodeVocabulary(data.vocabulary);
          toast.success("已生成解释音频");
        }
      } catch (error) {
        if ((error as any)?.name === "AbortError") {
          return;
        }
        console.error("Chosen explanation audio generation failed", error);
        toast.error(
          error instanceof Error ? error.message : "生成解释音频失败，请稍后重试"
        );
      } finally {
        if (isActive) {
          setIsGeneratingChosenExplanationAudio(false);
        }
      }
    };

    generateChosenExplanationAudio();

    return () => {
      isActive = false;
      controller.abort();
      setIsGeneratingChosenExplanationAudio(false);
    };
    */
  }, [
    episodeId,
    hasAttemptedChosenExplanationAudioGeneration,
    isChosenExplanationAudioMissing,
    isChosenExplanationMissing,
    isFullPlayerOpen,
    isGeneratingChosenExplanationAudio,
    updateEpisodeVocabulary,
  ]);

  const hasSubtitles = subtitles.length > 0;

  const [youtubeMetadata, setYoutubeMetadata] =
    useState<YouTubeMetadataSnapshot | null>(null);

  const rawEpisodeTitle =
    currentEpisode?.content?.title || currentEpisode?.title || "";
  const rawEpisodeAuthor =
    currentEpisode?.content?.author || currentEpisode?.author || "";
  const rawEpisodeThumbnail =
    currentEpisode?.content?.thumbnail ||
    currentEpisode?.content?.ownerAvatarUrl ||
    currentEpisode?.thumbnail ||
    currentEpisode?.ownerAvatarUrl ||
    "";
  const episodeTitle = youtubeMetadata?.title?.trim() || rawEpisodeTitle;
  const episodeAuthor = youtubeMetadata?.author?.trim() || rawEpisodeAuthor;
  const episodeThumbnail =
    youtubeMetadata?.thumbnail ||
    rawEpisodeThumbnail ||
    youtubeMetadata?.ownerAvatarUrl ||
    "";
  const episodeArtworkColor = useMemo(() => {
    const candidates: Array<unknown> = [
      currentEpisode?.content?.artwork_bg_color,
      currentEpisode?.content?.artworkBgColor,
      currentEpisode?.artwork_bg_color,
      currentEpisode?.artworkBgColor,
      currentEpisode?.content?.content?.artwork_bg_color,
      currentEpisode?.content?.content?.artworkBgColor,
      currentEpisode?.content?.accentColor,
      currentEpisode?.content?.content?.accentColor,
      currentEpisode?.accentColor,
      currentEpisode?.podcast?.accentColor,
      currentEpisode?.content?.podcast?.accentColor,
      currentEpisode?.content?.themeColor,
      currentEpisode?.themeColor,
      currentEpisode?.content?.content?.themeColor,
    ];

    for (const candidate of candidates) {
      const normalized = normalizeHexColor(candidate);
      if (normalized) {
        return normalized;
      }
    }

    const fallbackSource = [
      currentEpisode?.content?.title,
      currentEpisode?.title,
      currentEpisode?.content?.author,
      currentEpisode?.author,
      currentEpisode?.shareId,
      currentEpisode?.content?.shareId,
      currentEpisode?.id,
      currentEpisode?.content?.id,
    ].find(
      (value): value is string =>
        typeof value === "string" && value.trim().length > 0
    );

    if (fallbackSource) {
      const generated = normalizeHexColor(generateColorFromString(fallbackSource));
      if (generated) {
        return generated;
      }
    }

    return undefined;
  }, [currentEpisode]);
  useEffect(() => {
    setHasAttemptedChosenExplanationGeneration(false);
    setHasAttemptedChosenExplanationAudioGeneration(false);
    setIsGeneratingChosenExplanation(false);
    setIsGeneratingChosenExplanationAudio(false);
    setHasAttemptedVocabularyGeneration(false);
    setIsGeneratingVocabulary(false);
  }, [episodeId]);

  useEffect(() => {
    // 重置状态时，不要重置 Basic Plan Lite 的自动确认
    if (!isBasicPlanLiteAutoConfirmedRef.current) {
      setTranscriptGenerationConfirmed(false);
    }
    setShowTranscriptGenerationDialog(false);
    setIsTranscriptDialogLoading(false);
    isBasicPlanLiteAutoConfirmedRef.current = false; // 重置 ref 标记
  }, [episodeId]);

  useEffect(() => {
    if (!isFullPlayerOpen) {
      setHasAttemptedChosenExplanationGeneration(false);
      setHasAttemptedChosenExplanationAudioGeneration(false);
      setHasAttemptedVocabularyGeneration(false);
      setIsGeneratingChosenExplanation(false);
      setIsGeneratingChosenExplanationAudio(false);
      setIsGeneratingVocabulary(false);
    }
  }, [isFullPlayerOpen]);

  useEffect(() => {
    return () => {
      if (streamAbortControllerRef.current) {
        streamAbortControllerRef.current.abort();
        streamAbortControllerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    streamingAttemptRef.current = false;
    setStreamingSubtitles([]);
    setStreamingVocabulary(null); // 🔥 重置 streaming vocabulary
    setStreamingError(null);
    setExternalAudioUrl(null);
    setAudioFetchError(null);
    audioFetchAttemptRef.current = false;
    if (streamAbortControllerRef.current) {
      streamAbortControllerRef.current.abort();
      streamAbortControllerRef.current = null;
    }
    setIsStreamingTranscript(false);
  }, [episodeId]);

  const handleStreamEvent = useCallback(
    (rawEvent: string) => {
      if (!rawEvent) {
        return;
      }

      const lines = rawEvent.split("\n");
      let eventType = "message";
      const dataLines: string[] = [];

      for (const line of lines) {
        if (line.startsWith("event:")) {
          eventType = line.slice(6).trim();
        } else if (line.startsWith("data:")) {
          dataLines.push(line.slice(5));
        }
      }

      if (!dataLines.length) {
        return;
      }

      let payload: any;
      try {
        payload = JSON.parse(dataLines.join("\n"));
      } catch (error) {
        console.error("[TranscriptStream] Failed to parse SSE payload", error, { dataLines });
        return;
      }

      // 🔔 Handle progress events - show toast notifications
      if (eventType === "progress") {
        const { step, message, percent, currentChunk, totalChunks } = payload;
        // console.log("[TranscriptStream] 📢 Progress event received:", { step, message, percent });
        
        // 🔥 使用队列机制确保每个 toast 至少显示一段时间
        // Show toast for ALL progress steps
        if (step === 'audio_downloaded') {
          enqueueToast(message || 'Audio downloaded...');
        } else if (step === 'uploading') {
          enqueueToast(message || 'Uploading audio...');
        } else if (step === 'converting') {
          enqueueToast(message || 'Converting audio format...');
        } else if (step === 'transcribing') {
          enqueueToast(message || `Starting transcription...`);
        } else if (step === 'chunk_done' && currentChunk && totalChunks) {
          // Update toast with progress - show every chunk for visibility
          enqueueToast(`Transcribing... ${currentChunk}/${totalChunks} (${percent}%)`);
        } else if (step === 'vocabulary') {
          enqueueToast(message || 'Generating vocabulary...');
        } else if (step === 'saving') {
          enqueueToast(message || 'Saving transcript...');
        } else if (step === 'complete') {
          enqueueToast('Transcription complete! 🎉', true);
        } else {
          // Fallback for any other progress step
          enqueueToast(message || `Processing... ${percent || 0}%`);
        }
        return;
      }

      if (eventType === "vocabulary") {
        if (payload?.vocabulary) {
          if (process.env.NODE_ENV !== "production") {
            try {
              const scenarioTerms = Array.isArray(payload.vocabulary?.scenarios)
                ? payload.vocabulary.scenarios.flatMap((scenario: any) =>
                    Array.isArray(scenario?.terms) ? scenario.terms : []
                  )
                : [];
              // console.log("[TranscriptStream] vocabulary terms", {
              //   termCount: scenarioTerms.length,
              //   isFinal: payload.isFinal,
              //   chunkIndex: payload.chunkIndex,
              //   terms: scenarioTerms.map((term: any) => ({
              //     term: term?.term,
              //     timestamp: term?.timestamp,
              //     endTimestamp: term?.endTimestamp,
              //     exampleTimestamp: term?.examples?.[0]?.timestamp,
              //   })),
              // });
            } catch (logError) {
              console.warn("[TranscriptStream] failed to log vocabulary terms", logError);
            }
          }
          // 🔥 同时更新 currentEpisode 和独立的 streamingVocabulary state
          // streamingVocabulary 不会被 hydration 覆盖，确保 vocabulary 不会消失
          streamingVocabularyRef.current = payload.vocabulary;
          updateEpisodeVocabulary(payload.vocabulary);
          setStreamingVocabulary(payload.vocabulary);
          
          // ONLY trigger translations generation if this is the FINAL vocabulary chunk
          if (payload.isFinal) {
              setTimeout(() => {
                setHasAttemptedChosenExplanationGeneration(false);
              }, 2000);
          } else {
              setHasAttemptedChosenExplanationGeneration(true);
          }
        }
        return;
      }

      // 🎯 Handle chapters event from transcribe-stream
      if (eventType === "chapters") {
        if (payload?.chapters && Array.isArray(payload.chapters)) {
          updateEpisodeChapters(payload.chapters);
        }
        return;
      }

      if (eventType === "chunk") {
        const cues = buildSubtitlesFromStreamChunk(payload);
        if (!cues.length) {
          return;
        }
        setStreamingSubtitles((previous) => {
          const merged = [...previous];
          cues.forEach((cue) => {
            const existingIndex = merged.findIndex(
              (existing) =>
                Math.abs(existing.start - cue.start) < 0.02 &&
                existing.text === cue.text
            );
            if (existingIndex === -1) {
              merged.push(cue);
            } else {
              merged[existingIndex] = cue;
            }
          });
          merged.sort((a, b) => a.start - b.start);
          return merged;
        });
      } else if (eventType === "error") {
        setStreamingError(
          typeof payload?.message === "string"
            ? payload.message
            : "Transcription stream failed."
        );
      }
    },
    [setStreamingSubtitles, updateEpisodeVocabulary, updateEpisodeChapters, enqueueToast]
  );

  const startTranscriptStream = useCallback(async () => {
    // console.log("[TranscriptStream] 🎬 startTranscriptStream called!");
    
    // 🔥 对于 YouTube 视频，优先使用 video ID（让后端获取最新的音频 URL，避免过期问题）
    // 对于非 YouTube 视频，使用原始 audio URL
    // /api/transcribe-stream 现在支持直接传入 YouTube video ID
    let audioSource: string | null = null;
    
    if (youtubeVideoId) {
      // 🔥 YouTube 视频：直接传 video ID，让后端获取音频 URL（更可靠）
      audioSource = youtubeVideoId;
      // console.log('[TranscriptStream] Using YouTube video ID for streaming:', youtubeVideoId);
    } else if (transcriptionAudioUrl) {
      // 非 YouTube 视频：使用 audio URL
      audioSource = transcriptionAudioUrl;
      // console.log('[TranscriptStream] Using audio URL for streaming');
    }
    
    if (!audioSource) {
      // console.log('[TranscriptStream] No audio source available');
      return;
    }

    // 🔥 预检查：在开始 streaming 之前，先检查 Redis 是否已有数据
    // 这可以避免重复生成已存在的 transcript
    if (episodeId) {
      try {
        // console.log('[TranscriptStream] Pre-check: fetching episode data from Redis...');
        const preCheckResponse = await fetch(`/api/episodes/${episodeId}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });
        
        if (preCheckResponse.ok) {
          const preCheckData = await preCheckResponse.json();
          const existingTranscript = 
            preCheckData?.content?.content?.transcript ||
            preCheckData?.content?.transcript ||
            preCheckData?.transcript;
          
          if (Array.isArray(existingTranscript) && existingTranscript.length > 0) {
            // console.log('[TranscriptStream] Pre-check: transcript already exists in Redis, skipping streaming', {
            //   transcriptLength: existingTranscript.length,
            // });
            // 更新 currentEpisode 以包含已有的 transcript
            const shareData = preCheckData?.content ?? preCheckData ?? {};
            // 🔥 添加 _verifiedForUser 标记
            const baseGenerationRecords = preCheckData?.generationRecords ?? shareData?.generationRecords ?? {};
            const verifiedGenerationRecords = {
              ...baseGenerationRecords,
              _verifiedForUser: user?.id,
            };
            const normalizedShare = {
              ...shareData,
              hydrationPending: false,
              isHydrationPlaceholder: false,
              generationRecords: verifiedGenerationRecords,
              content: shareData?.content && typeof shareData.content === 'object'
                ? { ...shareData.content, hydrationPending: false, isHydrationPlaceholder: false, generationRecords: verifiedGenerationRecords }
                : shareData.content,
            };
            setCurrentEpisode?.(normalizedShare);
            return; // 不需要 streaming
          }
        }
        // console.log('[TranscriptStream] Pre-check: no existing transcript found, proceeding with streaming');
      } catch (preCheckError) {
        console.warn('[TranscriptStream] Pre-check failed, proceeding with streaming:', preCheckError);
        // 预检查失败，继续 streaming
      }
    }

    if (streamAbortControllerRef.current) {
      streamAbortControllerRef.current.abort();
      streamAbortControllerRef.current = null;
    }

    const controller = new AbortController();
    streamAbortControllerRef.current = controller;
    setIsStreamingTranscript(true);
    setStreamingSubtitles([]);
    setStreamingError(null);

    const formData = new FormData();
    // 🔥 传递 audio source（可以是 URL 或 YouTube video ID）
    formData.append("audio", audioSource);
    // console.log('[TranscriptStream] Streaming with audio source:', audioSource.slice(0, 50) + '...');
    if (episodeId) {
      formData.append("episodeId", episodeId);
    }
    if (episodeLanguage) {
      formData.append("episodeLanguage", episodeLanguage);
    }
    if (episodeTitle) {
      formData.append("episodeTitle", episodeTitle);
    }
    if (episodeAuthor) {
      formData.append("episodeAuthor", episodeAuthor);
    }
    if (vocabularyTargetLanguage) {
      formData.append("vocabularyLanguage", vocabularyTargetLanguage);
    }
    const trimmedDescription = episodeDescriptionText
      ? episodeDescriptionText.slice(0, 800)
      : "";
    if (trimmedDescription) {
      formData.append("episodeDescription", trimmedDescription);
    }
    if (episodeCategoryText) {
      formData.append("episodeCategory", episodeCategoryText);
    }
    if (episodePublishDateText) {
      formData.append("episodePublishDate", episodePublishDateText);
    }
    if (Array.isArray(episodeTagsList) && episodeTagsList.length > 0) {
      formData.append("episodeTags", JSON.stringify(episodeTagsList));
    }
    if (
      typeof episodeDurationSeconds === "number" &&
      Number.isFinite(episodeDurationSeconds)
    ) {
      formData.append("episodeDurationSeconds", String(episodeDurationSeconds));
    }
    if (resolvedMediaUrl) {
      formData.append("episodeVideoUrl", resolvedMediaUrl);
    }
    if (episodeLanguage) {
      formData.append("language", episodeLanguage);
    }

    try {
      let authHeaders: HeadersInit | undefined;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          authHeaders = { Authorization: `Bearer ${session.access_token}` };
        }
      } catch (error) {
        console.warn("[TranscriptStream] Failed to resolve auth session", error);
      }

      // console.log("[TranscriptStream] 📤 Sending request to /api/transcribe-stream...");
      
      const response = await fetch("/api/transcribe-stream", {
        method: "POST",
        headers: authHeaders,
        body: formData,
        signal: controller.signal,
      });

      // console.log("[TranscriptStream] 📥 Response received", { 
      //   status: response.status, 
      //   ok: response.ok,
      //   hasBody: !!response.body,
      // });

      if (!response.ok || !response.body) {
        throw new Error(`Transcription failed (${response.status})`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let chunkCount = 0;

      // console.log("[TranscriptStream] 🚀 Starting to read SSE stream");

      while (true) {
        const { value, done } = await reader.read();
        if (value) {
          chunkCount++;
          const decoded = decoder.decode(value, { stream: !done });
          buffer += decoded;
          
          // 🔍 Debug: Log first few chunks
          // if (chunkCount <= 3) {
          //   console.log("[TranscriptStream] 📦 Received chunk", { 
          //     chunkCount, 
          //     bytes: value.length,
          //     preview: decoded.slice(0, 150),
          //   });
          // }
          
          let boundaryIndex: number;
          while ((boundaryIndex = buffer.indexOf("\n\n")) !== -1) {
            const rawEvent = buffer.slice(0, boundaryIndex).trim();
            buffer = buffer.slice(boundaryIndex + 2);
            if (rawEvent) {
              handleStreamEvent(rawEvent);
            }
          }
        }
        if (done) {
          break;
        }
      }

      if (buffer.trim()) {
        handleStreamEvent(buffer.trim());
      }
    } catch (error) {
      if (controller.signal.aborted) {
        // 🔥 Clear progress toast when aborted
        toast.dismiss('transcribe-progress');
        return;
      }
      console.error("[TranscriptStream] Failed to stream transcription", error);
      
      // 🔥 Clear progress toast and show error
      toast.dismiss('transcribe-progress');
      toast.error('Transcription connection lost. Please try again.', { id: 'transcribe-error' });
      
      setStreamingError(
        error instanceof Error ? error.message : "Transcription stream failed."
      );
    } finally {
      if (streamAbortControllerRef.current === controller) {
        streamAbortControllerRef.current = null;
      }
      setIsStreamingTranscript(false);
    }
  }, [
    transcriptionAudioUrl,
    youtubeVideoId,
    episodeLanguage,
    handleStreamEvent,
    episodeId,
    episodeAuthor,
    episodeTitle,
    vocabularyTargetLanguage,
    episodeDescriptionText,
    episodeCategoryText,
    episodePublishDateText,
    episodeTagsList,
    episodeDurationSeconds,
    user?.id, // 🔥 添加 user?.id 用于 _verifiedForUser 标记
  ]);

  const baseShouldStreamTranscript = useMemo(() => {
    if (!episodeId) {
      // console.log('🔴 [baseShouldStreamTranscript] No episodeId');
      return false;
    }
    // Only stream if player is open
    if (!isFullPlayerOpen) {
      // console.log('🔴 [baseShouldStreamTranscript] Player not open');
      return false;
    }
    if (derivedSubtitlesData.length > 0 || baseEpisodeTranscriptLength > 0) {
      // console.log('🔴 [baseShouldStreamTranscript] Already has transcript', {
      //   derivedSubtitlesDataLength: derivedSubtitlesData.length,
      //   baseEpisodeTranscriptLength,
      // });
      return false;
    }
    if (!hydrationAttemptTimestamp) {
      // console.log('🔴 [baseShouldStreamTranscript] No hydration attempt timestamp');
      return false;
    }
    if (hydrationPending || isHydratingEpisode) {
      // console.log('🔴 [baseShouldStreamTranscript] Hydration pending', {
      //   hydrationPending,
      //   isHydratingEpisode,
      // });
      return false;
    }
    // 🔥 检查 subtitles（来自 usePlayer context）是否已有数据
    // 这可以捕获 hydration 成功后 context 更新的情况
    if (subtitles && subtitles.length > 0) {
      // console.log('🔴 [baseShouldStreamTranscript] Subtitles already exist in context', {
      //   subtitlesLength: subtitles.length,
      // });
      return false;
    }
    // 🔥 检查是否有 audio source（audio URL 或 YouTube video ID）
    const hasAudioSource = transcriptionAudioUrl || youtubeVideoId;
    if (!hasAudioSource) {
      // console.log('🔴 [baseShouldStreamTranscript] No audio source', {
      //   audioUrl,
      //   externalAudioUrl,
      //   transcriptionAudioUrl,
      //   youtubeVideoId,
      // });
      return false;
    }
    // console.log('✅ [baseShouldStreamTranscript] All checks passed', {
    //   transcriptionAudioUrl,
    //   youtubeVideoId,
    //   episodeId,
    // });
    return true;
  }, [
    transcriptionAudioUrl,
    youtubeVideoId,
    derivedSubtitlesData.length,
    baseEpisodeTranscriptLength,
    episodeId,
    hydrationPending,
    isHydratingEpisode,
    hydrationAttemptTimestamp,
    isFullPlayerOpen,
    audioUrl,
    externalAudioUrl,
    subtitles,
  ]);
  const shouldStreamTranscript = useMemo(() => {
    if (!baseShouldStreamTranscript) {
      // console.log('🔴 [shouldStreamTranscript] baseShouldStreamTranscript is false');
      return false;
    }
    if (!hasTranscriptGenerationRecord && !transcriptGenerationConfirmed) {
      // console.log('🔴 [shouldStreamTranscript] No generation record or confirmation', {
      //   hasTranscriptGenerationRecord,
      //   transcriptGenerationConfirmed,
      // });
      return false;
    }
    // console.log('✅ [shouldStreamTranscript] Will stream transcript');
    return true;
  }, [baseShouldStreamTranscript, hasTranscriptGenerationRecord, transcriptGenerationConfirmed]);

  // 🔥 用于跟踪已验证过的 episode 生成记录
  const verifiedGenerationRecordsRef = useRef<Map<string, boolean>>(new Map());

  useEffect(() => {
    dialogUseEffectCountRef.current++;
    const executionId = dialogUseEffectCountRef.current;
    
    // console.log(`🔍 [Dialog useEffect #${executionId}] Entry`, {
    //   transcriptGenerationKey,
    //   hasTranscriptGenerationRecord,
    //   transcriptGenerationConfirmed,
    //   isFullPlayerOpen,
    //   usageLoading,
    //   showTranscriptGenerationDialog,
    //   refAutoConfirmed: isBasicPlanLiteAutoConfirmedRef.current,
    // });
    
    if (!transcriptGenerationKey) {
      // console.log(`🔍 [Dialog useEffect #${executionId}] No key, returning`);
      return;
    }
    if (hasTranscriptGenerationRecord || transcriptGenerationConfirmed) {
      // console.log(`🔍 [Dialog useEffect #${executionId}] Early return - already has record or confirmed`);
      return;
    }
    if (!isFullPlayerOpen) {
      return;
    }
    // 在 hydrating 期间不弹出 dialog，等 hydration 完成后再检查
    if (hydrationPending || isHydratingEpisode) {
      return;
    }
    
    // 🔥 等待 usage 数据加载完成，避免显示不完整的信息
    if (usageLoading) {
      // console.log(`🔍 [Dialog useEffect #${executionId}] Waiting for usage to load`);
      return;
    }
    
    // 🔥 直接检查 generationRecords.transcript - 如果为 true，不显示 dialog
    // 改进检查：支持多个路径和 truthy 值
    const hasTranscriptInGenerationRecords = 
      (currentEpisode as any)?.generationRecords?.transcript === true ||
      Boolean((currentEpisode as any)?.generationRecords?.transcript) ||
      (currentEpisode?.content as any)?.generationRecords?.transcript === true ||
      Boolean((currentEpisode?.content as any)?.generationRecords?.transcript);
    
    // console.log(`🔍 [Dialog useEffect #${executionId}] Checking generationRecords:`, {
    //   episodeId,
    //   topLevel: (currentEpisode as any)?.generationRecords,
    //   contentLevel: (currentEpisode?.content as any)?.generationRecords,
    //   hasTranscriptInGenerationRecords,
    // });
    
    if (hasTranscriptInGenerationRecords) {
      // console.log(`✅ [Dialog useEffect #${executionId}] generationRecords.transcript is true, skipping dialog`);
      // 更新状态，避免后续重复检查
      setHasTranscriptGenerationRecord(true);
      return;
    }
    
    // 🔥 检查是否已经验证过这个 episode
    if (episodeId && verifiedGenerationRecordsRef.current.has(episodeId)) {
      const hasRecord = verifiedGenerationRecordsRef.current.get(episodeId);
      if (hasRecord) {
        // console.log(`🔍 [Dialog useEffect #${executionId}] Already verified - has generation record`);
        return;
      }
    }
    
    // 检测用户类型
    const planType = usageInfo?.planType;
    const isBasicPlanLite = planType === 'basic_lite' || usageInfo?.isBasicPlanLite === true;
    const isBasicPlan = planType === 'basic';
    const isFreePlan = planType === 'free';
    
    // 检测视频类型 - 尝试多个可能的路径
    const videoUrl = 
      currentEpisode?.content?.videoUrl || 
      currentEpisode?.videoUrl || 
      currentEpisode?.content?.content?.videoUrl ||
      currentEpisode?.audioUrl ||
      currentEpisode?.content?.audioUrl ||
      '';
    const isBlobVideo = typeof videoUrl === 'string' && videoUrl.includes('blob.vercel-storage.com');
    const isYouTubeVideo = !isBlobVideo && (
      currentEpisode?.origin === 'video' ||
      currentEpisode?.content?.origin === 'video' ||
      (typeof videoUrl === 'string' && (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')))
    );
    
    // 🔥 对于 Basic Plan Lite 用户，如果 videoUrl 还没有加载，先等待
    // 避免先弹出 dialog 再关闭的闪烁效果
    if (isBasicPlanLite && !videoUrl) {
      // console.log(`🔍 [Dialog useEffect #${executionId}] Basic Plan Lite - waiting for videoUrl to load`);
      return;
    }
    
    // 🔍 调试日志
    // console.log(`🔍 [Dialog Decision #${executionId}]`, {
    //   isBasicPlanLite,
    //   isBlobVideo,
    //   isYouTubeVideo,
    //   videoUrl,
    //   planType: usageInfo?.planType,
    //   origin: currentEpisode?.origin || currentEpisode?.content?.origin,
    //   hasTranscriptInGenerationRecords,
    // });
    
    // 🔥 检测 Premium Plan
    const isPremiumPlan = planType === 'premium' || usageInfo?.isPremium === true;
    
    // 🔥 对于 Blob 视频，只有 Basic Plan 和 Premium Plan 用户自动确认，不弹出 dialog
    // Basic Plan Lite 和 Free Plan 用户需要升级
    if (isBlobVideo && (isBasicPlan || isPremiumPlan)) {
      const planName = isPremiumPlan ? 'Premium' : 'Basic';
      // console.log(`✅ [${planName} Plan #${executionId}] Auto-confirming for blob video`, {
      //   refValue: isBasicPlanLiteAutoConfirmedRef.current,
      //   currentConfirmed: transcriptGenerationConfirmed,
      //   dialogCurrentlyShown: showTranscriptGenerationDialog,
      // });
      if (!isBasicPlanLiteAutoConfirmedRef.current) {
        isBasicPlanLiteAutoConfirmedRef.current = true; // 标记为自动确认
        setTranscriptGenerationConfirmed(true);
        // 🔥 关闭可能已经打开的 dialog
        if (showTranscriptGenerationDialog) {
          // console.log(`✅ [${planName} Plan #${executionId}] Closing existing dialog`);
          setShowTranscriptGenerationDialog(false);
        }
        // console.log(`✅ [${planName} Plan #${executionId}] Called setTranscriptGenerationConfirmed(true)`);
      // } else {
      //   console.log(`✅ [${planName} Plan #${executionId}] Already auto-confirmed, skipping setState`);
      }
      // console.log(`✅ [${planName} Plan #${executionId}] Returning from useEffect`);
      return;
    }
    
    // 🔥 YouTube 视频和 Podcast 现在使用相同的逻辑：检查 transcript 记录，没有则显示 dialog
    // 移除了对 YouTube 视频的特殊处理（升级提示和自动确认），使其与 podcast 保持一致
    
    // 🔥 对于 blob 视频，等待 duration 加载完成（避免显示错误的时长）
    if (isBlobVideo && (!duration || duration <= 0)) {
      return;
    }
    
    // 🔥 直接调用 API 检查生成记录，而不是依赖 currentEpisode
    // 这是最可靠的方式，避免了数据同步问题
    let isCancelled = false;
    
    const checkAndShowDialog = async () => {
      if (!episodeId) {
        // 没有 episodeId，根据用户类型显示不同的 dialog
        if (!isCancelled) {
          // console.log(`🔴 [Dialog useEffect #${executionId}] No episodeId, showing dialog`);
          // 🔥 Basic Plan Lite 用户没有 episodeId 时显示升级 dialog（无法确定 allowBasicPlanLite）
          // Free Plan 用户有 3 次生成额度，显示 "Generate Transcript" dialog
          if (isBasicPlanLite) {
            // console.log(`🔴 [Dialog useEffect #${executionId}] Basic Lite user without episodeId, showing upgrade dialog`);
            setShowYouTubeUpgradeDialog(true);
          } else {
            // 🔥 Free Plan 和其他用户显示 "Generate Transcript" dialog
            // console.log(`🔴 [Dialog useEffect #${executionId}] Showing generate dialog (Free/Basic/Premium)`);
            setShowTranscriptGenerationDialog(true);
          }
        }
        return;
      }
      
      // 🔥 如果用户未登录，跳转到登录页面，而不是显示 Generate Transcript dialog
      if (!user?.id) {
        // console.log(`🔴 [Dialog useEffect #${executionId}] User not logged in, redirecting to login`);
        const currentPath = typeof window !== 'undefined' 
          ? window.location.pathname + window.location.search 
          : '/';
        localStorage.setItem("auth_redirect_url", currentPath);
        router.push(`/login?redirectTo=${encodeURIComponent(currentPath)}`);
        return;
      }
      
      try {
        // 调用 API 检查生成记录
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        
        const response = await fetch(`/api/episodes/${episodeId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });
        
        // 🔥 声明 data 变量在 if 块外部，以便后续使用
        let data: any = null;
        
        if (response.ok) {
          data = await response.json();
          
          // 🔥 如果 API 返回 401 或用户未认证，generationRecords 可能都是 false
          // 检查是否有用户信息，如果没有用户信息，说明认证失败，应该等待认证完成
          const hasUser = data?.user !== null && data?.user !== undefined;
          
          // 🔥 改进检查：检查多个可能的数据路径，并支持 truthy 值
          const hasTranscript = 
            data?.generationRecords?.transcript === true ||
            Boolean(data?.generationRecords?.transcript) ||
            data?.content?.generationRecords?.transcript === true ||
            Boolean(data?.content?.generationRecords?.transcript);
          
          // console.log(`🔍 [Dialog API check #${executionId}] API response:`, {
          //   episodeId,
          //   hasUser,
          //   generationRecords: data?.generationRecords,
          //   contentGenerationRecords: data?.content?.generationRecords,
          //   hasTranscript,
          //   user: data?.user,
          //   allowBasicPlanLite: data?.allowBasicPlanLite,
          // });
          
          // 🔥 如果用户已登录但 API 返回未认证（hasUser 为 false），可能是 token 过期
          // 如果用户未登录，则直接显示 dialog（未登录用户不会有生成记录）
          if (!hasUser) {
            if (user?.id) {
              // 用户已登录但 token 可能过期，等待 token 刷新（Supabase 会自动刷新）
              // console.log(`⏳ [Dialog API check #${executionId}] User logged in but token invalid, waiting for token refresh...`);
              // 不显示 dialog，等待 token 刷新后重新检查
              return;
            } else {
              // 用户未登录，跳转到登录页面
              // console.log(`🔴 [Dialog API check #${executionId}] User not logged in, redirecting to login`);
              if (!isCancelled) {
                const currentPath = typeof window !== 'undefined' 
                  ? window.location.pathname + window.location.search 
                  : '/';
                localStorage.setItem("auth_redirect_url", currentPath);
                router.push(`/login?redirectTo=${encodeURIComponent(currentPath)}`);
              }
              return;
            }
          }
          
          // 缓存结果
          verifiedGenerationRecordsRef.current.set(episodeId, hasTranscript);
          
          if (hasTranscript) {
            // console.log(`✅ [Dialog API check #${executionId}] User has transcript record, not showing dialog`);
            // 更新 hasTranscriptGenerationRecord 状态
            setHasTranscriptGenerationRecord(true);
            return;
          }
        } else if (response.status === 401) {
          // 🔥 如果 API 返回 401
          if (user?.id) {
            // 用户已登录但 token 过期，等待 token 刷新
            // console.log(`⏳ [Dialog API check #${executionId}] API returned 401, user logged in, waiting for token refresh...`);
            return;
          } else {
            // 用户未登录，跳转到登录页面
            // console.log(`🔴 [Dialog API check #${executionId}] API returned 401, user not logged in, redirecting to login`);
            const currentPath = typeof window !== 'undefined' 
              ? window.location.pathname + window.location.search 
              : '/';
            localStorage.setItem("auth_redirect_url", currentPath);
            router.push(`/login?redirectTo=${encodeURIComponent(currentPath)}`);
            return;
          }
        }
        
        // 没有生成记录，根据用户类型和 API 返回的 allowBasicPlanLite 显示不同的 dialog
        if (!isCancelled) {
          // 🔥 检查 API 返回的 allowBasicPlanLite 字段
          const allowBasicPlanLite = data?.allowBasicPlanLite === true;
          
          // 🔥 Free Plan 用户有 3 次生成额度，显示 "Generate Transcript" dialog（次数配额）
          // Basic Plan Lite 用户：如果 API 返回 allowBasicPlanLite=false，显示升级 dialog
          const shouldShowUpgradeDialog = isBasicPlanLite && !allowBasicPlanLite;
          
          // 🎯 判断是否是付费用户（Basic Plan Lite / Basic / Premium）
          const isPaidUser = isBasicPlanLite || isPremiumPlan || isBasicPlan;
          
          if (shouldShowUpgradeDialog) {
            // console.log(`🔴 [Dialog useEffect #${executionId}] Basic Lite (not allowed) user without record, showing upgrade dialog`, {
            //   isBasicPlanLite,
            //   allowBasicPlanLite,
            // });
            setShowYouTubeUpgradeDialog(true);
          } else if (isPaidUser && allowBasicPlanLite) {
            // 🔥 付费用户（Basic Plan Lite / Basic / Premium）访问 allowBasicPlanLite=true 的视频，自动确认，不弹 dialog
            // console.log(`✅ [Dialog useEffect #${executionId}] Paid user with allowBasicPlanLite=true, auto-confirming`);
            setTranscriptGenerationConfirmed(true);
          } else {
            // 🔥 Free Plan 用户和 allowBasicPlanLite=false 的 Basic/Premium 用户显示 "Generate Transcript" dialog
            // console.log(`🔴 [Dialog useEffect #${executionId}] API check complete, showing generate dialog`, {
            //   isFreePlan,
            //   isBasicPlanLite,
            //   isPremium,
            //   allowBasicPlanLite,
            // });
            setShowTranscriptGenerationDialog(true);
          }
        }
      } catch (error) {
        console.error(`[Dialog useEffect #${executionId}] API check failed:`, error);
        // 🔥 API 检查失败时，不显示任何 dialog（保守策略）
        // 避免在用户有生成记录但 API 失败时错误显示 dialog
        // 用户可以刷新页面重试
        // console.log(`🔴 [Dialog useEffect #${executionId}] API check failed, not showing dialog to avoid false positives`);
      }
    };
    
    // 延迟 100ms 后检查，给其他状态更新一些时间
    const timeoutId = setTimeout(checkAndShowDialog, 100);
    
    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [
    transcriptGenerationKey,
    hasTranscriptGenerationRecord,
    transcriptGenerationConfirmed,
    isFullPlayerOpen,
    hydrationPending,
    isHydratingEpisode,
    currentEpisode,
    usageLoading,
    duration,
    usageInfo,
    // NOTE: showTranscriptGenerationDialog removed from deps to prevent
    // re-opening the dialog when user closes it (race condition with closeFullPlayer timeout)
  ]);

  const handleConfirmTranscriptDialog = useCallback(() => {
    // console.log('🔵 [Confirm] Button clicked', {
    //   hasTranscriptGenerationRecord,
    //   derivedSubtitlesDataLength: derivedSubtitlesData.length,
    //   baseEpisodeTranscriptLength,
    //   transcriptionAudioUrl,
    //   audioUrl,
    //   externalAudioUrl,
    //   episodeId,
    //   currentEpisodeOrigin: currentEpisode?.content?.origin || currentEpisode?.origin,
    // });
    
    if (transcriptDialogTimeoutRef.current) {
      clearTimeout(transcriptDialogTimeoutRef.current);
    }
    const shouldDeductWithoutStreaming =
      !hasTranscriptGenerationRecord &&
      (derivedSubtitlesData.length > 0 || baseEpisodeTranscriptLength > 0);

    const deductMinutes = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) return;
        if (pendingDeductRef.current) return;
        pendingDeductRef.current = true;
        const payloadDuration =
          (typeof episodeDurationSeconds === "number" && Number.isFinite(episodeDurationSeconds) && episodeDurationSeconds > 0)
            ? episodeDurationSeconds
          : typeof duration === "number" && Number.isFinite(duration) && duration > 0
          ? duration
          : 60;
        await fetch("/api/transcript/minutes", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            durationSeconds: payloadDuration,
            shareId: episodeId,
          }),
        });
      } catch (error) {
        console.error("[TranscriptDialog] Failed to deduct minutes", error);
      } finally {
        pendingDeductRef.current = false;
      }
    };

    if (shouldDeductWithoutStreaming) {
      deductMinutes();
    }

    // 🔥 立即显示 toast，让用户知道已开始处理
    toast.loading('Preparing transcription...', { id: 'transcribe-progress' });
    
    setIsTranscriptDialogLoading(true);
    // 🔥 减少延迟从 3000ms 到 500ms，让用户更快看到进度
    transcriptDialogTimeoutRef.current = setTimeout(() => {
      // console.log('🟢 [Confirm] Setting transcriptGenerationConfirmed to true');
      setTranscriptGenerationConfirmed(true);
      setShowTranscriptGenerationDialog(false);
      setIsTranscriptDialogLoading(false);
      transcriptDialogTimeoutRef.current = null;
    }, 500);
  }, [
    hasTranscriptGenerationRecord,
    derivedSubtitlesData.length,
    baseEpisodeTranscriptLength,
    episodeDurationSeconds,
    duration,
    episodeId,
    transcriptionAudioUrl,
    audioUrl,
    externalAudioUrl,
    currentEpisode,
  ]);

  useEffect(() => {
    if (derivedSubtitlesData.length > 0 && streamAbortControllerRef.current) {
      streamAbortControllerRef.current.abort();
      streamAbortControllerRef.current = null;
      setIsStreamingTranscript(false);
    }
  }, [derivedSubtitlesData.length]);

  const HYDRATION_RETRY_INTERVAL_MS = 30_000;

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (!episodeId || isHydratingEpisode) {
      // console.log("[GlobalPlayerControls v2] Skipping hydration (id/hydrating)", {
      //   episodeId,
      //   isHydratingEpisode,
      // });
      return;
    }
    
    // 🔥 当 SSE streaming 正在进行时，跳过 hydration 以防止覆盖 streaming 数据
    // streamingVocabulary 有数据表示 SSE 已经发送过 vocabulary
    const hasStreamingData = Boolean(streamingVocabularyRef.current) || isStreamingTranscript;
    // console.log("[GlobalPlayerControls] 🔍 Hydration check", {
    //   episodeId,
    //   isStreamingTranscript,
    //   hasStreamingVocabulary: !!streamingVocabularyRef.current,
    //   hasStreamingData,
    //   willSkip: hasStreamingData,
    // });
    if (hasStreamingData) {
      // console.log("[GlobalPlayerControls] ⏭️ Skipping hydration (SSE streaming active or has streaming vocabulary)");
      return;
    }

    const missingCriticalData =
      isVocabularyMissing ||
      isChosenExplanationMissing ||
      isChosenExplanationAudioMissing;
    
    // 🔥 检查是否需要获取用户的 generationRecords
    // 重要：即使 generationRecords 对象存在，也可能是过时的（比如从缓存加载的）
    // 所以需要检查是否有 _verifiedForUser 标记，表示已为当前用户验证过
    const existingGenerationRecords = 
      (currentEpisode as any)?.generationRecords ||
      (currentEpisode?.content as any)?.generationRecords;
    const hasVerifiedGenerationRecords = existingGenerationRecords?._verifiedForUser === user?.id;
    const needsGenerationRecordsHydration = Boolean(user?.id) && !hasVerifiedGenerationRecords;
    
    // console.log('[GlobalPlayerControls] Generation records hydration check:', {
    //   userId: user?.id,
    //   hasExistingRecords: !!existingGenerationRecords,
    //   verifiedForUser: existingGenerationRecords?._verifiedForUser,
    //   hasVerifiedGenerationRecords,
    //   needsGenerationRecordsHydration,
    // });

    if (hasTranscriptData && !missingCriticalData && !needsGenerationRecordsHydration) {
      return;
    }
    if (hydratingEpisodeIdRef.current === episodeId) {
      // console.log("[GlobalPlayerControls v2] Skipping hydration (already hydrating)", {
      //   episodeId,
      // });
      return;
    }
    const lastAttemptTimestamp = lastHydrationAttemptRef.current[episodeId];
    if (
      typeof lastAttemptTimestamp === "number" &&
      Date.now() - lastAttemptTimestamp < HYDRATION_RETRY_INTERVAL_MS
    ) {
      // console.log("[GlobalPlayerControls] Skipping hydration (retry interval)", {
      //   episodeId,
      //   lastAttemptTimestamp,
      // });
      return;
    }

    const controller = new AbortController();
    lastHydrationAttemptRef.current[episodeId] = Date.now();
    hydratingEpisodeIdRef.current = episodeId;
    setIsHydratingEpisode(true);

    const hydrateEpisode = async () => {
      try {
        const headers: HeadersInit = { "Content-Type": "application/json" };
        try {
          const { data: { session } } = await supabase.auth.getSession();
          const token = session?.access_token;
          if (token) {
            headers.Authorization = `Bearer ${token}`;
          }
        } catch (authError) {
          console.warn("[GlobalPlayerControls] Failed to resolve auth session for hydration", authError);
        }
        const response = await fetch(`/api/episodes/${episodeId}`, {
          method: "GET",
          headers,
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`Failed to hydrate episode (${response.status})`);
        }
        const episodePayload = await response.json();
        if (!episodePayload) {
          throw new Error("Hydration response missing content");
        }
        
        const shareData =
          (episodePayload?.content ?? episodePayload ?? {}) as Record<string, any>;
        
        // 🔥 在 generationRecords 中添加 _verifiedForUser 标记
        // 这样可以避免重复获取同一用户的生成记录
        const baseGenerationRecords = episodePayload?.generationRecords ?? shareData?.generationRecords ?? {};
        const verifiedGenerationRecords = {
          ...baseGenerationRecords,
          _verifiedForUser: user?.id,
        };
        
        // 🔥 保留现有的 vocabulary 数据（如果服务器返回的为空）
        // 这防止 SSE 传来的 vocabulary 被 hydration 覆盖
        // 使用 currentEpisodeRef.current 和 streamingVocabularyRef.current 获取最新数据
        const latestEpisode = currentEpisodeRef.current;
        const existingVocabulary = latestEpisode?.vocabulary ?? 
          latestEpisode?.content?.vocabulary ?? 
          latestEpisode?.content?.content?.vocabulary;
        const serverVocabulary = shareData?.vocabulary ?? 
          shareData?.content?.vocabulary ?? 
          shareData?.content?.content?.vocabulary;
        // 🔥 关键：也检查 streamingVocabularyRef（独立的SSE数据，不会被覆盖）
        const streamingVocab = streamingVocabularyRef.current;
        
        // 检查各个来源是否有有效数据
        const hasServerVocabulary = serverVocabulary?.scenarios?.some?.(
          (s: any) => Array.isArray(s?.terms) && s.terms.length > 0
        );
        const hasExistingVocabulary = existingVocabulary?.scenarios?.some?.(
          (s: any) => Array.isArray(s?.terms) && s.terms.length > 0
        );
        const hasStreamingVocabulary = streamingVocab?.scenarios?.some?.(
          (s: any) => Array.isArray(s?.terms) && s.terms.length > 0
        );
        
        // 🔥 优先级：服务器数据 > streamingVocabulary > currentEpisode数据
        // 如果服务器没有 vocabulary，优先使用 streamingVocabulary
        let vocabularyToUse = serverVocabulary;
        if (!hasServerVocabulary) {
          if (hasStreamingVocabulary) {
            // console.log('[GlobalPlayerControls] Hydration: Using streamingVocabulary (server empty)');
            vocabularyToUse = streamingVocab;
          } else if (hasExistingVocabulary) {
            // console.log('[GlobalPlayerControls] Hydration: Using existingVocabulary (server & streaming empty)');
            vocabularyToUse = existingVocabulary;
          }
        }
        
        // 同样处理 chapters
        const existingChapters = latestEpisode?.chapters ?? 
          latestEpisode?.content?.chapters;
        const serverChapters = shareData?.chapters ?? shareData?.content?.chapters;
        const chaptersToUse = (Array.isArray(serverChapters) && serverChapters.length > 0) 
          ? serverChapters 
          : existingChapters;
        
        const normalizedShare = {
          ...shareData,
          hydrationPending: false,
          isHydrationPlaceholder: false,
          generationRecords: verifiedGenerationRecords,
          vocabulary: vocabularyToUse,
          chapters: chaptersToUse,
          content:
            shareData?.content && typeof shareData.content === "object"
              ? {
                  ...shareData.content,
                  hydrationPending: false,
                  isHydrationPlaceholder: false,
                  generationRecords: verifiedGenerationRecords,
                  vocabulary: vocabularyToUse,
                  chapters: chaptersToUse,
                  content: shareData.content.content ? {
                    ...shareData.content.content,
                    vocabulary: vocabularyToUse,
                    chapters: chaptersToUse,
                  } : shareData.content.content,
                }
              : shareData.content,
        };
        
        // console.log('[GlobalPlayerControls] Hydration complete', {
        //   episodeId,
        //   userId: user?.id,
        //   generationRecords: verifiedGenerationRecords,
        //   hasServerVocabulary,
        //   hasExistingVocabulary,
        //   hasStreamingVocabulary,
        //   vocabularySource: hasServerVocabulary ? 'server' : (hasStreamingVocabulary ? 'streaming' : (hasExistingVocabulary ? 'existing' : 'none')),
        //   serverVocabularyScenarios: serverVocabulary?.scenarios?.length ?? 0,
        //   streamingVocabularyScenarios: streamingVocab?.scenarios?.length ?? 0,
        //   existingVocabularyScenarios: existingVocabulary?.scenarios?.length ?? 0,
        //   finalVocabularyScenarios: vocabularyToUse?.scenarios?.length ?? 0,
        // });
        
        setCurrentEpisode(normalizedShare);
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error("[GlobalPlayerControls] Failed to hydrate episode", error);
          const fallbackEpisode = clearHydrationPendingFlags(currentEpisode);
          if (fallbackEpisode && fallbackEpisode !== currentEpisode) {
            setCurrentEpisode(fallbackEpisode);
          }
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsHydratingEpisode(false);
          if (hydratingEpisodeIdRef.current === episodeId) {
            hydratingEpisodeIdRef.current = null;
          }
        }
      }
    };

    hydrateEpisode();

    return () => {
      controller.abort();
      setIsHydratingEpisode(false);
      if (hydratingEpisodeIdRef.current === episodeId) {
        hydratingEpisodeIdRef.current = null;
      }
    };
  }, [
    episodeId,
    hasTranscriptData,
    isChosenExplanationAudioMissing,
    isChosenExplanationMissing,
    isVocabularyMissing,
    hydrationPending,
    isHydratingEpisode,
    currentEpisode,
    clearHydrationPendingFlags,
    setCurrentEpisode,
    user?.id,
    isFullPlayerOpen, // 🔥 当全屏播放器打开时，检查是否需要 hydration
    isStreamingTranscript, // 🔥 当 SSE streaming 状态变化时重新评估
  ]);

  useEffect(() => {
    if (!shouldStreamTranscript || hydrationPending) {
      // console.log('🔴 [useEffect startTranscript] Not starting', {
      //   shouldStreamTranscript,
      //   hydrationPending,
      // });
      return;
    }

    if (streamingAttemptRef.current || isStreamingTranscript) {
      // console.log('🔴 [useEffect startTranscript] Already streaming', {
      //   streamingAttemptRef: streamingAttemptRef.current,
      //   isStreamingTranscript,
      // });
      return;
    }

    // console.log('🚀 [useEffect startTranscript] Starting transcript stream!');
    streamingAttemptRef.current = true;
    startTranscriptStream();
  }, [shouldStreamTranscript, isStreamingTranscript, startTranscriptStream, hydrationPending]);

  useEffect(() => {
    if (streamingError) {
      toast.error(streamingError);
    }
  }, [streamingError]);

  useEffect(() => {
    if (isStreamingTranscript) {
      setIsTranscriptDialogLoading(false);
    }
  }, [isStreamingTranscript]);

  useEffect(() => {
    return () => {
      if (transcriptDialogTimeoutRef.current) {
        clearTimeout(transcriptDialogTimeoutRef.current);
        transcriptDialogTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!transcriptGenerationConfirmed) {
      return;
    }
    if (!isStreamingTranscript && streamingAttemptRef.current) {
      refreshUsage?.();
    }
  }, [isStreamingTranscript, transcriptGenerationConfirmed, refreshUsage]);

  useEffect(() => {
    if (
      isStreamingTranscript ||
      streamingSubtitles.length === 0 ||
      !episodeId ||
      episodeId.startsWith("temp-") ||
      derivedSubtitlesData.length > 0 ||
      !transcriptionAudioUrl ||
      hydrationPending
    ) {
      return;
    }

    if (transcriptCacheShareIdRef.current === episodeId) {
      return;
    }

    const transcriptSections = convertCuesToTranscriptSections(streamingSubtitles);
    if (transcriptSections.length === 0) {
      return;
    }

    const metadataPayload = {
      videoUrl: resolvedMediaUrl || transcriptionAudioUrl,
      title:
        episodeTitle ||
        currentEpisode?.title ||
        currentEpisode?.content?.title ||
        `YouTube Video ${episodeId}`,
      author:
        episodeAuthor ||
        currentEpisode?.author ||
        currentEpisode?.content?.author ||
        "Unknown Channel",
      thumbnail:
        episodeThumbnail ||
        currentEpisode?.thumbnail ||
        currentEpisode?.content?.thumbnail ||
        "",
      ownerAvatarUrl:
        youtubeMetadata?.ownerAvatarUrl ||
        currentEpisode?.ownerAvatarUrl ||
        currentEpisode?.content?.ownerAvatarUrl ||
        "",
      ownerProfileUrl:
        youtubeMetadata?.ownerProfileUrl ||
        currentEpisode?.ownerProfileUrl ||
        currentEpisode?.content?.ownerProfileUrl ||
        "",
      publishDate:
        currentEpisode?.publishDate ||
        currentEpisode?.content?.publishDate ||
        youtubeMetadata?.publishDate ||
        undefined,
      visibility:
        currentEpisode?.visibility ||
        currentEpisode?.content?.visibility ||
        "public",
    };

    const payload: Record<string, unknown> = {
      shareId: episodeId,
      audioUrl: transcriptionAudioUrl,
      metadata: metadataPayload,
    };

    if (user?.id) {
      payload.userId = user.id;
    }

    const controller = new AbortController();
    transcriptCacheShareIdRef.current = episodeId;

    (async () => {
      try {
        const response = await fetch("/api/videos/cache", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Cache API failed (${response.status})`);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error("[GlobalPlayerControls] Failed to cache transcript", error);
          transcriptCacheShareIdRef.current = null;
        }
      }
    })();

    return () => controller.abort();
  }, [
    isStreamingTranscript,
    streamingSubtitles,
    episodeId,
    transcriptionAudioUrl,
    derivedSubtitlesData.length,
    episodeTitle,
    episodeAuthor,
    episodeThumbnail,
    resolvedMediaUrl,
    currentEpisode?.title,
    currentEpisode?.content?.title,
    currentEpisode?.author,
    currentEpisode?.content?.author,
    currentEpisode?.thumbnail,
    currentEpisode?.content?.thumbnail,
    currentEpisode?.ownerAvatarUrl,
    currentEpisode?.content?.ownerAvatarUrl,
    currentEpisode?.ownerProfileUrl,
    currentEpisode?.content?.ownerProfileUrl,
    currentEpisode?.publishDate,
    currentEpisode?.content?.publishDate,
    currentEpisode?.visibility,
    currentEpisode?.content?.visibility,
    youtubeMetadata?.ownerAvatarUrl,
    youtubeMetadata?.ownerProfileUrl,
    youtubeMetadata?.publishDate,
    resolvedMediaUrl,
    user?.id,
    hydrationPending,
  ]);

  const geoCountry = useGeoCountry();
  const isChinaRegion = geoCountry === "CN";
  const { isAccessible: isYouTubeAccessible } = useYouTubeAccessibility();

  const isBlobVideo = Boolean(
    videoUrl &&
      (videoUrl.includes("blob.vercel-storage.com") ||
        videoUrl.startsWith("blob:"))
  );

  const videoExtensionMatch = videoUrl?.split("?")[0]?.match(/\.([a-z0-9]+)$/i);
  const videoExtension = videoExtensionMatch?.[1]?.toLowerCase();
  const knownVideoExtensions = new Set([
    "mp4",
    "m4v",
    "mov",
    "webm",
    "mkv",
    "m3u8",
    "mpd",
  ]);
  const looksLikeVideoSource = Boolean(
    videoUrl &&
      (origin === "video" ||
        isBlobVideo ||
        (videoExtension && knownVideoExtensions.has(videoExtension)))
  );

  const isYouTubeLink = Boolean(
    videoUrl &&
      (videoUrl.includes("youtube.com") || videoUrl.includes("youtu.be"))
  );

  const hasDirectVideoFile = Boolean(
    (videoUrl && looksLikeVideoSource && !isYouTubeLink) && (!explicitYoutubeVideoId || isBlobVideo)
  );

  const canAccessYouTube = !isChinaRegion && isYouTubeAccessible;

  const isYoutubeEpisode = useMemo(() => {
    // 1. Strongest signal: Direct video file present -> NOT YouTube
    if (hasDirectVideoFile) {
        return false;
    }
    
    // 2. Explicit source type from data
    if (currentEpisode?.sourceType === "youtube") {
      return true;
    }
    if (currentEpisode?.sourceType === "direct" || currentEpisode?.sourceType === "direct/blob") {
      return false;
    }

    // 3. Explicit prop
    if (explicitYoutubeVideoId) {
      return true;
    }

    // 4. Source URL analysis
    if (isYouTubeLink) {
      return true;
    }

    // 5. (Removed) Inferring solely from ID format is dangerous when data is loading
    // if (youtubeVideoId) { return true; }

    return false;
  }, [currentEpisode?.sourceType, explicitYoutubeVideoId, isYouTubeLink, hasDirectVideoFile]);

  const shouldAttemptYoutubeAudioFetch = Boolean(
    isYoutubeEpisode &&
    !hasDirectVideoFile &&
    !audioUrl &&
    !externalAudioUrl
  );
  
  const shouldUseYoutube = !isBlobVideo && 
    !hasDirectVideoFile &&  // 🔥 当有直接视频文件时（包括 origin === "video"），不使用 YouTube
    currentEpisode?.sourceType !== "direct/blob" && 
    currentEpisode?.sourceType !== "direct" &&
    (explicitYoutubeVideoId
      ? Boolean(youtubeVideoId && canAccessYouTube)
      : Boolean(
          youtubeVideoId && 
          canAccessYouTube &&
          // CRITICAL: Only enable YouTube mode if we have positive confirmation
          // (either via explicit sourceType or a YouTube URL).
          // Do NOT default to YouTube just because the ID looks like one, 
          // as this causes false positives during initial load of Blob episodes.
          (currentEpisode?.sourceType === "youtube" || isYouTubeLink)
        ));

  // DEBUG LOGGING REMOVED
  useEffect(() => {
    // Removed debug logs for Blob detection
  }, [
    videoUrl,
    isBlobVideo,
    hasDirectVideoFile,
    explicitYoutubeVideoId,
    youtubeVideoId,
    isYouTubeLink,
    shouldUseYoutube,
    shouldAttemptYoutubeAudioFetch,
    isYoutubeEpisode
  ]);

  // 🔥 当 YouTube 视频没有 audioUrl 时，自动获取音频 URL
  useEffect(() => {
    if (!shouldAttemptYoutubeAudioFetch) {
      return;
    }
    if (audioFetchAttemptRef.current) {
      return;
    }
    if (!youtubeVideoId) {
      return;
    }

    // console.log('[GlobalPlayer] Fetching YouTube audio URL for:', youtubeVideoId);
    audioFetchAttemptRef.current = true;

    const fetchAudioUrl = async () => {
      try {
        const response = await fetch(`/api/youtube/audio-url?videoId=${youtubeVideoId}`);
        const data = await response.json();
        
        if (response.ok && data.file) {
          // console.log('[GlobalPlayer] YouTube audio URL fetched:', data.file);
          setExternalAudioUrl(data.file);
          setAudioFetchError(null);
        } else {
          const errorMsg = data.error || `Failed to fetch audio (${response.status})`;
          console.error('[GlobalPlayer] Failed to fetch YouTube audio URL:', errorMsg);
          setAudioFetchError(errorMsg);
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Failed to fetch audio URL';
        console.error('[GlobalPlayer] Error fetching YouTube audio URL:', error);
        setAudioFetchError(errorMsg);
      }
    };

    fetchAudioUrl();
  }, [shouldAttemptYoutubeAudioFetch, youtubeVideoId]);

  const activeYoutubeVideoId = shouldUseYoutube ? youtubeVideoId : undefined;
  const shouldRenderVideoPlayer = Boolean(
    (hasDirectVideoFile && videoUrl && looksLikeVideoSource) || shouldUseYoutube
  );
  const playerVideoSource =
    activeYoutubeVideoId
      ? `youtube/${activeYoutubeVideoId}`
      : resolvedMediaUrl;

  // useEffect(() => {
  //   if (process.env.NODE_ENV !== "production") {
  //     console.log("[GlobalPlayer] player video source state", {
  //       activeYoutubeVideoId,
  //       playerVideoSource,
  //       shouldUseYoutube,
  //       hasDirectVideoFile,
  //       shouldRenderVideoPlayer,
  //       resolvedMediaUrl,
  //     });
  //   }
  // }, [
  //   activeYoutubeVideoId,
  //   playerVideoSource,
  //   shouldUseYoutube,
  //   hasDirectVideoFile,
  //   shouldRenderVideoPlayer,
  //   resolvedMediaUrl,
  // ]);

  useEffect(() => {
    if (!activeYoutubeVideoId) {
      setYoutubeMetadata(null);
      return;
    }

    if (youtubeMetadata?.videoId === activeYoutubeVideoId) {
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const fetchYoutubeMetadata = async () => {
      try {
        const response = await fetch(
          `/api/youtube/metadata?videoId=${activeYoutubeVideoId}`,
          { signal: controller.signal }
        );

        if (!response.ok) {
          throw new Error(`metadata request failed with ${response.status}`);
        }

        const payload = await response.json();
        if (!isMounted) {
          return;
        }

        if (payload?.success && payload?.data) {
          setYoutubeMetadata({
            videoId: activeYoutubeVideoId,
            title: payload.data.title,
            thumbnail: payload.data.thumbnail,
            ownerAvatarUrl: payload.data.ownerAvatarUrl,
            author: payload.data.author,
            ownerProfileUrl: payload.data.ownerProfileUrl,
          });
        }
      } catch (error) {
        if (!isMounted) {
          return;
        }
        console.error("[GlobalPlayer] Failed to fetch YouTube metadata", {
          error,
          videoId: activeYoutubeVideoId,
        });
      }
    };

    fetchYoutubeMetadata();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [activeYoutubeVideoId, youtubeMetadata?.videoId]);

  useEffect(() => {
    if (!playerVideoSource) {
      return;
    }

    const mode = shouldUseYoutube ? "youtube" : "direct/blob";

    if (typeof window !== "undefined") {
      console.warn("[GlobalPlayer] media source changed", {
        episodeId,
        mode,
        source: playerVideoSource,
        geoCountry,
        youtubeAccessible: isYouTubeAccessible,
      });
    }

    if (!episodeId) {
      return;
    }

    const payload = {
      episodeId,
      mode,
      source: playerVideoSource,
      geoCountry: geoCountry ?? "unknown",
      youtubeAccessible: isYouTubeAccessible,
      timestamp: Date.now(),
    };

    const body = JSON.stringify(payload);
    const logUrl = "/api/player/source-log";

    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(logUrl, blob);
    } else {
      fetch(logUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {
        // best-effort logging only
      });
    }
  }, [
    episodeId,
    geoCountry,
    isYouTubeAccessible,
    playerVideoSource,
    shouldUseYoutube,
  ]);

  const mediaArtwork = useMemo(() => {
    if (!episodeThumbnail) {
      return undefined;
    }
    return [
      { src: episodeThumbnail, sizes: "96x96", type: "image/jpeg" },
      { src: episodeThumbnail, sizes: "128x128", type: "image/jpeg" },
      { src: episodeThumbnail, sizes: "192x192", type: "image/jpeg" },
      { src: episodeThumbnail, sizes: "256x256", type: "image/jpeg" },
      { src: episodeThumbnail, sizes: "384x384", type: "image/jpeg" },
      { src: episodeThumbnail, sizes: "512x512", type: "image/jpeg" },
    ];
  }, [episodeThumbnail]);

  // 🔥 修复：在 episodes 页面不准备视频配置，避免两个视频播放器同时播放导致两个声音
  // Episodes 页面的 VideoPlayerSection 已经在处理视频播放
  const shouldPrepareVideoConfig =
    shouldRenderVideoPlayer && Boolean(playerVideoSource) && !isOnEpisodePage;

  const videoConfig = useMemo<VideoPlayerConfig | undefined>(() => {
    if (!shouldPrepareVideoConfig || !playerVideoSource) {
      return undefined;
    }
    return {
      url: playerVideoSource,
      artwork: mediaArtwork,
    };
  }, [mediaArtwork, playerVideoSource, shouldPrepareVideoConfig]);

  const canNavigateToEpisode = Boolean(episodeId);
  const shouldShowNavigateButton = canNavigateToEpisode && !isOnEpisodePage;

  // 防抖 refs，防止快速连续点击
  const globalPlayPauseLastActionTimeRef = useRef<number>(0);
  const GLOBAL_PLAY_PAUSE_DEBOUNCE_MS = 300;

  const handleTogglePlayPause = useCallback(() => {
    if (!isPlaying && freePreviewLocked) {
      showFreePreviewToast();
      return;
    }
    
    // 防抖：防止快速连续点击
    const now = Date.now();
    const timeSinceLastAction = now - globalPlayPauseLastActionTimeRef.current;
    
    if (timeSinceLastAction < GLOBAL_PLAY_PAUSE_DEBOUNCE_MS) {
      // console.log('[GlobalPlayPause] Debounced, too quick:', timeSinceLastAction + 'ms');
      return;
    }
    
    globalPlayPauseLastActionTimeRef.current = now;
    
    // 直接播放/暂停，不需要检查页面或跳转
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, freePreviewLocked, showFreePreviewToast, play, pause]);

  const handleNavigateToEpisode = () => {
    // 先关闭全屏播放器，以便切换到详情页面时抽屉收起
    closeFullPlayer();

    // 🎯 Podverse风格：导航前先保存当前播放进度
    if (currentTime > 0 && duration) {
      // console.log("🎯 Bottom player navigation triggered, saving progress");
      // 从player context获取saveProgressImmediately函数
      const audioElement = document.querySelector("audio") as HTMLAudioElement;
      if (audioElement && audioElement.currentTime > 0) {
        // 创建一个事件来通知player context保存进度
        const saveEvent = new CustomEvent("saveProgressBeforeNavigation", {
          detail: {
            currentTime: audioElement.currentTime,
            duration: audioElement.duration,
            reason: "bottom_player_navigation",
          },
        });
        window.dispatchEvent(saveEvent);
      }
    }

    let targetEpisodeId = episodeId;
    if (targetEpisodeId) {
      // 🔥 如果是临时ID，尝试从 localStorage 获取永久 ID
      if (targetEpisodeId.startsWith("temp-")) {
        // console.log(
        //   "Temporary episode ID detected, checking for permanent ID mapping..."
        // );

        // 检查是否有已保存的永久 ID 映射
        if (typeof window !== "undefined") {
          const mappings = JSON.parse(
            localStorage.getItem("tempIdMappings") || "{}"
          );
          const permanentId = mappings[targetEpisodeId];

          if (permanentId) {
            // console.log(
            //   `✅ Found permanent ID: ${permanentId}, redirecting...`
            // );
            targetEpisodeId = permanentId;
          } else {
            console.log("⚠️ No permanent ID found yet, using temporary ID");
          }
        }
      }

      // 由于localStorage已经保存了播放器状态，包括currentTime
      // 不需要在URL中添加时间参数，避免重复和潜在冲突
      const url = `/episodes/${targetEpisodeId}`;

      // 使用Next.js客户端路由，避免页面刷新
      // localStorage中的播放状态会自动恢复播放位置
      router.push(url);
    }
  };

  const scrollToCurrentTranscript = () => {
    // 使用 usePathname hook 检查是否在episode页面，确保路由变化时能正确响应
    if (isOnEpisodePage) {
      // 如果已经在 episode 页面，滚动到当前转录位置
      const activeTranscript = document.querySelector(
        '.transcript-content-container [data-active="true"]'
      );
      if (activeTranscript) {
        const elementTop = activeTranscript.getBoundingClientRect().top;
        const { isVideoMinimized } = useEpisodeStore.getState();
        const viewportWidth =
          typeof window !== "undefined" ? window.innerWidth : 0;
        const offsetBase =
          viewportWidth < 768
            ? 314
            : viewportWidth < 1280
            ? isVideoMinimized
              ? 180
              : 550
            : isVideoMinimized
            ? 120
            : 365;
        const offsetTop = window.pageYOffset + elementTop - offsetBase;

        window.scrollTo({
          top: offsetTop,
          behavior: "smooth",
        });
      }
    } else {
      // 如果不在 episode 页面，跳转到对应的 episode 页面
      handleNavigateToEpisode();
    }
  };

  const handleFullPlayerOpenChange = useCallback(
    (open: boolean) => {
      // Don't close FullPlayerDialog if ShadowingPracticeCard is open
      if (!open && showShadowingDialog) {
        return;
      }

      if (!isMobile) {
        if (open) {
          closeFullPlayer();
        }
        return;
      }

      if (open) {
        openFullPlayer();
      } else {
        closeFullPlayer();
      }
    },
    [isMobile, openFullPlayer, closeFullPlayer, showShadowingDialog]
  );

  const handleOpenFullPlayer = useCallback(() => {
    if (!isMobile) {
      return;
    }
    // 🎯 如果当前页面是 /episodes/xxx，不弹开 full dialog（因为已经在 episode 详情页了）
    if (pathname?.startsWith('/episodes/')) {
      return;
    }
    openFullPlayer();
  }, [isMobile, openFullPlayer, pathname]);

  // Get current chunk from subtitles or cleanReadData
  const getCurrentChunk = useCallback(() => {
    // First try to get from subtitles (more reliable)
    if (subtitles && Array.isArray(subtitles) && subtitles.length > 0) {
      // Find the subtitle cue that contains currentTime
      let closestCue: SubtitleCue | null = null;
      let minDistance = Infinity;
      
      for (const cue of subtitles) {
        const start = Number(cue.start);
        const end = Number.isFinite(cue.end) ? Number(cue.end) : start + 3; // Default 3 seconds if no end time
        
        // Exact match
        if (currentTime >= start && currentTime <= end) {
          return {
            text: cue.text || '',
            translation: cue.translation,
            timestamp: [start, end] as [number, number],
          };
        }
        
        // Find closest cue if no exact match
        const distance = Math.min(
          Math.abs(currentTime - start),
          Math.abs(currentTime - end)
        );
        if (distance < minDistance) {
          minDistance = distance;
          closestCue = cue;
        }
      }
      
      // If we found a close cue (within 2 seconds), use it
      if (closestCue && minDistance < 2) {
        const start = Number(closestCue.start);
        const end = Number.isFinite(closestCue.end) ? Number(closestCue.end) : start + 3;
        return {
          text: closestCue.text || '',
          translation: closestCue.translation,
          timestamp: [start, end] as [number, number],
        };
      }
    }

    // Fallback to cleanReadData if subtitles not available
    if (cleanReadData && Array.isArray(cleanReadData)) {
      // Find the chunk that contains currentTime
      for (const group of cleanReadData) {
        if (group.chunks && Array.isArray(group.chunks)) {
          for (const chunk of group.chunks) {
            const [start, end] = Array.isArray(chunk.timestamp) 
              ? chunk.timestamp 
              : [0, 0];
            if (currentTime >= start && currentTime <= end) {
              return {
                text: chunk.text || '',
                translation: chunk.translation,
                timestamp: [start, end] as [number, number],
              };
            }
          }
        }
        // Also check group level
        const [start, end] = Array.isArray(group.timestamp)
          ? group.timestamp
          : [0, 0];
        if (currentTime >= start && currentTime <= end) {
          return {
            text: group.text || '',
            translation: group.translation,
            timestamp: [start, end] as [number, number],
          };
        }
      }
    }

    // If still not found, try to get from currentEpisode content
    if (currentEpisode?.content?.transcript && Array.isArray(currentEpisode.content.transcript)) {
      for (const segment of currentEpisode.content.transcript) {
        const chunks = segment.chunks || segment.sentences || [segment];
        for (const chunk of chunks) {
          const start = Array.isArray(chunk.timestamp) 
            ? chunk.timestamp[0] 
            : chunk.startTime || segment.startTime || 0;
          const end = Array.isArray(chunk.timestamp) 
            ? chunk.timestamp[1] 
            : chunk.endTime || segment.endTime || start + 3;
          
          if (currentTime >= start && currentTime <= end) {
            return {
              text: chunk.text || segment.text || '',
              translation: chunk.translation || segment.translation,
              timestamp: [start, end] as [number, number],
            };
          }
        }
      }
    }

    return null;
  }, [subtitles, cleanReadData, currentEpisode, currentTime]);

  // Handle shadowing click
  const handleShadowingClick = useCallback(() => {
    // console.log('🎤 Shadowing button clicked', {
    //   currentTime,
    //   hasSubtitles: !!subtitles && subtitles.length > 0,
    //   hasCleanReadData: !!cleanReadData,
    //   hasEpisodeContent: !!currentEpisode?.content?.transcript,
    // });
    const currentChunk = getCurrentChunk();
    // console.log('Current chunk:', currentChunk);
    if (!currentChunk || !currentChunk.text) {
      console.warn('No chunk found:', {
        currentTime,
        subtitlesLength: subtitles?.length,
        cleanReadDataLength: Array.isArray(cleanReadData) ? cleanReadData.length : 'not array',
      });
      toast.error(t('shadowing.noChunk', 'No chunk found at current time'));
      return;
    }

    // console.log('✅ Setting shadowing state and showing dialog');
    setShadowingState(prev => ({
      ...prev,
      currentSentence: currentChunk,
      showSentence: false,
      showTranscription: false,
      transcriptionAccuracy: null,
    }));
    setShowShadowingDialog(true);
    pause();
    
    // Debug: Check state after setting
    // setTimeout(() => {
    //   console.log('🔍 State check:', {
    //     showShadowingDialog: true, // We just set it
    //     currentSentence: currentChunk,
    //   });
    // }, 100);
  }, [getCurrentChunk, pause, t, currentTime, subtitles, cleanReadData, currentEpisode]);

  // Handle shadowing replay
  const handleShadowingReplay = useCallback((speed: number = shadowingState.playbackSpeed) => {
    if (!shadowingState.currentSentence) return;

    // Clear existing timeout
    if (shadowingTimeoutRef.current) {
      clearTimeout(shadowingTimeoutRef.current);
      shadowingTimeoutRef.current = null;
    }

    const [startTime] = shadowingState.currentSentence.timestamp;
    seekTo(startTime);
    setPlaybackSpeed(speed);

    setTimeout(async () => {
      try {
        await play();
        const sentenceDuration = shadowingState.currentSentence!.timestamp[1] - shadowingState.currentSentence!.timestamp[0];
        const pauseDelay = Math.max(100, (sentenceDuration - 0.05) * 1000 / speed);

        shadowingTimeoutRef.current = setTimeout(() => {
          setPlaybackSpeed(1.0);
          pause();
          shadowingTimeoutRef.current = null;
        }, pauseDelay);
      } catch (error) {
        console.error('Error in shadowing replay:', error);
      }
    }, 100);
  }, [shadowingState.currentSentence, shadowingState.playbackSpeed, seekTo, setPlaybackSpeed, play, pause]);

  // Handle shadowing continue
  const handleShadowingContinue = useCallback(() => {
    setShowShadowingDialog(false);
    setShadowingState(prev => ({
      ...prev,
      showTranslation: false,
      showSentence: false,
    }));

    if (shadowingTimeoutRef.current) {
      clearTimeout(shadowingTimeoutRef.current);
      shadowingTimeoutRef.current = null;
    }

    setTimeout(async () => {
      if (shadowingState.currentSentence) {
        const chunkEndTime = shadowingState.currentSentence.timestamp[1] + 0.1;
        seekTo(chunkEndTime);
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      play();
    }, 100);
  }, [shadowingState.currentSentence, seekTo, play]);

  // Listen to transcription results and calculate accuracy
  useEffect(() => {
    if (transcribedText && shadowingState.currentSentence?.text) {
      const accuracy = calculateOptimalSimilarity(
        transcribedText,
        shadowingState.currentSentence.text
      );
      setShadowingState(prev => ({ ...prev, transcriptionAccuracy: accuracy }));

      // Save record
      const transcriptionKey = `${transcribedText}-${shadowingState.currentSentence.text}`;
      const episodeId = currentEpisode?.shareId || currentEpisode?.content?.shareId;
      if (episodeId && !saving && transcriptionKey !== lastSavedTranscriptionRef.current) {
        lastSavedTranscriptionRef.current = transcriptionKey;

        const duration = recordingStartTimeRef.current
          ? (Date.now() - recordingStartTimeRef.current) / 1000
          : 3;

        const episodeTitle = currentEpisode?.content?.title || currentEpisode?.title || 'Unknown Episode';
        const episodeThumbnail = currentEpisode?.content?.thumbnail || currentEpisode?.thumbnail || '';

        saveRecord({
          userId: user?.id,
          episodeId,
          episodeTitle,
          episodeThumbnail,
          sentenceText: shadowingState.currentSentence.text,
          userTranscription: transcribedText,
          accuracy,
          duration
        }).catch(error => {
          console.error('Failed to save shadowing record:', error);
          lastSavedTranscriptionRef.current = null;
        });
      }
    }
  }, [
    transcribedText,
    shadowingState.currentSentence,
    currentEpisode,
    saving,
    saveRecord,
    user?.id,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (shadowingTimeoutRef.current) {
        clearTimeout(shadowingTimeoutRef.current);
      }
    };
  }, []);

  // Debug: Log state changes
  // useEffect(() => {
  //   console.log('🔍 Shadowing state changed:', {
  //     showShadowingDialog,
  //     hasCurrentSentence: !!shadowingState.currentSentence,
  //     currentSentenceText: shadowingState.currentSentence?.text?.substring(0, 50),
  //   });
  // }, [showShadowingDialog, shadowingState.currentSentence]);

  const bottomPlayerContent = useMemo(() => {
    if (!currentEpisode) {
      return {};
    }

    const mergedContent = currentEpisode.content
      ? {
          ...currentEpisode.content,
          title: episodeTitle || currentEpisode.content.title,
          author: episodeAuthor || currentEpisode.content.author,
          thumbnail: episodeThumbnail || currentEpisode.content.thumbnail,
          ownerAvatarUrl:
            youtubeMetadata?.ownerAvatarUrl ||
            currentEpisode.content.ownerAvatarUrl,
          ownerProfileUrl:
            youtubeMetadata?.ownerProfileUrl ||
            currentEpisode.content.ownerProfileUrl,
        }
      : undefined;

    return {
      ...currentEpisode,
      title: episodeTitle || currentEpisode.title,
      author: episodeAuthor || currentEpisode.author,
      thumbnail: episodeThumbnail || currentEpisode.thumbnail,
       sourceType: currentEpisode.sourceType,
       videoId: currentEpisode.videoId,
      ownerAvatarUrl:
        youtubeMetadata?.ownerAvatarUrl || currentEpisode.ownerAvatarUrl,
      ownerProfileUrl:
        youtubeMetadata?.ownerProfileUrl || currentEpisode.ownerProfileUrl,
      content: mergedContent,
    };
  }, [
    currentEpisode,
    episodeAuthor,
    episodeThumbnail,
    episodeTitle,
    youtubeMetadata?.ownerAvatarUrl,
    youtubeMetadata?.ownerProfileUrl,
  ]);

  // 🔥 Refresh usage info when dialog opens to ensure accurate Remaining Quotes
  useEffect(() => {
    if (showTranscriptGenerationDialog) {
      // console.log('🔄 [Transcript Dialog] Refreshing usage info when dialog opens');
      refreshUsage();
    }
  }, [showTranscriptGenerationDialog, refreshUsage]);

  return (
    <>
      <Dialog
        open={showTranscriptGenerationDialog}
        onOpenChange={(open) => {
          setShowTranscriptGenerationDialog(open);
          if (!open) {
            closeFullPlayer();
          }
        }}
      >
        <DialogContent className="rounded-[32px] border-2 border-border/50 bg-background/95 backdrop-blur-xl">
          <DialogHeader className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-foreground/10">
                <Sparkles className="h-5 w-5 text-foreground" />
              </div>
              <DialogTitle className="text-xl font-semibold">
                {t("transcript.dialog.title", "Generate Transcript")}
              </DialogTitle>
            </div>
            <p className="text-base leading-relaxed text-muted-foreground">
              {isFreePlan 
                ? t(
                    "transcript.dialog.descriptionCount",
                    "Generating subtitles will use 1 of your monthly generations."
                  )
                : t(
                    "transcript.dialog.description",
                    "Generating subtitles requires deducting time from your monthly quota."
                  )
              }
            </p>
          </DialogHeader>

          <div className="space-y-3 rounded-2xl border border-border/50 bg-muted/20 px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {t("transcript.dialog.cost", "Cost")}
              </span>
              <div className="flex items-center gap-1.5 text-sm font-medium">
                <span className="text-lg">{isFreePlan ? 1 : minutesToDeduct}</span>
                <span className="text-muted-foreground">
                  {isFreePlan 
                    ? t("common.count.times", "times")
                    : t("common.time.minutes", "min")
                  }
                </span>
              </div>
            </div>

            <div className="h-px w-full bg-border/50" />

            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {t("transcript.dialog.remaining", "Remaining Quota")}
              </span>
              <div className="flex items-center gap-1.5 text-sm font-medium">
                <span
                  className={cn(
                    insufficientQuota ? "text-destructive" : "text-primary"
                  )}
                >
                  {isFreePlan
                    ? (usageInfo?.remainingTranscripts ?? 0)
                    : (typeof transcriptMinutesRemaining === "number"
                        ? transcriptMinutesRemaining
                        : "–")
                  }
                </span>
                <span className="text-muted-foreground">
                  {isFreePlan 
                    ? t("common.count.times", "times")
                    : t("common.time.minutes", "min")
                  }
                </span>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-3">
            <Button
              variant="ghost"
              onClick={() => {
                setShowTranscriptGenerationDialog(false);
                closeFullPlayer();
              }}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              onClick={handleConfirmTranscriptDialog}
              className="rounded-xl bg-foreground text-background "
              disabled={insufficientQuota || isTranscriptDialogLoading}
            >
              {isTranscriptDialogLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("common.processing", "Processing...")}
                </>
              ) : (
                t("common.confirm", "Confirm")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UpgradePromptDialog
        isOpen={showYouTubeUpgradeDialog}
        onClose={() => {
          setShowYouTubeUpgradeDialog(false);
          closeFullPlayer();
        }}
        onUpgrade={() => {
          setShowYouTubeUpgradeDialog(false);
          closeFullPlayer();
        }}
        errorType="limit_exceeded"
        remainingCount={usageInfo?.remainingTranscripts ?? 0}
        limitCount={usageInfo?.transcriptLimit ?? 3}
      />

      <div
        className={shouldShow ? "" : "hidden"}
        style={{
          // 确保隐藏时不占用空间，但DOM结构保持
          display: shouldShow ? "block" : "none",
        }}
      >
      <BottomPlayerControls
        content={bottomPlayerContent}
        youtubeTime={currentTime}
        youtubeDuration={duration}
        isPlaying={isPlaying}
        playbackSpeed={playbackSpeed}
        cleanReadData={cleanReadData}
        onSkipBackward={skipBackward}
        onSkipForward={skipForward}
        onScrollToCurrentTranscript={scrollToCurrentTranscript}
        onTogglePlayPause={handleTogglePlayPause}
        onSetPlaybackSpeed={setPlaybackSpeed}
        onScrubStart={handleScrubStart}
        onScrubChange={handleScrubChange}
        onScrubEnd={handleScrubEnd}
        formatTime={formatTime}
        isUserLoading={isLoading}
        loadingProgress={{ data: isLoading }}
        isPlaybackLocked={isPlaybackLocked}
        playbackLockReason={playbackLockReason}
        onNavigateToEpisode={!isMobile ? handleNavigateToEpisode : undefined}
        onOpenFullPlayer={isMobile ? handleOpenFullPlayer : undefined}
        hasSubtitles={hasSubtitles}
        onShadowingClick={handleShadowingClick}
      />
      {isMobile ? (
        <FullPlayerDialog
          open={isFullPlayerOpen}
          onOpenChange={handleFullPlayerOpenChange}
          subtitles={subtitles}
          activeSubtitleIndex={activeSubtitleIndex}
          episodeTitle={episodeTitle}
          episodeAuthor={episodeAuthor}
          episodeThumbnail={episodeThumbnail}
          artworkColor={episodeArtworkColor}
          episodeId={episodeId}
          currentTime={currentTime}
          duration={duration}
          formatTime={formatTime}
          onSeek={seekTo}
          onTogglePlayPause={handleTogglePlayPause}
          onSkipBackward={skipBackward}
          onSkipForward={skipForward}
          isPlaying={isPlaying}
          playbackSpeed={playbackSpeed}
          onChangePlaybackSpeed={setPlaybackSpeed}
          isDarkTheme={isDarkTheme}
          onNavigateToEpisode={
            shouldShowNavigateButton ? handleNavigateToEpisode : undefined
          }
          onSetActiveSubtitleIndex={setActiveSubtitleIndex}
          vocabularyData={episodeVocabularyData}
          isLoading={isLoading}
          isTranscriptLoading={transcriptLoadingState}
          videoConfig={videoConfig}
          onVideoPlayStateChange={updatePlayingState}
          onVideoTimeUpdate={updateTime}
          onVideoDurationChange={updateDuration}
          isWordCheckerReady={isWordCheckerReady}
          hasDictionarySelected={hasDictionarySelected}
          isDictionaryInitializing={isDictionaryInitializing}
          onVideoEnded={handleMediaEnded}
          onPausePlayback={pause}
          onResumePlayback={play}
          onShadowingClick={handleShadowingClick}
          onTranscriptUpdate={handleTranscriptUpdate}
          setCurrentEpisode={setCurrentEpisode}
          isGeneratingAudio={isGeneratingAudio}
          onVocabularyAudioMissing={handleVocabularyAudioMissing}
          onChosenExplanationMissing={handleChosenExplanationMissing}
          tempAudioOverrides={tempAudioOverrides}
          tempExplanationOverrides={tempExplanationOverrides}
          showTranscriptGenerationDialog={showTranscriptGenerationDialog}
          transcriptGenerationConfirmed={transcriptGenerationConfirmed}
          // 🎯 Chapters
          chapters={episodeChaptersData as Chapter[] || []}
          isGeneratingChapters={isGeneratingChapters}
          showChapters={showChapters}
          onToggleChapters={setShowChapters}
          // 🎯 Vocabulary update callback
          onVocabularyUpdate={updateEpisodeVocabulary}
        />
      ) : null}

      {/* Shadowing Practice Card */}
      <AnimatePresence mode="wait" initial={false}>
        {showShadowingDialog && shadowingState.currentSentence && (
          <motion.div
            key="shadowing-card"
            data-shadowing-card
            initial={{ opacity: 0, y: 200 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 200 }}
            transition={{ 
              duration: 0.3, 
              ease: [0.25, 1, 0.5, 1],
              opacity: { duration: 0.2 }
            }}
            style={{ willChange: 'transform, opacity' }}
            className="pointer-events-none fixed inset-x-4 bottom-4 z-[100] mx-auto max-w-[361px] md:mx-auto md:w-full overflow-hidden"
            onClick={(e) => {
              // Close on background click
              if (e.target === e.currentTarget) {
                setShowShadowingDialog(false);
                play();
              }
            }}
          >
            <div className="pointer-events-auto relative w-full overflow-hidden rounded-[40px]">
              <ShadowingPracticeCard
                sentence={shadowingState.currentSentence}
                showSentence={shadowingState.showSentence}
                showTranslation={shadowingState.showTranslation}
                showTranscription={shadowingState.showTranscription}
                transcriptionAccuracy={shadowingState.transcriptionAccuracy}
                transcribedText={transcribedText}
                transcriptionError={transcriptionError}
                isRecording={isRecording}
                isTranscribing={isTranscribing}
                audioLevel={audioLevel}
                playbackSpeed={shadowingState.playbackSpeed}
                onToggleSentence={() => setShadowingState(prev => ({ ...prev, showSentence: !prev.showSentence }))}
                onToggleTranslation={() => setShadowingState(prev => ({ ...prev, showTranslation: !prev.showTranslation }))}
                onStartRecording={async () => {
                  recordingStartTimeRef.current = Date.now();
                  setShadowingState(prev => ({
                    ...prev,
                    showTranscription: false,
                    transcriptionAccuracy: null
                  }));
                  await startRecording();
                }}
                onStopRecording={() => {
                  stopRecording();
                  setShadowingState(prev => ({ ...prev, showTranscription: true }));
                }}
                onReplay={(speed) => handleShadowingReplay(speed || shadowingState.playbackSpeed)}
                onContinue={handleShadowingContinue}
                onSkip={(e) => {
                  e?.stopPropagation?.();
                  setShowShadowingDialog(false);
                  play();
                }}
                containerClassName="bg-transparent"
                cardClassName="shadow-none"
                disableEntranceAnimation
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </>
  );
};
